import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { Box, Typography, IconButton, useTheme, CircularProgress, Accordion, AccordionSummary, AccordionDetails, Paper, Button, Checkbox, TextField, Alert, FormControl, Select, MenuItem } from "@mui/material";
import { getAuthToken } from "../../utils/getAuthToken";
import { checkSubscriptionActive } from "../../utils/subscriptionError";
import { toast } from "react-hot-toast";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CategoryIcon from '@mui/icons-material/Category';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import MemoryIcon from '@mui/icons-material/Memory';
import { useDispatch, useSelector } from "react-redux";
import { updateActionConfig, addBranch, setNodeNextBranches, removeBranch, fetchActions } from '../../redux/Slice/Workflow/workflowSlice';
import axios from 'axios';
import { fetchTeams, fetchTeamMembers, fetchRolesWithUsage } from '../../redux/Slice/Settings/settingsSlice';
import Autocomplete from '@mui/material/Autocomplete';

import { addNodeToBranch, updateNodeInBranch } from '../../redux/Slice/Workflow/workflowSlice';
import TemplateSendPreview from '../Inbox/Features/TemplateSendFeatures/TemplateSendPreview';
import { fetchTemplates, uploadFile, clearUploadState, uploadFileWithMediaID, clearUploadMediaIDState } from '../../redux/Slice/Templates/templateSlice';
import CatalogSend from '../Inbox/Features/TemplateSendFeatures/CatalogSend';
import { fetchCatalogs } from '../../redux/Slice/Catalog/catalogSlice';
import { ApiIntegrationForm } from '../../pages/Settings/ApiIntegrationPage';
import BusinessHoursEditor, { DEFAULT_BUSINESS_HOURS_VALUE, isBusinessHoursValueValid } from './BusinessHoursEditor';
import { BUSINESS_HOURS_BRANCH_ACTION } from './businessHoursBranch';
import ContactFieldsEditor from "./ContactFieldsEditor";
import useContactFieldOptions, { normalizeContactFieldValue } from "../../hooks/useContactFieldOptions";
import {
  CONTACT_FIELDS_BRANCH_ACTION,
  CONTACT_FIELDS_BRANCH_ACTION_NAME,
  DEFAULT_CONTACT_FIELDS_VALUE,
  isContactFieldsValueValid,
  buildContactFieldsConfig,
  applyContactFieldsBranches,
} from "./contactFieldsBranch";
import SetContactFieldsEditor from "./SetContactFieldsEditor";
import {
  SET_CONTACT_FIELDS_ACTION,
  SET_CONTACT_FIELDS_ACTION_NAME,
  DEFAULT_SET_CONTACT_FIELDS_VALUE,
  getSetContactFieldsError,
  buildSetContactFieldsConfig,
} from "./setContactFieldsAction";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

const NO_REPLY_UNITS = [
  { key: 'days', seconds: 86400 },
  { key: 'hours', seconds: 3600 },
  { key: 'minutes', seconds: 60 },
  { key: 'seconds', seconds: 1 },
];
const DEFAULT_KEYWORD_NO_REPLY = { enabled: false, days: 0, hours: 0, minutes: 0, seconds: 0 };
const CATEGORY_ICONS = {
  "Message": <MessageIcon sx={{ mr: 1, color: 'primary.main' }} />,
  "Contact": <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />,
  // WhatsApp Web workflows only (see isWhatsappWebWorkflow in the component)
  "Integrations": <IntegrationInstructionsIcon sx={{ mr: 1, color: 'primary.main' }} />,
  "Variables": <MemoryIcon sx={{ mr: 1, color: 'primary.main' }} />,
};

// Website Chat's action catalog — hardcoded (like Instagram/Facebook's own
// ChooseInstagramActionDrawer.jsx / ChooseFacebookActionDrawer.jsx), not DB/Redux-backed like
// WhatsApp's. `config`/`fields` labels below are copied verbatim from the server's extractors
// (extractMessageFromAction/extractImageMessageFromAction/.../extractAssignmentFromAction in
// workflow.executor.js) and from workflow.actionDefaults.js's MARK_AS_CLOSED_ACTION — a label
// mismatch here means a send/assign/close silently no-ops server-side.
const WEBSITE_MESSAGE_ACTIONS = [
  {
    name: "Send text message",
    description: "Send text message to a conversation",
    category: "Message",
    config: [
      {
        name: "Text Message Node",
        required: true,
        fields: [
          { label: "Message", type: "text large" },
          { label: "Wait for user reply", type: "checkbox", required: false, placeholder: "Pause workflow until user sends a message before running the next step" },
        ],
      },
    ],
  },
  {
    name: "AI Reply",
    description: "Reply to the visitor in website chat using AI (RAG). When AI can't answer or fails, send fallback message and/or assign to a team member.",
    category: "Message",
    config: [
      {
        name: "AI Reply Node",
        required: true,
        fields: [
          { label: "AI Agent", type: "agent", required: false },
          {
            label: "Fallback message (when AI can't answer)",
            type: "text large",
            required: false,
            placeholder: "e.g. I'm not sure about that. A team member will help you shortly.",
          },
          { label: "Assign to team member (when AI can't answer)", type: "teammember", required: false },
        ],
      },
    ],
  },
  {
    name: "Send image message",
    description: "Send image message to a conversation",
    category: "Message",
    config: [
      {
        name: "Image Message Node",
        required: true,
        fields: [
          { label: "Image", type: "file", required: true, accept: "image/*" },
          { label: "Caption", type: "text", required: false },
          { label: "Wait for user reply", type: "checkbox", required: false },
        ],
      },
    ],
  },
  {
    name: "Send video message",
    description: "Send video message to a conversation",
    category: "Message",
    config: [
      {
        name: "Video Message Node",
        required: true,
        fields: [
          { label: "Video", type: "file", required: true, accept: "video/*" },
          { label: "Caption", type: "text", required: false },
          { label: "Wait for user reply", type: "checkbox", required: false },
        ],
      },
    ],
  },
  {
    name: "Send audio message",
    description: "Send audio message to a conversation",
    category: "Message",
    config: [
      {
        name: "Audio Message Node",
        required: true,
        fields: [
          { label: "Audio", type: "file", required: true, accept: "audio/*" },
          { label: "Wait for user reply", type: "checkbox", required: false },
        ],
      },
    ],
  },
  {
    name: "Send document message",
    description: "Send document message to a conversation",
    category: "Message",
    config: [
      {
        name: "Document Message Node",
        required: true,
        fields: [
          { label: "Document", type: "file", required: true },
          { label: "Caption", type: "text", required: false },
          { label: "Filename", type: "text", required: false },
          { label: "Wait for user reply", type: "checkbox", required: false },
        ],
      },
    ],
  },
  // Config labels match extractInteractiveReplyButtonsFromAction / armWebsiteNoReplyExpiry in
  // workflow.executor.js. Header is None or Text only; "Wait for reply" adds a timed no-reply branch.
  {
    name: "Send interactive reply buttons message",
    description: "Send a message with up to 10 buttons; each button starts its own branch",
    category: "Message",
    config: [
      {
        name: "Interactive Reply Buttons Message Node",
        required: true,
        fields: [
          {
            label: "Header type",
            type: "select",
            required: false,
            options: [
              { label: "None", value: "none" },
              { label: "Text", value: "text", subfields: [{ label: "Header text", type: "text", required: true }] },
            ],
          },
          { label: "Body", type: "text large", required: true },
          { label: "Button", type: "section", subfields: [{ label: "Button Title", type: "text", required: true }, { label: "Button Description", type: "text", required: false }] },
          {
            label: "Wait for reply",
            type: "checkbox",
            required: false,
            subfields: [
              { label: "Days", type: "number" },
              { label: "hours", type: "number" },
              { label: "minutes", type: "number" },
              { label: "seconds", type: "number" },
            ],
          },
        ],
      },
    ],
  },
];

// WhatsApp caps reply buttons at 3; website chat allows up to 10.
const WEBSITE_MAX_BUTTONS = 10;

const WEBSITE_CONTACT_ACTIONS = [
  SET_CONTACT_FIELDS_ACTION,
  {
    name: "Assign conversation",
    description: "Assign this conversation to a specific team member.",
    category: "Contact",
    config: [
      {
        name: "Assign conversation to a member either directly or using round robin",
        required: true,
        fields: [
          {
            label: "Select Assignment Style",
            type: "select",
            required: true,
            options: [
              { label: "Round Robin (All)", pointer: null },
              {
                label: "Round Robin (specific members)",
                subfields: [{ label: "Select member", type: "multipleteammember", options: "members" }],
                pointer: null,
              },
              {
                label: "Specific member",
                subfields: [{ label: "Select member", type: "teammember", options: "members", required: true }],
              },
              {
                label: "Specific team",
                subfields: [{ label: "Select team", type: "team", options: "team" }],
              },
            ],
          },
        ],
      },
      {
        name: "Auto-close after assignment",
        required: false,
        fields: [
          { label: "Enable auto-close", type: "checkbox", selectedValue: false },
          { label: "Select Interval Duration", type: "number", selectedValue: 30, dependsOn: "Enable auto-close" },
          {
            label: "Select Duration Type",
            type: "select",
            selectedValue: "Minutes",
            options: ["Minutes", "Hours", "Days"],
            dependsOn: "Enable auto-close",
          },
        ],
      },
    ],
  },
  {
    name: "Mark as closed",
    description: "Closes the conversation for this contact, same as a teammate closing it from the inbox.",
    category: "Contact",
    config: [
      {
        name: "Mark as Closed Node",
        required: false,
        fields: [],
      },
    ],
  },
];

// Same definition Instagram/Facebook use — the executor's "Go to action" handler is
// channel-agnostic (jumps to the branch/node picked in the "actions" field).
const WEBSITE_QUICK_ACTIONS = [
  {
    name: "Go to action",
    description: "Go back to a specific action",
    category: "Quick Actions",
    config: [
      {
        name: "Go to",
        required: true,
        fields: [{ label: "Existing Actions", type: "actions" }],
      },
    ],
  },
];

// Same option and upload field as the WhatsApp buttons message's "Header type" (workflow.actionDefaults.js INTERACTIVE_BUTTONS_ACTION),
// so the saved step is the one the executor already reads.
const WHATSAPP_WEB_IMAGE_HEADER_OPTION = {
  label: "Image",
  value: "image",
  subfields: [{ label: "Header image", type: "file", required: false, accept: "image/*" }],
};

const WEBSITE_ACTIONS_BY_CATEGORY = [
  { category: "Message", actions: WEBSITE_MESSAGE_ACTIONS },
  { category: "Contact", actions: WEBSITE_CONTACT_ACTIONS },
  { category: "Logic", actions: [BUSINESS_HOURS_BRANCH_ACTION, CONTACT_FIELDS_BRANCH_ACTION] },
  { category: "Quick Actions", actions: WEBSITE_QUICK_ACTIONS },
];

const isGoToActionName = (name) => String(name || '').toLowerCase().includes('go to');

const DEFAULT_API_INTEGRATION_CONFIG = {
  apiUrl: '',
  method: 'POST',
  headers: {},
  formConfig: { fields: [], lineItemsConfig: { catalogArrayPath: 'allProducts', targetPath: 'line_items', columns: [] } },
};

// "Send Template" trigger quick-reply branches only — see isSendTemplateBranch below. Same
// contact fields already offered by the API Integration action's "From Contact" source
// (ApiIntegrationPage.jsx CONTACT_FIELDS), kept as a separate small list here since this one
// writes a `{{contact.field}}` token string instead of a sourceType/sourcePath pair.
const TEMPLATE_VAR_CONTACT_FIELDS = [
  { value: 'phoneNumberID', label: 'Phone' },
  { value: 'name', label: 'Name' },
];

// Infers which "Source" a body variable's currently-stored string represents, so re-opening an
// already-configured action shows the right selection instead of always defaulting to Fixed.
// Purely a display-time inference — the stored value itself is always just a plain string.
function parseTemplateVariableSource(value, variableMapping) {
  if (typeof value !== 'string') return { source: 'fixed', key: '' };
  const m = value.match(/^\{\{\s*([\w.]+)\s*\}\}$/);
  if (!m) return { source: 'fixed', key: '' };
  const token = m[1];
  if (token.startsWith('contact.')) {
    const field = token.slice('contact.'.length);
    if (TEMPLATE_VAR_CONTACT_FIELDS.some((f) => f.value === field)) return { source: 'contact', key: field };
  } else if (Array.isArray(variableMapping) && variableMapping.some((v) => v.name === token)) {
    return { source: 'template', key: token };
  }
  return { source: 'fixed', key: '' };
}



const ChooseWebsiteActionDrawer = ({ open, onClose, editAction, editActionIndex, editActionBranch, editActionNodeId, pendingInsert }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const dispatch = useDispatch();
  const { selectedWorkflow, actions: serverActions } = useSelector(state => state.workflow);
  // Website Chat's action catalog is a fixed hardcoded list (see WEBSITE_ACTIONS_BY_CATEGORY
  // above), not fetched from the DB/Redux the way WhatsApp's is.
  //
  // A WhatsApp Web workflow uses the same list PLUS the two steps that do not depend on a messaging platform, API Integration and
  // Set Variable. Those come from the server's catalog (the one WhatsApp workflows use) instead of being copied here, so their
  // field labels are exactly what the executor reads. This drawer already holds their forms and their dedicated sections below.
  const isWhatsappWebWorkflow = selectedWorkflow?.channel === 'whatsapp_web';
  const actions = useMemo(() => {
    if (!isWhatsappWebWorkflow) return WEBSITE_ACTIONS_BY_CATEGORY;
    // The buttons step also offers an image header here, as WhatsApp's own buttons message does (the website chat shows text only).
    const withImageHeader = (group) => ({
      ...group,
      actions: group.actions.map((action) =>
        action.name !== 'Send interactive reply buttons message'
          ? action
          : {
              ...action,
              config: action.config.map((node) => ({
                ...node,
                fields: node.fields.map((field) =>
                  field.label === 'Header type' ? { ...field, options: [...field.options, WHATSAPP_WEB_IMAGE_HEADER_OPTION] } : field
                ),
              })),
            }
      ),
    });
    const webBase = WEBSITE_ACTIONS_BY_CATEGORY.map(withImageHeader);
    const platformFree = (Array.isArray(serverActions) ? serverActions : [])
      .flatMap((group) => group.actions || [])
      .filter((action) => action.name === 'API Integration' || action.name === 'Set Variable')
      // The executor runs both in any workflow, so no trigger restriction may hide them here.
      .map(({ onlyForTriggers, ...action }) => action);
    return platformFree.length ? [...webBase, { category: 'Integrations', actions: platformFree }] : webBase;
  }, [isWhatsappWebWorkflow, serverActions]);
  // Ask the server for its catalog once per opening (never in a loop, even if it answers empty or fails).
  const requestedServerActionsRef = useRef(false);
  useEffect(() => {
    if (!open) {
      requestedServerActionsRef.current = false;
      return;
    }
    const alreadyLoaded = Array.isArray(serverActions) && serverActions.length > 0;
    if (isWhatsappWebWorkflow && !alreadyLoaded && !requestedServerActionsRef.current) {
      requestedServerActionsRef.current = true;
      dispatch(fetchActions());
    }
  }, [open, isWhatsappWebWorkflow, serverActions, dispatch]);
  const { teams, teamMembers, rolesWithUsage, loading: settingsLoading } = useSelector(state => state.settings);
  const { templates, status: templatesStatus, uploadStatus, uploadResult, uploadMediaIDStatus, uploadMediaIDResult } = useSelector(state => state.template);
  const { catalogs, status: catalogsStatus } = useSelector(state => state.catalog);
  const currentWorkspace = useSelector(state => state.workspace?.currentWorkspace);
  const workspaceList = useSelector(state => state.workspace?.workspaces);
  const authWorkspaceId = useSelector(state => state.auth?.user?.workspaceId);
  // Prefer the workflow's own workspaceId — falls back to current workspace, then auth token
  const workspaceId =
    (selectedWorkflow?.workspaceId ? String(selectedWorkflow.workspaceId) : null) ||
    currentWorkspace?._id ||
    selectedWorkflow?.workspace?._id ||
    authWorkspaceId ||
    (Array.isArray(workspaceList) ? workspaceList[0]?._id : null) ||
    null;

  const [businessHoursExpandedDay, setBusinessHoursExpandedDay] = useState(null);
  const contactFieldOptions = useContactFieldOptions(workspaceId);
  const [aiAgents, setAiAgents] = useState([]);
  const [aiAgentsLoading, setAiAgentsLoading] = useState(false);
  const aiAgentsCacheRef = useRef(new Map());

  useEffect(() => {
    if (!open) return;
    if (!workspaceId) {
      setAiAgents([]);
      setAiAgentsLoading(false);
      return;
    }
    if (aiAgentsCacheRef.current.has(workspaceId)) {
      setAiAgents(aiAgentsCacheRef.current.get(workspaceId) || []);
      setAiAgentsLoading(false);
      return;
    }

    let cancelled = false;
    setAiAgentsLoading(true);

    (async () => {
      try {
        const token = await getAuthToken();
        if (cancelled || !token) return;

        const r = await fetch(`${SERVER_DOMAIN}/api/workspaces/${workspaceId}/ai/agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (cancelled) return;

        if (!r.ok) {
          console.warn(`[AI Reply] Failed to load agents — HTTP ${r.status} for workspace ${workspaceId}`);
          return;
        }

        const data = await r.json();
        if (cancelled) return;

        const rawAgents = Array.isArray(data)
          ? data
          : Array.isArray(data?.agents)
            ? data.agents
            : Array.isArray(data?.data?.agents)
              ? data.data.agents
              : [];
        const agents = rawAgents.filter(a => a?.isActive !== false);
        aiAgentsCacheRef.current.set(workspaceId, agents);
        setAiAgents(agents);
      } catch (err) {
        if (!cancelled) {
          console.error(`[AI Reply] Error loading agents for workspace ${workspaceId}:`, err?.message || err);
        }
      } finally {
        setAiAgentsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [workspaceId, open]);

  const [expanded, setExpanded] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [localConfig, setLocalConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState('');
  const [templateMediaUploads, setTemplateMediaUploads] = useState({});
  const [currentUploadingTemplate, setCurrentUploadingTemplate] = useState(null);
  const templateFileInputRefs = useRef({});
  // Carousel card media upload state
  const [carouselMediaUploads, setCarouselMediaUploads] = useState({});
  const [currentUploadingCarouselCard, setCurrentUploadingCarouselCard] = useState(null);
  const carouselFileInputRefs = useRef({});
  // Removed pendingSave state since we handle API calls directly in handleSaveAction
  // Fetch guards to avoid repeated refetch loops while drawer is open
  const teamsFetchAttemptedRef = useRef(false);
  const teamMembersFetchAttemptedRef = useRef(false);
  const rolesFetchAttemptedRef = useRef(false);
  // Track if we've attempted to fetch catalogs to prevent infinite loops
  const catalogsFetchAttemptedRef = useRef(false);
  // Track if a fetch is currently in progress to prevent concurrent fetches
  const catalogsFetchInProgressRef = useRef(false);
  /** Scroll container for action list vs config — reset on view change so content starts at top */
  const drawerBodyScrollRef = useRef(null);

  // State for action selection mode
  const [actionSelectionMode, setActionSelectionMode] = useState(false);
  const [actionSelectionFieldKey, setActionSelectionFieldKey] = useState(null);
  
  // State for keyword management
  const [keywords, setKeywords] = useState([]);
  
  // State for keyword target selection mode
  const [keywordTargetSelectionMode, setKeywordTargetSelectionMode] = useState(false);
  const [keywordTargetSelectionIndex, setKeywordTargetSelectionIndex] = useState(null);
  
  // State for incorrect keyword message configuration
  const [incorrectKeywordMessage, setIncorrectKeywordMessage] = useState({
    enabled: false,
    text: "I didn't understand that. Please try again.",
    showOptions: false
  });

  // State for keyword wait mode (wait for keyword reply vs send next message immediately)
  const [waitForKeyword, setWaitForKeyword] = useState(false);

  // WhatsApp Web keyword branches' timed "no reply" branch (same shape as the QR workflow drawer)
  const [keywordNoReply, setKeywordNoReply] = useState(DEFAULT_KEYWORD_NO_REPLY);

  // WhatsApp Web workflows work like QR ones: each keyword gets its own branch (like a list option) instead of pointing at an
  // existing message. Sets action.keywords to [{ keyword, branchId }] and keeps the action's `next` (keyword-* branches, an
  // optional no-reply-* branch + any unrelated ids) and the branch trigger nodes in sync. Website chat keeps target messages.
  const useKeywordBranches = isWhatsappWebWorkflow;
  const syncKeywordNoReplyBranch = async (actionWithConfig, hasKeywords) => {
    const isNoReplyId = id => typeof id === 'string' && id.startsWith('no-reply-');
    const existingId = (editAction?.next || []).find(isNoReplyId);
    const totalSeconds = NO_REPLY_UNITS.reduce((sum, unit) => sum + (parseInt(keywordNoReply[unit.key], 10) || 0) * unit.seconds, 0);

    if (!hasKeywords || !keywordNoReply.enabled || totalSeconds <= 0) {
      if (existingId) await dispatch(removeBranch({ branchId: existingId }));
      actionWithConfig.next = (actionWithConfig.next || []).filter(id => !isNoReplyId(id));
      if (actionWithConfig.next.length === 0) delete actionWithConfig.next;
      return;
    }

    const branchId = existingId || `no-reply-${Date.now()}`;
    const currentBranches = window.store?.getState?.()?.workflow?.selectedWorkflow?.currentVersion?.branches || {};
    if (!currentBranches[branchId]) {
      await dispatch(addBranch({ branchId }));
    }
    const triggerNode = {
      id: `${branchId}-trigger`,
      type: 'trigger',
      trigger: {
        name: 'no reply',
        description: 'no reply',
        triggerType: 'non-root',
        config: [{ fields: NO_REPLY_UNITS.map(unit => ({ type: 'number', label: unit.key, selectedValue: parseInt(keywordNoReply[unit.key], 10) || 0 })) }],
        category: 'Nil',
      },
    };
    if (currentBranches[branchId]?.length > 0) {
      await dispatch(updateNodeInBranch({ branchId, nodeId: triggerNode.id, updates: triggerNode }));
    } else {
      await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
    }
    actionWithConfig.next = [...(actionWithConfig.next || []).filter(id => !isNoReplyId(id)), branchId];
  };
  const syncKeywordBranches = async (actionWithConfig) => {
    const completeKeywords = keywords.filter(kw => kw.keyword && kw.keyword.trim());
    const previousIds = (editAction?.next || []).filter(id => typeof id === 'string' && id.startsWith('keyword-'));
    const currentBranches = window.store?.getState?.()?.workflow?.selectedWorkflow?.currentVersion?.branches || {};

    const keywordMappings = completeKeywords.map((kw, idx) => ({
      keyword: kw.keyword.trim(),
      branchId: kw.branchId || `keyword-${Date.now()}-${idx}`,
    }));
    const keptIds = keywordMappings.map(kw => kw.branchId);

    for (const branchId of previousIds.filter(id => !keptIds.includes(id))) {
      await dispatch(removeBranch({ branchId }));
    }

    for (const { keyword, branchId } of keywordMappings) {
      if (!currentBranches[branchId]) {
        await dispatch(addBranch({ branchId }));
      }
      const triggerNode = {
        id: `${branchId}-trigger`,
        type: 'trigger',
        trigger: {
          name: 'keyword',
          description: 'Trigger for keyword match',
          triggerType: 'non-root',
          config: [{ fields: [{ label: 'Keyword', type: 'text', selectedValue: keyword }] }],
          category: 'Nil',
        },
      };
      if (currentBranches[branchId]?.length > 0) {
        await dispatch(updateNodeInBranch({ branchId, nodeId: triggerNode.id, updates: triggerNode }));
      } else {
        await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
      }
    }

    const otherNext = (editAction?.next || []).filter(id => !(typeof id === 'string' && id.startsWith('keyword-')));
    const next = [...otherNext, ...keptIds];
    if (next.length > 0) {
      actionWithConfig.next = next;
    } else {
      delete actionWithConfig.next;
    }
    if (keywordMappings.length > 0) {
      actionWithConfig.keywords = keywordMappings;
    } else {
      delete actionWithConfig.keywords;
    }
    await syncKeywordNoReplyBranch(actionWithConfig, keywordMappings.length > 0);
  };

  // Validation errors for required fields (key -> error message)
  const [fieldErrors, setFieldErrors] = useState({});

  // Full-size image preview
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  // State for Set Variable multi-entry support
  const [variableEntries, setVariableEntries] = useState([
    { type: 'Text', name: '', valueSource: 'Fixed value', fixedValue: '', contactField: '' }
  ]);

  /**
   * Helper function to extract all action nodes from the current workflow
   * This is used for fields with type='actions' to allow selecting existing actions
   * @returns {Array} Array of action objects with id, branchId, nodeId, name, description, category, and action
   */
  const extractWorkflowActions = useCallback(() => {
    const actions = [];
    const branches = selectedWorkflow?.currentVersion?.branches || {};
    
    // Get the current branch being edited
    const currentBranchId = editActionBranch || pendingInsert?.branchId;
    
    Object.keys(branches).forEach(branchId => {
      const branch = branches[branchId];
      if (Array.isArray(branch)) {
        branch.forEach((node, nodeIndex) => {
          if (node.type === 'action' && node.action) {
            // Exclude actions from the same branch as the current node being edited
            if (branchId === currentBranchId) {
              return; // Skip this action
            }
            
            // Create a unique identifier for this action in the workflow
            const actionId = `${branchId}-${nodeIndex}`;
            actions.push({
              id: actionId,
              branchId,
              nodeId: node.id || nodeIndex,
              nodeIndex,
              name: node.action.name,
              description: node.action.description,
              category: node.action.category,
              // Store the full action object for reference
              action: node.action
            });
          }
        });
      }
    });
    
    return actions;
  }, [selectedWorkflow, editActionBranch, pendingInsert]);

  // Get workflow actions for the actions field type
  const workflowActions = React.useMemo(() => {
    const actions = extractWorkflowActions();
    return actions;
  }, [extractWorkflowActions]);

  /**
   * Extract all workflow messages (action nodes) for keyword target selection
   * Uses the same logic as extractWorkflowActions but includes all actions (not excluding current branch)
   * Returns all messages with their IDs, names, and locations
   */
  const extractWorkflowMessages = React.useMemo(() => {
    const messages = [];
    const branches = selectedWorkflow?.currentVersion?.branches || {};
    
    // Helper function to extract message text from action config
    // Uses the same logic as WorkflowNode.jsx to extract display text
    const extractMessageText = (action) => {
      if (!action || !action.config || !Array.isArray(action.config)) {
        return null;
      }
      
      // Recursively search through all fields and subfields
      const searchFields = (fields) => {
        if (!Array.isArray(fields)) return null;
        
        for (const field of fields) {
          // Check field type first (most reliable)
          if (field.type === 'text large' && field.selectedValue && typeof field.selectedValue === 'string') {
            const text = field.selectedValue.trim();
            if (text.length > 0) return text;
          }
          
          if (field.type === 'text' && field.selectedValue && typeof field.selectedValue === 'string') {
            const text = field.selectedValue.trim();
            if (text.length > 0) return text;
          }
          
          // For template messages
          if (field.type === 'template' && field.selectedValue) {
            if (typeof field.selectedValue === 'object' && field.selectedValue.name) {
              return field.selectedValue.name;
            }
          }
          
          // Check label-based matching as fallback
          if (field.label) {
            const labelLower = field.label.toLowerCase();
            // For text messages
            if ((labelLower.includes('message') || labelLower === 'text') && 
                field.selectedValue && typeof field.selectedValue === 'string') {
              const text = field.selectedValue.trim();
              if (text.length > 0) return text;
            }
            // For list messages - prefer body over header
            if (labelLower.includes('body') && 
                field.selectedValue && typeof field.selectedValue === 'string') {
              const text = field.selectedValue.trim();
              if (text.length > 0) return text;
            }
            if (labelLower.includes('header') && 
                field.selectedValue && typeof field.selectedValue === 'string') {
              const text = field.selectedValue.trim();
              if (text.length > 0) return text;
            }
          }
          
          // Recursively search subfields
          if (field.subfields && Array.isArray(field.subfields)) {
            const result = searchFields(field.subfields);
            if (result) return result;
          }
          
          // Recursively search nested fields
          if (field.fields && Array.isArray(field.fields)) {
            const result = searchFields(field.fields);
            if (result) return result;
          }
        }
        
        return null;
      };
      
      // Search through all config sections
      for (const cfg of action.config) {
        if (cfg.fields && Array.isArray(cfg.fields)) {
          const result = searchFields(cfg.fields);
          if (result) return result;
        }
      }
      
      return null;
    };
    
    Object.keys(branches).forEach(branchId => {
      const branch = branches[branchId];
      if (Array.isArray(branch)) {
        branch.forEach((node, nodeIndex) => {
          if (node.type === 'action' && node.action && node.id) {
            const actionName = node.action.name || 'Unnamed Action';
            const messageText = extractMessageText(node.action);
            
            // Format display name: use message text if available, otherwise use action name
            let displayName = messageText || actionName;
            
            // Truncate if too long
            if (displayName.length > 60) {
              displayName = displayName.substring(0, 57) + '...';
            }
            
            messages.push({
              id: node.id,
              messageId: node.id,
              name: displayName,
              actionName: actionName,
              messageText: messageText,
              branchId,
              nodeIndex,
              action: node.action
            });
          }
        });
      }
    });
    
    return messages;
  }, [selectedWorkflow]);

  // Check if the current node being edited is just before an end node
  const isNodeBeforeEndNode = React.useMemo(() => {
    if (!editActionBranch || editActionNodeId === undefined || editActionNodeId === null) {
      // For new nodes being inserted, check if the insertion point is just before an end node
      if (pendingInsert) {
        const branch = selectedWorkflow?.currentVersion?.branches?.[pendingInsert.branchId];
        if (branch && Array.isArray(branch)) {
          // Check if this is the last position in the branch and the previous node has no next branches
          const isLastPosition = pendingInsert.nodeIndex === branch.length;
          if (isLastPosition && branch.length > 0) {
            const previousNode = branch[branch.length - 1];
            return !previousNode.next || !Array.isArray(previousNode.next) || previousNode.next.length === 0;
          }
        }
        return false;
      }
      return false;
    }

    // For existing nodes being edited
    const branch = selectedWorkflow?.currentVersion?.branches?.[editActionBranch];
    if (!branch || !Array.isArray(branch)) return false;

    // Find the current node in the branch
    const currentNodeIndex = branch.findIndex(node => 
      node.id === editActionNodeId || 
      node._id === editActionNodeId || 
      node.id === parseInt(editActionNodeId) || 
      node._id === parseInt(editActionNodeId)
    );

    if (currentNodeIndex === -1) return false;

    // Check if this is the last node in the branch
    const isLastNode = currentNodeIndex === branch.length - 1;
    
    // Check if this node has no next branches
    const currentNode = branch[currentNodeIndex];
    const hasNoNextBranches = !currentNode.next || !Array.isArray(currentNode.next) || currentNode.next.length === 0;


    return isLastNode && hasNoNextBranches;
  }, [editActionBranch, editActionNodeId, pendingInsert, selectedWorkflow]);

  // Function to handle action selection from the workflow
  const handleActionSelection = useCallback((selectedActionData) => {
    if (actionSelectionMode && actionSelectionFieldKey) {

      // Find the corresponding workflow action
      const workflowAction = workflowActions.find(action =>
        action.branchId === selectedActionData.branchId &&
        action.nodeIndex === selectedActionData.nodeIndex
      );

      if (workflowAction) {
        // Update the form value
        setFormValues(v => ({ ...v, [actionSelectionFieldKey]: workflowAction.id }));
      }

      // Exit selection mode
      setActionSelectionMode(false);
      setActionSelectionFieldKey(null);
    }
  }, [actionSelectionMode, actionSelectionFieldKey, workflowActions]);

  // Function to handle keyword target message selection from the workflow
  const handleKeywordTargetSelection = useCallback((selectedActionData) => {
    if (keywordTargetSelectionMode && keywordTargetSelectionIndex !== null) {

      // Get the branch and find the node to get its ID
      const branch = selectedWorkflow?.currentVersion?.branches?.[selectedActionData.branchId];
      if (branch && Array.isArray(branch) && selectedActionData.nodeIndex < branch.length) {
        const targetNode = branch[selectedActionData.nodeIndex];
        if (targetNode && targetNode.id) {
          // Update the keyword's targetId with the message ID
          const newKeywords = [...keywords];
          newKeywords[keywordTargetSelectionIndex].targetId = targetNode.id;
          setKeywords(newKeywords);
        }
      }

      // Exit selection mode
      setKeywordTargetSelectionMode(false);
      setKeywordTargetSelectionIndex(null);
    }
  }, [keywordTargetSelectionMode, keywordTargetSelectionIndex, selectedWorkflow, keywords]);

  // Expose the selection handler globally so ActionsTab can access it
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.handleActionSelection = handleActionSelection;
      window.setActionSelectionMode = setActionSelectionMode;
      window.setActionSelectionFieldKey = setActionSelectionFieldKey;
      window.isActionSelectionMode = () => actionSelectionMode;
      window.getCurrentSelectionBranchId = () => editActionBranch || pendingInsert?.branchId;
      
      // Expose keyword target selection handler
      window.handleKeywordTargetSelection = handleKeywordTargetSelection;
      window.isKeywordTargetSelectionMode = () => keywordTargetSelectionMode;
      
      return () => {
        delete window.handleActionSelection;
        delete window.setActionSelectionMode;
        delete window.setActionSelectionFieldKey;
        delete window.isActionSelectionMode;
        delete window.getCurrentSelectionBranchId;
        delete window.handleKeywordTargetSelection;
        delete window.isKeywordTargetSelectionMode;
      };
    }
  }, [actionSelectionMode, actionSelectionFieldKey, keywordTargetSelectionMode, keywordTargetSelectionIndex, workflowActions, editActionBranch, pendingInsert, keywords, selectedWorkflow, handleActionSelection, handleKeywordTargetSelection]);

  // Get additional context about why the node is not before an end node
  const getNodeContext = React.useMemo(() => {
    if (isNodeBeforeEndNode) return null;

    if (!editActionBranch || editActionNodeId === undefined || editActionNodeId === null) {
      if (pendingInsert) {
        const branch = selectedWorkflow?.currentVersion?.branches?.[pendingInsert.branchId];
        if (branch && Array.isArray(branch)) {
          const isLastPosition = pendingInsert.nodeIndex === branch.length;
          if (isLastPosition && branch.length > 0) {
            const previousNode = branch[branch.length - 1];
            if (previousNode.next && Array.isArray(previousNode.next) && previousNode.next.length > 0) {
              return 'branching';
            }
          }
        }
      }
      return 'middle';
    }

    const branch = selectedWorkflow?.currentVersion?.branches?.[editActionBranch];
    if (!branch || !Array.isArray(branch)) return 'middle';

    const currentNodeIndex = branch.findIndex(node => 
      node.id === editActionNodeId || 
      node._id === editActionNodeId || 
      node.id === parseInt(editActionNodeId) || 
      node._id === parseInt(editActionNodeId)
    );

    if (currentNodeIndex === -1) return 'middle';

    const isLastNode = currentNodeIndex === branch.length - 1;
    const currentNode = branch[currentNodeIndex];
    const hasNextBranches = currentNode.next && Array.isArray(currentNode.next) && currentNode.next.length > 0;

    if (!isLastNode) {
      return 'middle';
    } else if (hasNextBranches) {
      return 'branching';
    } else {
      return 'middle';
    }
  }, [editActionBranch, editActionNodeId, pendingInsert, selectedWorkflow, isNodeBeforeEndNode]);

  // When selectedAction changes, reset localConfig
  useEffect(() => {
    if (selectedAction) {
      let config = selectedAction.config ? JSON.parse(JSON.stringify(selectedAction.config)) : null;

      // For AI Reply, ensure the "AI Agent" field is always present (handles nodes saved before this field was added)
      if (selectedAction.name === 'AI Reply' && Array.isArray(config)) {
        config.forEach(node => {
          if (!Array.isArray(node.fields)) return;
          if (!node.fields.some(f => f.label === 'AI Agent')) {
            node.fields.unshift({ label: 'AI Agent', type: 'agent', required: false });
          }
        });
      }

      setLocalConfig(config);
    }
  }, [selectedAction]);

  useEffect(() => {
    if (!open) return;
    const el = drawerBodyScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [open, selectedAction, editAction, editActionNodeId, editActionBranch, pendingInsert]);

  // Recursively set selectedValue for all fields and subfields, using parentKey for nested keys and section index
  function setSelectedValues(fields, formValues, parentKey = "", isBeforeEndNode = false) {
    return fields.map((field, idx) => {
      let updatedField = { ...field };
      const key = `${parentKey}${field.label || field.name || idx}`;
      if (field.subfields) {
        updatedField.subfields = setSelectedValues(field.subfields, formValues, `${key}-${idx}-`, isBeforeEndNode);
      }
      if (field.fields) {
        updatedField.fields = setSelectedValues(field.fields, formValues, `${key}-${idx}-`, isBeforeEndNode);
      }
      // For select fields with options that have subfields, also process the selected option's subfields
      if (field.type === 'select' && Array.isArray(field.options)) {
        const selectedValue = formValues[key];
        if (selectedValue) {
          const selectedOption = field.options.find(opt => (opt.label || opt.value) === selectedValue);
          if (selectedOption && Array.isArray(selectedOption.subfields) && selectedOption.subfields.length > 0) {
            // Process subfields of the selected option
            updatedField.options = field.options.map(opt => {
              if ((opt.label || opt.value) === selectedValue && Array.isArray(opt.subfields)) {
                return {
                  ...opt,
                  subfields: setSelectedValues(opt.subfields, formValues, `${key}-`, isBeforeEndNode)
                };
              }
              return opt;
            });
          }
        }
      }
      if (field.label || field.name) {
        if (field.type === 'actions') {
          // For actions field type, store the branch ID and index of the selected action
          // Only process if the node is before an end node
          if (isBeforeEndNode) {
            const selectedActionId = formValues[key];
            if (selectedActionId) {
              const selectedWorkflowAction = workflowActions.find(action => action.id === selectedActionId);
              if (selectedWorkflowAction) {
                // Store as object with branchId, nodeIndex, and action ID
                updatedField.selectedValue = {
                  branchId: selectedWorkflowAction.branchId,
                  nodeIndex: selectedWorkflowAction.nodeIndex,
                  actionId: selectedWorkflowAction.nodeId || selectedWorkflowAction.nodeIndex
                };
              } else {
                updatedField.selectedValue = '';
              }
            } else {
              updatedField.selectedValue = '';
            }
          } else {
            // If not before end node, clear any existing value
            updatedField.selectedValue = '';
          }
        } else if (field.type === 'agent') {
          // Store the selected agent ID (empty string = use workspace default agent)
          updatedField.selectedValue = formValues[key] !== undefined ? formValues[key] : '';
        } else {
         // For file uploads, extract the URL from the object if it exists
         const formValue = formValues[key];
         if (field.type === 'file' && field.label === 'Image') {
           // Multi-image field: save as array of URL strings
           if (Array.isArray(formValue)) {
             updatedField.selectedValue = formValue
               .filter(f => f && f.status === 'uploaded' && f.url)
               .map(f => f.url);
           } else {
             updatedField.selectedValue = [];
           }
         } else if (field.type === 'file' && formValue && typeof formValue === 'object' && formValue.url) {
           updatedField.selectedValue = formValue.url;
         } else if (field.type === 'section' && field.label && field.label.toLowerCase().includes('button')) {
           // Button sections don't have selectedValue themselves, only their subfields do
           updatedField.selectedValue = undefined;
         } else if (field.type === 'template' && formValue && typeof formValue === 'object' && formValue.name) {
           // Merge the full template object (with components) so WorkflowNode can render the preview
           const fullTpl = (templates || []).find(t => t.name === formValue.name);
           updatedField.selectedValue = fullTpl ? { ...fullTpl, ...formValue } : formValue;
         } else {
           updatedField.selectedValue = formValue !== undefined ? formValue : '';
         }
        }
      }
      return updatedField;
    });
  }

  // Recursively extract selectedValue from all fields and subfields into formValues, using parentKey for nested keys and section index
  const extractSelectedValues = useCallback((fields, formValues = {}, parentKey = "") => {
    fields.forEach((field, idx) => {
      const key = `${parentKey}${field.label || field.name || idx}`;
      // For button section fields, still extract their subfield values but use idx for uniqueness
      if (field.type === 'section' && field.label && field.label.toLowerCase().includes('button')) {
        // Process subfields for button sections - these need to be extracted so they can be displayed
        // Use idx in key to ensure each button has unique keys
        if (field.subfields) {
          extractSelectedValues(field.subfields, formValues, `${key}-${idx}-`);
        }
        // Also process fields if they exist
        if (field.fields) {
          extractSelectedValues(field.fields, formValues, `${key}-${idx}-`);
        }
        // Don't store the section itself as a value, but continue processing
        // (don't return early, let it continue to process subfields normally)
        return; // Skip storing button section values, but we've already processed subfields
      }
      if ((field.label || field.name) && field.selectedValue !== undefined) {
        if (field.type === 'actions') {
          // For actions field type, the selectedValue should already be the branchId and nodeIndex object
          formValues[key] = field.selectedValue;
        } else if (field.type === 'agent') {
          // Restore selected agent ID (empty string = workspace default)
          formValues[key] = field.selectedValue || '';
        } else if (field.type === 'file') {
          if (field.label === 'Image') {
            // Multi-image field: convert stored URLs to display objects
            if (Array.isArray(field.selectedValue)) {
              formValues[key] = field.selectedValue.filter(Boolean).map(v =>
                typeof v === 'string' ? { status: 'uploaded', url: v } : v
              );
            } else if (field.selectedValue) {
              const url = typeof field.selectedValue === 'object' ? field.selectedValue.url : field.selectedValue;
              if (url) formValues[key] = [{ status: 'uploaded', url }];
              else formValues[key] = [];
            } else {
              formValues[key] = [];
            }
          } else if (typeof field.selectedValue === 'object' && field.selectedValue.url) {
            formValues[key] = field.selectedValue.url;
          } else {
            formValues[key] = field.selectedValue;
          }
        } else if (field.type === 'catalog') {
          // For catalog field type, ensure we store the full object
          if (field.selectedValue && typeof field.selectedValue === 'object') {
            formValues[key] = field.selectedValue;
          } else if (field.subfields && field.subfields.length > 0) {
            // Fallback: Try to reconstruct from subfields
            const catalogIdField = field.subfields.find(sf => sf.label === 'Catalog ID');
            const catalogNameField = field.subfields.find(sf => sf.label === 'Catalog Name');
            const productsField = field.subfields.find(sf => sf.label === 'Products');
            if (catalogIdField && catalogIdField.selectedValue) {
              formValues[key] = {
                id: catalogIdField.selectedValue,
                _id: catalogIdField.selectedValue,
                name: catalogNameField?.selectedValue || '',
                products: productsField?.selectedValue || [],
              };
            }
          }
        } else {
          formValues[key] = field.selectedValue;
        }
      }
      if (field.subfields) {
        extractSelectedValues(field.subfields, formValues, `${key}-${idx}-`);
      }
      if (field.fields) {
        extractSelectedValues(field.fields, formValues, `${key}-${idx}-`);
      }
      // For select fields with options that have subfields, also extract values from the selected option's subfields
      if (field.type === 'select' && Array.isArray(field.options)) {
        const selectedValue = formValues[key];
        if (selectedValue) {
          const selectedOption = field.options.find(opt => (opt.label || opt.value) === selectedValue);
          if (selectedOption && Array.isArray(selectedOption.subfields) && selectedOption.subfields.length > 0) {
            extractSelectedValues(selectedOption.subfields, formValues, `${key}-`);
          }
        }
      }
    });
    return formValues;
  }, []);

  useEffect(() => {
    if (editAction) {
      setSelectedAction(editAction);
      // Recursively extract selectedValue from config using nested keys
      const newFormValues = {};
      (editAction.config || []).forEach((cfg, idx) => {
        if (cfg.fields) extractSelectedValues(cfg.fields, newFormValues, `${idx}-`);
      });
      
      // Handle actions field type - convert branchId/nodeIndex objects back to workflow action IDs
      Object.keys(newFormValues).forEach(key => {
        const value = newFormValues[key];
        if (value && typeof value === 'object' && value.branchId && value.nodeIndex !== undefined) {
          // Check if this might be a branchId/nodeIndex object for an action
          const matchingAction = workflowActions.find(action => 
            action.branchId === value.branchId && action.nodeIndex === value.nodeIndex
          );
          if (matchingAction) {
            newFormValues[key] = matchingAction.id;
          }
        }
      });
      
      setFormValues(newFormValues);

      // Load variable entries for Set Variable action (multi-variable support)
      if (editAction.name === 'Set Variable') {
        const nodeConfig = (editAction.config || []).find(c => c.name === 'Set Variable Node');
        const variablesField = nodeConfig?.fields?.find(f => f.label === 'Variables');
        if (variablesField?.selectedValue && Array.isArray(variablesField.selectedValue) && variablesField.selectedValue.length > 0) {
          setVariableEntries(variablesField.selectedValue.map(v => ({
            type: v.type || 'Text',
            name: v.name || '',
            valueSource: v.valueSource || 'Fixed value',
            fixedValue: v.value || '',
            contactField: v.contactField || '',
          })));
        } else {
          // Fall back to old single-variable format for backward compatibility
          const nameField = nodeConfig?.fields?.find(f => f.label === 'Variable name');
          const typeField = nodeConfig?.fields?.find(f => f.label === 'Variable type');
          const sourceField = nodeConfig?.fields?.find(f => f.label === 'Value source');
          if (nameField?.selectedValue) {
            const valueSourceRaw = sourceField?.selectedValue || 'Fixed value';
            const valueSourceNorm = String(valueSourceRaw).toLowerCase().includes('contact') ? 'From contact' : 'Fixed value';
            let fixedValue = '';
            let contactField = '';
            if (sourceField?.options) {
              const fixedOpt = sourceField.options.find(o => String(o.label || '').toLowerCase().includes('fixed'));
              const valSub = fixedOpt?.subfields?.find(s => s.label === 'Variable value');
              if (valSub?.selectedValue !== undefined) fixedValue = String(valSub.selectedValue);
              const contactOpt = sourceField.options.find(o => String(o.label || '').toLowerCase().includes('contact'));
              const cfSub = contactOpt?.subfields?.find(s => s.label === 'Contact field');
              if (cfSub?.selectedValue) contactField = cfSub.selectedValue;
            }
            setVariableEntries([{
              type: String(typeField?.selectedValue || 'text') === 'number' ? 'Number' : 'Text',
              name: nameField.selectedValue,
              valueSource: valueSourceNorm,
              fixedValue,
              contactField,
            }]);
          } else {
            setVariableEntries([{ type: 'Text', name: '', valueSource: 'Fixed value', fixedValue: '', contactField: '' }]);
          }
        }
      }

      // Check if this is a Contact category action, Quick ACTION, or Business Hours branch - if so, don't load keywords
      const isContactCategory = editAction?.category === 'Contact';
      const isQuickAction = editAction?.name?.toLowerCase().includes('quick action');
      const isBusinessHoursBranch = editAction?.name === 'Branch: Business Hours' || editAction?.name === CONTACT_FIELDS_BRANCH_ACTION_NAME;
      const shouldExcludeKeywords = isContactCategory || isQuickAction || isBusinessHoursBranch || isGoToActionName(editAction?.name);

      // Load keywords from action if they exist and action is not Contact category or Quick ACTION
      if (!shouldExcludeKeywords) {
        // Load keywords from action if they exist
        // editAction is the action object itself (from stepRaw.action), so keywords are directly on editAction
        if (editAction.keywords && Array.isArray(editAction.keywords)) {
          setKeywords(editAction.keywords);
        } else {
          setKeywords([]);
        }

        // Load the keyword branches' no-reply timer from its branch trigger node
        const noReplyBranchId = (editAction.next || []).find(id => typeof id === 'string' && id.startsWith('no-reply-'));
        const noReplyFields = window.store?.getState?.()?.workflow?.selectedWorkflow?.currentVersion?.branches?.[noReplyBranchId]?.[0]?.trigger?.config?.[0]?.fields;
        if (noReplyFields) {
          const savedNoReply = { ...DEFAULT_KEYWORD_NO_REPLY, enabled: true };
          noReplyFields.forEach(field => { savedNoReply[field.label] = field.selectedValue; });
          setKeywordNoReply(savedNoReply);
        } else {
          setKeywordNoReply(DEFAULT_KEYWORD_NO_REPLY);
        }

        // Load incorrect keyword message config from action if it exists
        if (editAction.incorrectKeywordMessage) {
          setIncorrectKeywordMessage({
            enabled: editAction.incorrectKeywordMessage.enabled !== undefined ? editAction.incorrectKeywordMessage.enabled : false,
            text: editAction.incorrectKeywordMessage.text || "I didn't understand that. Please try again.",
            showOptions: editAction.incorrectKeywordMessage.showOptions || false
          });
        } else {
          // Use default values
          setIncorrectKeywordMessage({
            enabled: false,
            text: "I didn't understand that. Please try again.",
            showOptions: false
          });
        }
        
        // Load waitForKeyword setting from action if it exists
        if (editAction.waitForKeyword !== undefined) {
          setWaitForKeyword(editAction.waitForKeyword);
        } else {
          // Default to false (send next message immediately)
          setWaitForKeyword(false);
        }
      } else {
        // Don't load keywords for Contact category or Quick ACTION
        setKeywords([]);
        setKeywordNoReply(DEFAULT_KEYWORD_NO_REPLY);
        setIncorrectKeywordMessage({
          enabled: false,
          text: "I didn't understand that. Please try again.",
          showOptions: false
        });
        setWaitForKeyword(false);
      }
    } else {
      setKeywords([]);
      setKeywordNoReply(DEFAULT_KEYWORD_NO_REPLY);
      setIncorrectKeywordMessage({
        enabled: false,
        text: "I didn't understand that. Please try again.",
        showOptions: false
      });
      setWaitForKeyword(false);
      setVariableEntries([{ type: 'Text', name: '', valueSource: 'Fixed value', fixedValue: '', contactField: '' }]);
    }
  }, [editAction, workflowActions, extractSelectedValues]);

  // Scan config for team/teammember fields
  useEffect(() => {
    if (!localConfig) return;
    let needsTeams = false;
    let needsTeamMembers = false;
    let needsRoles = false;
    let needsTemplates = false;
    let needsCatalogs = false;
    
    const scanFields = (fields) => {
      for (const field of fields) {
        if (field.type === 'team') needsTeams = true;
        if (field.type === 'teammember') needsTeamMembers = true;
        if (field.type === 'multipleteammember') needsTeamMembers = true; // Added for multiple team members
        if (field.type === 'multipleroles') needsRoles = true; // Added for multipleroles
        if (field.type === 'template') needsTemplates = true; // Added for templates
        if (field.type === 'catalog') {
          needsCatalogs = true; // Added for catalogs
        }
        if (field.subfields) scanFields(field.subfields);
        if (field.fields) scanFields(field.fields);
        if (Array.isArray(field.options)) {
          field.options.forEach((option) => {
            if (option && typeof option === 'object' && Array.isArray(option.subfields)) {
              scanFields(option.subfields);
            }
          });
        }
      }
    };
    for (const cfg of localConfig) {
      if (cfg.fields) scanFields(cfg.fields);
    }
    
    if (needsTeams &&
        !teamsFetchAttemptedRef.current &&
        (!teams || teams.length === 0) &&
        !settingsLoading) {
      teamsFetchAttemptedRef.current = true;
      dispatch(fetchTeams());
    }
    if (needsTeamMembers &&
        !teamMembersFetchAttemptedRef.current &&
        (!teamMembers || teamMembers.length === 0) &&
        !settingsLoading) {
      teamMembersFetchAttemptedRef.current = true;
      dispatch(fetchTeamMembers());
    }
    if (needsRoles &&
        !rolesFetchAttemptedRef.current &&
        (!rolesWithUsage || rolesWithUsage.length === 0) &&
        !settingsLoading) {
      rolesFetchAttemptedRef.current = true;
      dispatch(fetchRolesWithUsage());
    }
    if (
      needsTemplates &&
      (!templates || templates.length === 0) &&
      templatesStatus !== 'loading' &&
      templatesStatus !== 'succeeded'
    ) {
      dispatch(fetchTemplates());
    }
    // Reset fetch attempt flag if catalogs are no longer needed
    if (!needsCatalogs) {
      catalogsFetchAttemptedRef.current = false;
    }
    
    // Only fetch catalogs if:
    // 1. A catalog field is actually needed in the config
    // 2. We haven't already attempted to fetch (to prevent infinite loops)
    // 3. A fetch is not already in progress
    // 4. Catalogs are not currently loading or already succeeded
    // 5. We don't already have catalogs
    if (needsCatalogs && 
        !catalogsFetchAttemptedRef.current && 
        !catalogsFetchInProgressRef.current &&
        catalogsStatus !== 'loading' && 
        catalogsStatus !== 'succeeded' && // Don't fetch if we already have a successful fetch
        (!catalogs || catalogs.length === 0)) {
      catalogsFetchAttemptedRef.current = true;
      catalogsFetchInProgressRef.current = true;
      console.log('📦 Fetching catalogs from ChooseActionDrawer');
      dispatch(fetchCatalogs()).finally(() => {
        catalogsFetchInProgressRef.current = false;
      });
    }
    
    // Reset in-progress flag when status changes to succeeded or failed
    if (catalogsStatus === 'succeeded' || catalogsStatus === 'failed') {
      catalogsFetchInProgressRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- catalogs.length used intentionally instead of catalogs to prevent re-triggers
  }, [localConfig, teams, teamMembers, rolesWithUsage, settingsLoading, dispatch, templates, templatesStatus, catalogsStatus, catalogs?.length]);

  // Helper to recursively duplicate a section at a given path
  function duplicateSectionAtPath(fields, path) {
    if (path.length === 0) return fields;
    const idx = path[0];
    if (path.length === 1) {
      // Duplicate at this level
      const sectionToDuplicate = fields[idx];
      const newFields = [...fields];
      newFields.splice(idx + 1, 0, JSON.parse(JSON.stringify(sectionToDuplicate)));
      return newFields;
    } else {
      // Go deeper
      return fields.map((f, i) => {
        if (i === idx && Array.isArray(f.subfields)) {
          return { ...f, subfields: duplicateSectionAtPath(f.subfields, path.slice(1)) };
        }
        return f;
      });
    }
  }

  // Helper function to count sections and total options in list message
  const countSectionsAndOptions = (fields) => {
    let sectionCount = 0;
    let totalOptions = 0;
    
    if (!Array.isArray(fields)) return { sectionCount: 0, totalOptions: 0 };
    
    for (const field of fields) {
      const fieldLabel = (field.label || '').toLowerCase();
      
      // Count top-level "Section" fields
      if (fieldLabel === 'section' && Array.isArray(field.subfields)) {
        sectionCount++;
        // Count "Option" fields that are direct children of this Section
        for (const sub of field.subfields) {
          if ((sub.label || '').toLowerCase() === 'option' && Array.isArray(sub.subfields)) {
            totalOptions++;
          }
        }
      }
    }
    
    return { sectionCount, totalOptions };
  };
  // Helper function to count buttons in interactive reply buttons message
  const countButtons = (fields) => {
    let buttonCount = 0;
    
    if (!Array.isArray(fields)) return { buttonCount: 0 };
    
    for (const field of fields) {
      const fieldLabel = (field.label || '').toLowerCase();
      
      // Count "Button" fields (section type)
      if (fieldLabel === 'button' && Array.isArray(field.subfields)) {
        buttonCount++;
      }
    }
    
    return { buttonCount };
  };

  // Handler to duplicate a section at any depth
  const handleDuplicateSection = (cfgIdx, fieldPath) => {
    // Only validate for list message actions
    if (selectedAction?.name === 'Send list message') {
      // Count current sections and options
      let totalSections = 0;
      let totalOptions = 0;
      
      if (localConfig && localConfig[cfgIdx] && Array.isArray(localConfig[cfgIdx].fields)) {
        const counts = countSectionsAndOptions(localConfig[cfgIdx].fields);
        totalSections = counts.sectionCount;
        totalOptions = counts.totalOptions;
      }
      
      // Traverse the path to find the field being duplicated
      let currentFields = localConfig?.[cfgIdx]?.fields;
      let fieldToDuplicate = null;
      
      if (currentFields && fieldPath.length > 0) {
        for (let i = 0; i < fieldPath.length; i++) {
          const idx = fieldPath[i];
          if (currentFields && currentFields[idx]) {
            fieldToDuplicate = currentFields[idx];
            if (i < fieldPath.length - 1 && Array.isArray(fieldToDuplicate.subfields)) {
              currentFields = fieldToDuplicate.subfields;
            }
          }
        }
      }
      
      // Determine if we're adding a Section or an Option
      const isAddingSection = fieldToDuplicate && (fieldToDuplicate.label || '').toLowerCase() === 'section';
      const isAddingOption = fieldToDuplicate && (fieldToDuplicate.label || '').toLowerCase() === 'option';
      
      if (isAddingSection) {
        if (totalSections >= 10) {
          alert('Maximum 10 sections allowed. Cannot add more sections.');
          return;
        }
      } else if (isAddingOption) {
        // Adding an option - check total options limit
        if (totalOptions >= 10) {
          alert('Maximum 10 total options allowed across all sections. Cannot add more options.');
          return;
        }
      }
    }
    
    setLocalConfig(prev => {
      if (!prev) return prev;
      const newConfig = [...prev];
      const fields = newConfig[cfgIdx].fields;
      const newFields = duplicateSectionAtPath(fields, fieldPath);
      newConfig[cfgIdx] = { ...newConfig[cfgIdx], fields: newFields };
      // Also update Redux
      if (selectedAction) {
        dispatch(updateActionConfig({ actionId: selectedAction._id || selectedAction.id || selectedAction.name, newConfig: newConfig }));
      }
      return newConfig;
    });
  };

  // Handler to remove a section at any depth
  function removeSectionAtPath(fields, path) {
    if (path.length === 0) return fields;
    const idx = path[0];
    if (path.length === 1) {
      // Remove at this level
      const newFields = [...fields];
      newFields.splice(idx, 1);
      return newFields;
    } else {
      // Go deeper
      return fields.map((f, i) => {
        if (i === idx && Array.isArray(f.subfields)) {
          return { ...f, subfields: removeSectionAtPath(f.subfields, path.slice(1)) };
        }
        return f;
      });
    }
  }

  const handleRemoveSection = (cfgIdx, fieldPath) => {
    setLocalConfig(prev => {
      if (!prev) return prev;
      const newConfig = [...prev];
      const fields = newConfig[cfgIdx].fields;
      const newFields = removeSectionAtPath(fields, fieldPath);
      newConfig[cfgIdx] = { ...newConfig[cfgIdx], fields: newFields };
      // Also update Redux
      if (selectedAction) {
        dispatch(updateActionConfig({ actionId: selectedAction._id || selectedAction.id || selectedAction.name, newConfig: newConfig }));
      }
      return newConfig;
    });
  };

  // Clear validation errors when drawer or selected action changes
  useEffect(() => {
    setFieldErrors({});
  }, [open, selectedAction]);

  // Fetch templates when drawer opens
  useEffect(() => {
    if (
      open &&
      (!templates || templates.length === 0) &&
      templatesStatus !== 'loading' &&
      templatesStatus !== 'succeeded'
    ) {
      dispatch(fetchTemplates());
    }
  }, [open, templates, templatesStatus, dispatch]);

  // Reset catalog fetch attempt when drawer closes
  useEffect(() => {
    if (!open) {
      teamsFetchAttemptedRef.current = false;
      teamMembersFetchAttemptedRef.current = false;
      rolesFetchAttemptedRef.current = false;
      catalogsFetchAttemptedRef.current = false;
      catalogsFetchInProgressRef.current = false;
    }
  }, [open]);

  // Filter templates based on search term
  const filteredTemplates = React.useMemo(() => {
    if (!templates || templates.length === 0) return [];
    if (!templateSearchTerm.trim()) return templates;
    
    return templates.filter(template => 
      template.name?.toLowerCase().includes(templateSearchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(templateSearchTerm.toLowerCase()) ||
      template.components?.some(comp => 
        comp.text?.toLowerCase().includes(templateSearchTerm.toLowerCase())
      )
    );
  }, [templates, templateSearchTerm]);


  // Handle template media uploads
  const handleTemplateFileSelect = (fieldKey, template, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Find header component to determine format
    const headerComp = template.components?.find(c => c.type === 'HEADER');
    if (!headerComp) return;

    const format = (headerComp.format || 'TEXT').toUpperCase();
    
    // Validate file type
    const fileName = file.name.toLowerCase();
    if (format === 'DOCUMENT' && !fileName.endsWith('.pdf')) {
      alert('Please select a .pdf file for DOCUMENT type.');
      if (templateFileInputRefs.current[fieldKey]) {
        templateFileInputRefs.current[fieldKey].value = '';
      }
      return;
    } else if (format === 'IMAGE' && !(fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))) {
      alert('Please select a .png or .jpg/.jpeg file for IMAGE type.');
      if (templateFileInputRefs.current[fieldKey]) {
        templateFileInputRefs.current[fieldKey].value = '';
      }
      return;
    } else if (format === 'VIDEO' && !fileName.endsWith('.mp4')) {
      alert('Please select a .mp4 file for VIDEO type.');
      if (templateFileInputRefs.current[fieldKey]) {
        templateFileInputRefs.current[fieldKey].value = '';
      }
      return;
    }

    // Set current uploading template
    setCurrentUploadingTemplate(fieldKey);

    // Dispatch upload based on format
    if (format === 'IMAGE' || format === 'VIDEO') {
      dispatch(uploadFileWithMediaID(file));
    } else {
      dispatch(uploadFile(file));
    }
  };

  // Handle successful uploads
  useEffect(() => {
    if (uploadStatus === 'succeeded' && uploadResult && currentUploadingTemplate) {
      setTemplateMediaUploads(prev => ({
        ...prev,
        [currentUploadingTemplate]: uploadResult
      }));
      
      // Save media data to form values
      setFormValues(v => {
        const currentValue = v[currentUploadingTemplate];
        const updatedValue = typeof currentValue === 'object' ? { ...currentValue } : {};
        updatedValue.media = uploadResult;
        return { ...v, [currentUploadingTemplate]: updatedValue };
      });
      
      setCurrentUploadingTemplate(null);
      dispatch(clearUploadState());
    }
  }, [uploadStatus, uploadResult, currentUploadingTemplate, dispatch]);

  useEffect(() => {
    if (uploadMediaIDStatus === 'succeeded' && uploadMediaIDResult && currentUploadingTemplate) {
      setTemplateMediaUploads(prev => ({
        ...prev,
        [currentUploadingTemplate]: uploadMediaIDResult
      }));
      
      // Save media data to form values
      setFormValues(v => {
        const currentValue = v[currentUploadingTemplate];
        const updatedValue = typeof currentValue === 'object' ? { ...currentValue } : {};
        updatedValue.media = uploadMediaIDResult;
        return { ...v, [currentUploadingTemplate]: updatedValue };
      });
      
      setCurrentUploadingTemplate(null);
      dispatch(clearUploadMediaIDState());
    }
  }, [uploadMediaIDStatus, uploadMediaIDResult, currentUploadingTemplate, dispatch]);

  // Carousel card media upload handlers
  const handleCarouselCardFileSelect = (fieldKey, cardIndex, card, e) => {
    const file = e.target.files?.[0];
    if (!file) return;


    // Find header component to determine format
    const cardHeader = card.components?.find(c => c.type === 'header' || c.type === 'HEADER');
    if (!cardHeader) {
      alert(`Card ${cardIndex + 1} doesn't have a header component`);
      if (carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`]) {
        carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`].value = '';
      }
      return;
    }

    const format = (cardHeader.format || 'TEXT').toUpperCase();
    
    // Validate file type
    const fileName = file.name.toLowerCase();
    if (format === 'DOCUMENT' && !fileName.endsWith('.pdf')) {
      alert('Please select a .pdf file for DOCUMENT type.');
      if (carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`]) {
        carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`].value = '';
      }
      return;
    } else if (format === 'IMAGE' && !(fileName.endsWith('.png') || fileName.endsWith('.jpg') || fileName.endsWith('.jpeg'))) {
      alert('Please select a .png or .jpg/.jpeg file for IMAGE type.');
      if (carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`]) {
        carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`].value = '';
      }
      return;
    } else if (format === 'VIDEO' && !fileName.endsWith('.mp4')) {
      alert('Please select a .mp4 file for VIDEO type.');
      if (carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`]) {
        carouselFileInputRefs.current[`${fieldKey}-${cardIndex}`].value = '';
      }
      return;
    }

    // Set current uploading carousel card
    setCurrentUploadingCarouselCard(`${fieldKey}-${cardIndex}`);

    // Dispatch upload based on format
    if (format === 'IMAGE' || format === 'VIDEO') {
      dispatch(uploadFileWithMediaID(file));
    } else {
      dispatch(uploadFile(file));
    }
  };

  // Handle successful carousel card uploads
  useEffect(() => {
    if (uploadStatus === 'succeeded' && uploadResult && currentUploadingCarouselCard) {
      
      setCarouselMediaUploads(prev => ({
        ...prev,
        [currentUploadingCarouselCard]: uploadResult
      }));
      
      // Save media data to form values
      setFormValues(v => {
        const lastDashIndex = currentUploadingCarouselCard.lastIndexOf('-');
        const fieldKey = currentUploadingCarouselCard.slice(0, lastDashIndex);
        const cardIndex = currentUploadingCarouselCard.slice(lastDashIndex + 1);
        
        const currentValue = v[fieldKey];
        const updatedValue = typeof currentValue === 'object' ? { ...currentValue } : {};
        if (!updatedValue.carouselMedia) {
          updatedValue.carouselMedia = {};
        }
        updatedValue.carouselMedia[cardIndex] = uploadResult;
        
        return { ...v, [fieldKey]: updatedValue };
      });
      
      setCurrentUploadingCarouselCard(null);
      dispatch(clearUploadState());
    }
  }, [uploadStatus, uploadResult, currentUploadingCarouselCard, dispatch]);

  useEffect(() => {
    if (uploadMediaIDStatus === 'succeeded' && uploadMediaIDResult && currentUploadingCarouselCard) {
      
      setCarouselMediaUploads(prev => ({
        ...prev,
        [currentUploadingCarouselCard]: uploadMediaIDResult
      }));
      
      // Save media data to form values
      setFormValues(v => {
        const lastDashIndex = currentUploadingCarouselCard.lastIndexOf('-');
        const fieldKey = currentUploadingCarouselCard.slice(0, lastDashIndex);
        const cardIndex = currentUploadingCarouselCard.slice(lastDashIndex + 1);
        
        const currentValue = v[fieldKey];
        const updatedValue = typeof currentValue === 'object' ? { ...currentValue } : {};
        if (!updatedValue.carouselMedia) {
          updatedValue.carouselMedia = {};
        }
        updatedValue.carouselMedia[cardIndex] = uploadMediaIDResult;
        
        return { ...v, [fieldKey]: updatedValue };
      });
      
      setCurrentUploadingCarouselCard(null);
      dispatch(clearUploadMediaIDState());
    }
  }, [uploadMediaIDStatus, uploadMediaIDResult, currentUploadingCarouselCard, dispatch]);

  const handleAccordionChange = (category) => (event, isExpanded) => {
    setExpanded(isExpanded ? category : false);
  };

  /**
   * Validate required workflow action fields (message text, template, etc.).
   * Returns an object of fieldKey -> error message for empty/invalid fields.
   */
  const validateRequiredFields = () => {
    const errors = {};
    const MIN_CHARS = 1;

    // Validate incorrect keyword message when enabled
    if (incorrectKeywordMessage.enabled) {
      const text = (incorrectKeywordMessage.text || '').trim();
      if (text.length < MIN_CHARS) {
        errors['__incorrectKeywordMessage'] = 'Message text is required (at least 1 character).';
      }
    }

    if (selectedAction.name === 'API Integration') {
      const config = formValues['0-Configuration'] ?? DEFAULT_API_INTEGRATION_CONFIG;
      if (!(config.apiUrl || '').trim()) {
        errors['0-Configuration-apiUrl'] = 'API URL is required.';
      }
      return errors;
    }

    if (selectedAction.name === 'Branch: Business Hours') {
      const config = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE;
      if (!isBusinessHoursValueValid(config)) {
        errors['__businessHours'] = 'Fix the schedule errors before saving (each enabled day needs at least one valid, non-overlapping interval).';
      }
      return errors;
    }

    if (selectedAction.name === SET_CONTACT_FIELDS_ACTION_NAME) {
      const config = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_SET_CONTACT_FIELDS_VALUE;
      const setFieldsError = getSetContactFieldsError(config);
      if (setFieldsError) errors['__setContactFields'] = setFieldsError;
      return errors;
    }

    if (selectedAction.name === CONTACT_FIELDS_BRANCH_ACTION_NAME) {
      const config = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE;
      if (!isContactFieldsValueValid(config)) {
        errors['__contactFields'] = 'Select at least one contact field.';
      }
      return errors;
    }

    if (selectedAction.name === 'Set Variable') {
      if (!variableEntries.some(e => e.name.trim())) {
        errors['__setVariable'] = 'At least one variable must have a name.';
      } else {
        variableEntries.forEach((e, i) => {
          if (!e.name.trim()) {
            errors[`__setVariable_name_${i}`] = `Variable ${i + 1}: name is required.`;
          } else if (e.valueSource === 'From contact' && !e.contactField) {
            errors[`__setVariable_contact_${i}`] = `Variable ${i + 1}: contact field is required when using "From contact".`;
          }
        });
      }
      return errors;
    }

    if (!localConfig || !Array.isArray(localConfig)) return errors;

    const checkFields = (fields, parentKey) => {
      if (!Array.isArray(fields)) return;
      fields.forEach((field, idx) => {
        const key = `${parentKey}${field.label || field.name || idx}`;
        if (field.type === 'text' || field.type === 'text large') {
          const labelLower = (field.label || field.name || '').toLowerCase();
          const keyLower = key.toLowerCase();
          // In list message, option description is optional
          const isListMessageOptionDescription = selectedAction.name === 'Send list message' &&
            labelLower === 'description' && keyLower.includes('option');
          // Caption is optional for image, video, document (Caption is a sibling of Image/Video/Document in config, so key is e.g. "0-Caption")
          const isCaptionField = labelLower === 'caption';
          // Set Variable: "Variable value" is optional (fixed value can be empty; when "From contact" this subfield is not used)
          const isSetVariableValueField = selectedAction.name === 'Set Variable' && labelLower === 'variable value';
          // Catalog description is metadata and should not block saving a catalog action
          const isCatalogDescriptionField = labelLower === 'catalog description';
          // Interactive button description is optional
          const isButtonDescriptionField = labelLower === 'button description';
          if (isListMessageOptionDescription || isCaptionField || isSetVariableValueField || isCatalogDescriptionField || isButtonDescriptionField) {
            // skip required check
          } else {
            const val = formValues[key];
            const str = typeof val === 'string' ? val.trim() : '';
            if (str.length < MIN_CHARS) {
              const label = field.label || field.name || 'This field';
              errors[key] = `${label} is required (at least ${MIN_CHARS} character).`;
            }
          }
        }
        if (field.type === 'template') {
          const val = formValues[key];
          const hasTemplate = val && typeof val === 'object' && (val.name || val.selectedValue);
          if (!hasTemplate) {
            const label = field.label || field.name || 'Template';
            errors[key] = `${label} is required. Please select a template.`;
          }
        }
        if (field.type === 'catalog' && (selectedAction.name === 'Send Catalog Message' || selectedAction.name === 'Send catalog message' || selectedAction.name === 'Send Catalog')) {
          const val = formValues[key];
          const hasCatalog = val && typeof val === 'object' && (val.id || val._id);
          if (!hasCatalog) {
            const label = field.label || field.name || 'Catalog';
            errors[key] = `${label} is required. Please select a catalog.`;
          }
        }
        if (field.subfields && field.type !== 'catalog') checkFields(field.subfields, `${key}-${idx}-`);
        if (field.fields) checkFields(field.fields, `${key}-${idx}-`);
        if (field.type === 'select' && Array.isArray(field.options)) {
          const selectedValue = formValues[key];
          if (field.required && !selectedValue) {
            const label = field.label || field.name || 'Selection';
            errors[key] = `${label} is required.`;
          }
          const selectedOption = field.options.find(opt => (opt.label || opt.value) === selectedValue);
          if (selectedOption?.subfields) checkFields(selectedOption.subfields, `${key}-`);
        }
      });
    };

    localConfig.forEach((cfg, idx) => {
      if (cfg.fields) checkFields(cfg.fields, `${idx}-`);
    });

    return errors;
  };

  // Save handler for actions
  const handleSaveAction = async () => {
    if (!selectedAction) return;
    const { blocked, message: blockMsg } = await checkSubscriptionActive(getAuthToken);
    if (blocked) { toast.error(blockMsg, { id: 'subscription-blocked', duration: 5000 }); return; }

    setFieldErrors({});
    const validationErrors = validateRequiredFields();
    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      const firstError = Object.values(validationErrors)[0];
      const message = Object.keys(validationErrors).length === 1
        ? firstError
        : `Please fix the following:\n\n${Object.values(validationErrors).join('\n')}`;
      alert(message);
      return;
    }

    // Get current workflow state to check for existing branches
    const currentState = window.store?.getState ? window.store.getState() : null;
    const currentBranches = currentState?.workflow?.selectedWorkflow?.currentVersion?.branches || {};
    
    // API Integration action: config is stored in formValues['0-Configuration']
    let configWithSelectedValues;
    if (selectedAction.name === 'API Integration') {
      const configValue = formValues['0-Configuration'] ?? DEFAULT_API_INTEGRATION_CONFIG;
      configWithSelectedValues = [
        {
          name: 'API Integration Node',
          required: true,
          fields: [
            { label: 'Configuration', type: 'apiIntegrationForm', selectedValue: configValue },
          ],
        },
      ];
    } else if (selectedAction.name === 'Branch: Business Hours') {
      const configValue = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE;
      configWithSelectedValues = [
        {
          name: 'Business Hours Node',
          required: true,
          fields: [
            { label: 'Configuration', type: 'businessHours', selectedValue: configValue },
          ],
        },
      ];
    } else if (selectedAction.name === SET_CONTACT_FIELDS_ACTION_NAME) {
      const configValue = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_SET_CONTACT_FIELDS_VALUE;
      configWithSelectedValues = buildSetContactFieldsConfig(configValue);
    } else if (selectedAction.name === CONTACT_FIELDS_BRANCH_ACTION_NAME) {
      const configValue = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE;
      configWithSelectedValues = buildContactFieldsConfig(configValue);
    } else if (selectedAction.name === 'Set Variable') {
      // Serialize variableEntries into the new multi-variable config format
      const validEntries = variableEntries.filter(e => e.name.trim());
      configWithSelectedValues = [
        {
          name: 'Set Variable Node',
          required: true,
          fields: [
            {
              label: 'Variables',
              type: 'json',
              selectedValue: validEntries.map(e => ({
                type: e.type,
                name: e.name.trim(),
                valueSource: e.valueSource,
                value: e.fixedValue,
                contactField: e.contactField,
              })),
            },
          ],
        },
      ];
    } else {
      // Recursively set selectedValue for all fields using nested keys
      configWithSelectedValues = (localConfig || []).map((cfg, idx) => ({
        ...cfg,
        fields: cfg.fields ? setSelectedValues(cfg.fields, formValues, `${idx}-`, isNodeBeforeEndNode) : [],
      }));
    }

    // Normalize template fields to match modal actions structure
    const enhanceTemplateFields = (fields, parentKey = "") => {
      if (!Array.isArray(fields)) return;
      fields.forEach((field, idx) => {
        const key = `${parentKey}${field.label || field.name || idx}`;
        if (field.type === 'template') {
          const templateVal = formValues[key];
          if (templateVal && typeof templateVal === 'object') {
            
            // Store name as primary selectedValue for quick display
            field.selectedValue = templateVal.name || field.selectedValue || '';
            // Store full details in subfields so it persists like other actions' config
            field.subfields = [
              { label: 'Template Name', type: 'text', selectedValue: templateVal.name || '' },
              { label: 'Template Language', type: 'text', selectedValue: templateVal.language || '' },
              { label: 'Template Category', type: 'text', selectedValue: templateVal.category || '' },
              { label: 'Variables', type: 'json', selectedValue: templateVal.variables || {} },
              { label: 'Button Variables', type: 'json', selectedValue: templateVal.buttonVariables || {} },
              { label: 'Media', type: 'json', selectedValue: templateVal.media || null },
              { label: 'Carousel Media', type: 'json', selectedValue: templateVal.carouselMedia || {} },
            ];
            
            // Log carousel media if it exists
            if (templateVal.carouselMedia && Object.keys(templateVal.carouselMedia).length > 0) {
              // Carousel media will be saved to database
            }
          }
        }
        
        // Handle catalog field type
        if (field.type === 'catalog') {
          let catalogVal = formValues[key];
          
          // If not in formValues, check if field already has selectedValue (from editing)
          if ((!catalogVal || typeof catalogVal !== 'object') && field.selectedValue && typeof field.selectedValue === 'object') {
            catalogVal = field.selectedValue;
          }
          
          if (catalogVal && typeof catalogVal === 'object') {
            
            // Ensure catalog has required fields
            if (!catalogVal.id && !catalogVal._id) {
              console.error('❌ Catalog object missing id/_id:', catalogVal);
            }
            if (!catalogVal.products || !Array.isArray(catalogVal.products) || catalogVal.products.length === 0) {
              console.warn('⚠️ Catalog object has no products selected');
            }
            
            // Store the FULL catalog object in selectedValue (like templates do)
            // This is what the server-side extraction function expects
            field.selectedValue = catalogVal;
            
            // Also store full details in subfields for backup/display purposes
            field.subfields = [
              { label: 'Catalog Name', type: 'text', selectedValue: catalogVal.name || '' },
              { label: 'Catalog ID', type: 'text', selectedValue: catalogVal.id || catalogVal._id || '' },
              { label: 'Catalog Description', type: 'text', selectedValue: catalogVal.description || '' },
              { label: 'Business Vertical', type: 'text', selectedValue: catalogVal.vertical || '' },
              { label: 'Total Products', type: 'number', selectedValue: catalogVal.totalProducts || 0 },
              { label: 'Catalog Status', type: 'text', selectedValue: catalogVal.isEnabled ? 'Active' : 'Inactive' },
              { label: 'Products', type: 'json', selectedValue: catalogVal.products || [] },
            ];
          } else if (field.subfields && field.subfields.length > 0) {
            // Fallback: Try to reconstruct catalog object from subfields if formValues is empty
            const catalogIdField = field.subfields.find(sf => sf.label === 'Catalog ID');
            const catalogNameField = field.subfields.find(sf => sf.label === 'Catalog Name');
            const productsField = field.subfields.find(sf => sf.label === 'Products');
            
            if (catalogIdField && catalogIdField.selectedValue) {
              // Reconstruct catalog object from subfields
              const reconstructedCatalog = {
                id: catalogIdField.selectedValue,
                _id: catalogIdField.selectedValue,
                name: catalogNameField?.selectedValue || '',
                products: productsField?.selectedValue || [],
              };
              field.selectedValue = reconstructedCatalog;
            } else {
              console.warn('⚠️ Catalog field found but no valid catalog data in formValues or subfields');
            }
          } else {
            console.warn('⚠️ Catalog field found but catalogVal is not an object and no subfields available');
            console.warn('⚠️ catalogVal:', catalogVal);
            console.warn('⚠️ field.selectedValue:', field.selectedValue);
          }
        }
        if (field.subfields) enhanceTemplateFields(field.subfields, `${key}-${idx}-`);
        if (field.fields) enhanceTemplateFields(field.fields, `${key}-${idx}-`);
      });
    };

    (configWithSelectedValues || []).forEach(cfg => {
      enhanceTemplateFields(cfg.fields, `${cfg.name || ''}-`);
    });
    
    
    // Special handling for "Assign conversation" action to store selected IDs
    if (selectedAction.name === 'Assign conversation') {
      
      // Find the assignment style field
      const assignmentStyleField = configWithSelectedValues[0]?.fields?.find(field => 
        field.label === 'Select Assignment Style'
      );
      
      if (assignmentStyleField && assignmentStyleField.selectedValue) {
        
        // Find the selected option that contains the subfields
        const selectedOption = assignmentStyleField.options?.find(option => 
          option.label === assignmentStyleField.selectedValue
        );
        
        if (selectedOption && selectedOption.subfields) {
          // Process each subfield to capture selected IDs
          selectedOption.subfields.forEach(subfield => {
            
            // Find the form value for this subfield by searching through all form keys
            let formKey = null;
            let formValue = null;
            
            // Search for the form key that matches this subfield
            Object.keys(formValues).forEach(key => {
              if (key.includes(subfield.label)) {
                formKey = key;
                formValue = formValues[key];
              }
            });
            
            if (formKey && formValue) {
              if (subfield.type === 'multipleteammember' || subfield.type === 'teammember') {
                const selectedIds = Array.isArray(formValue) ? formValue : [formValue];
                subfield.selectedValues = selectedIds;
                subfield.selectedNames = selectedIds.map(id => {
                  const member = (teamMembers || []).find(m => m._id === id || String(m._id) === String(id));
                  return member ? (member.name || member.email || id) : id;
                });
              } else if (subfield.type === 'team') {
                const selectedTeamId = formValue;
                subfield.selectedValues = [selectedTeamId];
                const team = (teams || []).find(t => t._id === selectedTeamId || String(t._id) === String(selectedTeamId));
                subfield.selectedNames = [team ? (team.teamName || team.name || selectedTeamId) : selectedTeamId];
              } else if (subfield.type === 'multipleroles') {
                const selectedRoleIds = Array.isArray(formValue) ? formValue : [formValue];
                subfield.selectedValues = selectedRoleIds;
              }
            }
          });
        }
      }
    }
    
    const actionWithConfig = {
      ...selectedAction,
      config: configWithSelectedValues,
    };
    
    // Check if this is a Contact category action, Quick ACTION, or Business Hours branch - if so, don't save keywords
    const isContactCategory = selectedAction?.category === 'Contact' || editAction?.category === 'Contact';
    const isQuickAction = selectedAction?.name?.toLowerCase().includes('quick action') || editAction?.name?.toLowerCase().includes('quick action');
    const isBusinessHoursBranch = selectedAction?.name === 'Branch: Business Hours' || editAction?.name === 'Branch: Business Hours'
      || selectedAction?.name === CONTACT_FIELDS_BRANCH_ACTION_NAME || editAction?.name === CONTACT_FIELDS_BRANCH_ACTION_NAME;
    const shouldExcludeKeywords = isContactCategory || isQuickAction || isBusinessHoursBranch || isGoToActionName(selectedAction?.name) || isGoToActionName(editAction?.name);

    // Add keywords to action if they exist and action is not Contact category or Quick ACTION
    if (!shouldExcludeKeywords) {
      if (useKeywordBranches) {
        await syncKeywordBranches(actionWithConfig);
      } else if (keywords && keywords.length > 0) {
        actionWithConfig.keywords = keywords.filter(kw => kw.keyword && kw.targetId);
      } else {
        // Remove keywords if none exist
        delete actionWithConfig.keywords;
      }
      
      // Add incorrect keyword message config to action
      if (incorrectKeywordMessage) {
        actionWithConfig.incorrectKeywordMessage = {
          enabled: incorrectKeywordMessage.enabled !== undefined ? incorrectKeywordMessage.enabled : false,
          text: incorrectKeywordMessage.text || "I didn't understand that. Please try again.",
          showOptions: incorrectKeywordMessage.showOptions || false
        };
      }
      
      // Add waitForKeyword setting to action (only if keywords are configured)
      if (actionWithConfig.keywords?.length > 0 && (useKeywordBranches || keywords.some(kw => kw.keyword && kw.targetId))) {
        // Keyword branches only run if the enrollment is still active when the user types the keyword,
        // so the flow must pause here (same as a list message waiting for a reply).
        actionWithConfig.waitForKeyword = useKeywordBranches ? true : waitForKeyword;
      } else {
        // Remove waitForKeyword if no keywords are configured
        delete actionWithConfig.waitForKeyword;
      }
    } else {
      // Remove all keyword-related fields for Contact category and Quick ACTION
      delete actionWithConfig.keywords;
      delete actionWithConfig.incorrectKeywordMessage;
      delete actionWithConfig.waitForKeyword;
    }
    
    // Validation for "Send list message" lengths
    if (actionWithConfig.name === 'Send list message') {
      const findFieldValueByLabel = (fields, targetLabel) => {
        if (!Array.isArray(fields)) return undefined;
        const target = (targetLabel || '').toLowerCase();
        for (const field of fields) {
          const label = (field.label || field.name || '').toLowerCase();
          if (label === target) {
            return field.selectedValue;
          }
          // Search nested structures
          if (Array.isArray(field.subfields)) {
            const foundInSub = findFieldValueByLabel(field.subfields, targetLabel);
            if (foundInSub !== undefined) return foundInSub;
          }
          if (Array.isArray(field.fields)) {
            const foundInFields = findFieldValueByLabel(field.fields, targetLabel);
            if (foundInFields !== undefined) return foundInFields;
          }
        }
        return undefined;
      };

      // Pull values from the normalized config
      let buttonTitleValue;
      let messageBodyValue;
      let messageHeaderValue;
      for (const cfg of actionWithConfig.config || []) {
        if (buttonTitleValue === undefined) {
          buttonTitleValue = findFieldValueByLabel(cfg.fields, 'Button Title');
        }
        if (messageBodyValue === undefined) {
          messageBodyValue = findFieldValueByLabel(cfg.fields, 'Message body');
        }
        if (messageHeaderValue === undefined) {
          messageHeaderValue = findFieldValueByLabel(cfg.fields, 'Message header');
        }
        if (buttonTitleValue !== undefined && messageBodyValue !== undefined && messageHeaderValue !== undefined) break;
      }

      // Fallback to form values if not found in config
      if (buttonTitleValue === undefined) {
        const key = Object.keys(formValues).find(k => k.toLowerCase().includes('button title'));
        if (key) buttonTitleValue = formValues[key];
      }
      if (messageBodyValue === undefined) {
        const key = Object.keys(formValues).find(k => k.toLowerCase().includes('message body'));
        if (key) messageBodyValue = formValues[key];
      }
      if (messageHeaderValue === undefined) {
        const key = Object.keys(formValues).find(k => k.toLowerCase().includes('message header'));
        if (key) messageHeaderValue = formValues[key];
      }

      if (typeof buttonTitleValue === 'string' && buttonTitleValue.length > 20) {
        alert('Button must be at most 20 characters.');
        setSaving(false);
        return;
      }
      if (typeof messageBodyValue === 'string' && messageBodyValue.length > 4096) {
        alert('Message body must be at most 4096 characters.');
        setSaving(false);
        return;
      }
      if (typeof messageHeaderValue === 'string' && messageHeaderValue.length > 60) {
        alert('Message header must be at most 60 characters.');
        setSaving(false);
        return;
      }

      // Validate Section title (<=24), Option title (<=24), Option description (<=72)
      const validateSectionsAndOptions = (fields) => {
        if (!Array.isArray(fields)) return { ok: true };
        for (const field of fields) {
          if ((field.label || '').toLowerCase() === 'section' && Array.isArray(field.subfields)) {
            // Section title
            const sectionTitleField = field.subfields.find(sf => (sf.label || '').toLowerCase() === 'section title');
            const sectionTitleVal = sectionTitleField?.selectedValue;
            if (typeof sectionTitleVal === 'string' && sectionTitleVal.length > 24) {
              return { ok: false, message: 'Section title must be at most 24 characters.' };
            }
            // Options under section
            for (const sub of field.subfields) {
              if ((sub.label || '').toLowerCase() === 'option' && Array.isArray(sub.subfields)) {
                const optionTitleField = sub.subfields.find(osf => (osf.label || '').toLowerCase() === 'title');
                const optionDescField = sub.subfields.find(osf => (osf.label || '').toLowerCase() === 'description');
                const optionTitleVal = optionTitleField?.selectedValue;
                const optionDescVal = optionDescField?.selectedValue;
                if (typeof optionTitleVal === 'string' && optionTitleVal.length > 24) {
                  return { ok: false, message: 'Option label must be at most 24 characters.' };
                }
                if (typeof optionDescVal === 'string' && optionDescVal.length > 72) {
                  return { ok: false, message: 'Description must be at most 72 characters.' };
                }
              }
            }
          }
          // Recurse deeper if fields exist
          if (Array.isArray(field.fields)) {
            const r = validateSectionsAndOptions(field.fields);
            if (!r.ok) return r;
          }
          if (Array.isArray(field.subfields)) {
            const r = validateSectionsAndOptions(field.subfields);
            if (!r.ok) return r;
          }
        }
        return { ok: true };
      };

      for (const cfg of actionWithConfig.config || []) {
        const res = validateSectionsAndOptions(cfg.fields);
        if (!res.ok) {
          alert(res.message);
          setSaving(false);
          return;
        }
      }
    }
    
    // Validation for "Send Catalog Message" - ensure catalog is selected
    if (actionWithConfig.name === 'Send Catalog Message' || actionWithConfig.name === 'Send catalog message' || actionWithConfig.name === 'Send Catalog') {
      const findCatalogField = (fields) => {
        if (!Array.isArray(fields)) return null;
        for (const field of fields) {
          if (field.type === 'catalog') {
            return field;
          }
          if (field.subfields) {
            const found = findCatalogField(field.subfields);
            if (found) return found;
          }
          if (field.fields) {
            const found = findCatalogField(field.fields);
            if (found) return found;
          }
        }
        return null;
      };
      
      let catalogField = null;
      for (const cfg of actionWithConfig.config || []) {
        catalogField = findCatalogField(cfg.fields);
        if (catalogField) break;
      }
      
      if (catalogField) {
        const catalogValue = catalogField.selectedValue;
        // Check if catalog is selected (should be an object with id)
        if (!catalogValue || typeof catalogValue !== 'object' || (!catalogValue.id && !catalogValue._id)) {
          alert('Please select a catalog and at least one product before saving.');
          setSaving(false);
          return;
        }
        
        // Check if at least one product is selected
        if (!catalogValue.products || !Array.isArray(catalogValue.products) || catalogValue.products.length === 0) {
          alert('Please select at least one product from the catalog.');
          setSaving(false);
          return;
        }
        // Meta API allows at most 30 products per catalog message
        if (catalogValue.products.length > 30) {
          alert('Please select at most 30 products. The Meta/WhatsApp API does not allow more.');
          setSaving(false);
          return;
        }
      } else {
        alert('Catalog field not found in action config. Please check the action configuration.');
        setSaving(false);
        return;
      }
    }

    setSaving(true);

    // Special logic for interactive reply buttons message actions
    if (actionWithConfig.name === 'Send interactive reply buttons message' || 
      actionWithConfig.name === 'Send Interactive Reply Buttons Message') {
    
    // Validate buttons count before processing
    let totalButtons = 0;
    
    (actionWithConfig.config || []).forEach(cfg => {
      if (Array.isArray(cfg.fields)) {
        const counts = countButtons(cfg.fields);
        totalButtons += counts.buttonCount;
      }
    });
    
    // Validate button count
    if (totalButtons > WEBSITE_MAX_BUTTONS) {
      alert(`Maximum ${WEBSITE_MAX_BUTTONS} buttons allowed. You have ${totalButtons} buttons. Please remove ${totalButtons - WEBSITE_MAX_BUTTONS} button(s) before saving.`);
      setSaving(false);
      return;
    }
    
    // Validate at least one button exists
    if (totalButtons === 0) {
      alert('At least one button is required. Please add at least one button before saving.');
      setSaving(false);
      return;
    }
    
    // Extract all buttons from config
    const buttons = [];
    (actionWithConfig.config || []).forEach(cfg => {
      (cfg.fields || []).forEach(field => {
        if (field.label && field.label.toLowerCase().includes('button') && Array.isArray(field.subfields)) {
          const titleField = field.subfields.find(f => 
            (f.label === 'Button Title' || f.label === 'Title') && f.selectedValue
          );
          if (titleField && titleField.selectedValue) {
            buttons.push({
              title: titleField.selectedValue.trim()
            });
          }
        }
      });
    });
    
    // Alternative extraction method using form values if config traversal fails
    if (buttons.length === 0) {
      const buttonTitles = [];
      
      // Look for form values that match the pattern "X-Button-Y-Button Title"
      Object.keys(formValues).forEach(key => {
        const match = key.match(/(\d+)-Button-(\d+)-Button Title/);
        if (match) {
          const [, configIndex, buttonIndex] = match;
          const title = formValues[key];
          if (title && title.trim()) {
            buttonTitles.push({
              title: title.trim(),
              configIndex: parseInt(configIndex),
              buttonIndex: parseInt(buttonIndex)
            });
          }
        }
      });
      
      // Sort by configIndex and buttonIndex to maintain order
      buttonTitles.sort((a, b) => {
        if (a.configIndex !== b.configIndex) return a.configIndex - b.configIndex;
        return a.buttonIndex - b.buttonIndex;
      });
      
      buttons.push(...buttonTitles);
    }
    
    // Create branches for each button (always create branches for buttons, similar to list message options)
    if (buttons.length > 0) {
      // Get current workflow state to check for existing branches
      const currentState = window.store?.getState ? window.store.getState() : null;
      const currentBranches = currentState?.workflow?.selectedWorkflow?.currentVersion?.branches || {};
      
      // Check if we're editing an existing action and reuse existing branches
      let branchIds = [];
      if (editAction && editAction.next && Array.isArray(editAction.next)) {
        console.log('Editing existing action, reusing existing branches:', editAction.next);
        branchIds = editAction.next.filter(id => id.startsWith('button-'));
      } else {
        // Create new branches for each button
        branchIds = buttons.map((button, idx) => `button-${Date.now()}-${idx}`);
      }
      
      // Ensure we have enough branches for all buttons
      while (branchIds.length < buttons.length) {
        branchIds.push(`button-${Date.now()}-${branchIds.length}`);
      }
      
      // Remove extra branches if we have more than needed
      if (branchIds.length > buttons.length) {
        const branchesToRemove = branchIds.slice(buttons.length);
        console.log('Removing extra button branches:', branchesToRemove);
        
        for (const branchIdToRemove of branchesToRemove) {
          console.log(`Removing button branch: ${branchIdToRemove}`);
          await dispatch(removeBranch({ branchId: branchIdToRemove }));
        }
        
        branchIds = branchIds.slice(0, buttons.length);
      }
      
      // Create or update branches for each button
      for (let i = 0; i < branchIds.length; i++) {
        const branchId = branchIds[i];
        const button = buttons[i];
        
        // Check if branch already exists
        if (!currentBranches[branchId]) {
          await dispatch(addBranch({ branchId }));
        }
        
        // Clear existing nodes in the branch and add the trigger node
        const triggerNode = {
          id: `${branchId}-trigger`,
          type: 'trigger',
          trigger: {
            name: 'button reply',
            description: 'Trigger for button reply',
            triggerType: 'non-root',
            config: [{
              fields: [{
                label: 'Button Title',
                type: 'text',
                selectedValue: button.title
              }]
            }],
            category: 'Nil'
          }
        };
        
        // Update the branch with the new trigger node
        await dispatch(updateNodeInBranch({ 
          branchId, 
          nodeId: `${branchId}-trigger`, 
          updates: triggerNode 
        }));
        
        // If the node doesn't exist, add it
        const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
        const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
        if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
          await dispatch(addNodeToBranch({
            branchId,
            node: triggerNode
          }));
        }
      }
      
      // Set the next array for this node
      actionWithConfig.next = branchIds;

      // "Wait for reply" adds a timed "No reply" branch — workflow.expiry.js routes the contact
      // there when the interval passes without a button tap.
      const waitForReplyField = (actionWithConfig.config || [])
        .flatMap(cfg => cfg.fields || [])
        .find(f => f.label === 'Wait for reply');
      const waitForReplyEnabled = waitForReplyField?.selectedValue === true || waitForReplyField?.selectedValue === 'true';
      const existingNoReplyId = Array.isArray(editAction?.next) ? editAction.next.find(id => id.startsWith('no-reply-')) : null;

      if (waitForReplyEnabled) {
        const unitValue = (unit) => parseInt(
          waitForReplyField.subfields?.find(s => (s.label || '').toLowerCase() === unit)?.selectedValue,
          10
        ) || 0;
        const noReplyBranchId = existingNoReplyId || `no-reply-${Date.now()}`;
        const noReplyTriggerNode = {
          id: `${noReplyBranchId}-trigger`,
          type: 'trigger',
          trigger: {
            name: 'no reply',
            description: 'no reply',
            triggerType: 'non-root',
            config: [{
              fields: ['days', 'hours', 'minutes', 'seconds'].map(unit => ({
                type: 'number',
                label: unit,
                selectedValue: unitValue(unit)
              }))
            }],
            category: 'Nil'
          }
        };

        if (!currentBranches[noReplyBranchId]) {
          await dispatch(addBranch({ branchId: noReplyBranchId }));
        }
        await dispatch(updateNodeInBranch({
          branchId: noReplyBranchId,
          nodeId: `${noReplyBranchId}-trigger`,
          updates: noReplyTriggerNode
        }));
        const stateAfterNoReplyUpdate = window.store?.getState ? window.store.getState() : null;
        const noReplyBranchAfterUpdate = stateAfterNoReplyUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[noReplyBranchId];
        if (!noReplyBranchAfterUpdate || noReplyBranchAfterUpdate.length === 0) {
          await dispatch(addNodeToBranch({ branchId: noReplyBranchId, node: noReplyTriggerNode }));
        }
        actionWithConfig.next.push(noReplyBranchId);
      } else if (existingNoReplyId) {
        await dispatch(removeBranch({ branchId: existingNoReplyId }));
      }
    }
  }

    // Special logic for list message actions
    if (actionWithConfig.name === 'Send list message') {
      
      // Validate sections and options count before processing
      let totalSections = 0;
      let totalOptions = 0;
      
      (actionWithConfig.config || []).forEach(cfg => {
        if (Array.isArray(cfg.fields)) {
          const counts = countSectionsAndOptions(cfg.fields);
          totalSections += counts.sectionCount;
          totalOptions += counts.totalOptions;
        }
      });
      
      // Validate section count
      if (totalSections > 10) {
        alert(`Maximum 10 sections allowed. You have ${totalSections} sections. Please remove ${totalSections - 10} section(s) before saving.`);
        setSaving(false);
        return;
      }
      
      // Validate total options count
      if (totalOptions > 10) {
        alert(`Maximum 10 total options allowed across all sections. You have ${totalOptions} options. Please remove ${totalOptions - 10} option(s) before saving.`);
        setSaving(false);
        return;
      }
      
      // 1. Extract all options from all sections (correct structure)
      const options = [];
      
      (actionWithConfig.config || []).forEach(cfg => {
        (cfg.fields || []).forEach(field => {
          if (field.label === 'Section' && Array.isArray(field.subfields)) {
            field.subfields.forEach(section => {
              if (section.label === 'Option' && Array.isArray(section.subfields)) {
                options.push(section);
              }
            });
          }
        });
      });
      
      // 2. Check if "Wait for reply" is enabled
      let waitForReplyEnabled = false;
      
      
      // Check the config that has selectedValue set by setSelectedValues
      (actionWithConfig.config || []).forEach((cfg, cfgIdx) => {
        (cfg.fields || []).forEach(field => {
          if (field.label === 'Wait for reply') {
            waitForReplyEnabled = field.selectedValue === true || field.selectedValue === 'true';
          }
        });
      });
      
      // If not found in config, try form values
      if (!waitForReplyEnabled) {
        Object.keys(formValues).forEach(key => {
          if (key.includes('Wait for reply')) {
            const value = formValues[key];
            waitForReplyEnabled = value === true || value === 'true' || value === 'on' || value === 1;
          }
        });
      }
      
      
      // 3. Create branches based on options count and wait for reply setting
      
      let shouldCreateBranches = false;
      let shouldCreateWaitBranch = false;
      
      if (options.length === 1) {
        // Single option: only branch if wait for reply is enabled
        shouldCreateBranches = waitForReplyEnabled;
        shouldCreateWaitBranch = waitForReplyEnabled;
      } else if (options.length > 1) {
        // Multiple options: always create branches, wait branch if enabled
        shouldCreateBranches = true;
        shouldCreateWaitBranch = waitForReplyEnabled;
      } else {
        // No options: no branching
        shouldCreateBranches = false;
        shouldCreateWaitBranch = false;
      }
      
      
      if (shouldCreateBranches) {
        
        // Alternative extraction method using form values if config traversal fails
        if (options.length === 0) {
          const optionTitles = [];
          
          // Look for form values that match the pattern "X-Section-Y-Option-Z-Title"
          Object.keys(formValues).forEach(key => {
            const match = key.match(/(\d+)-Section-(\d+)-Option-(\d+)-Title/);
            if (match) {
              const [, configIndex, sectionIndex, optionIndex] = match;
              const title = formValues[key];
                              if (title && title.trim()) {
                  // Look for the Response variable name field in the form
                  const responseVariableKey = Object.keys(formValues).find(key => key.includes('Response variable name'));
                  let variableName = responseVariableKey ? formValues[responseVariableKey] : null;
                  
                  // If not found in form values, try to get it from the action's stored config
                  if (!variableName && selectedAction && selectedAction.config) {
                    for (const config of selectedAction.config) {
                      if (config.fields) {
                        for (const field of config.fields) {
                          if (field.label === 'Response variable name' && field.selectedValue) {
                            variableName = field.selectedValue;
                            break;
                          }
                        }
                      }
                    }
                  }
                  
                  // If still not found, use a default
                  if (!variableName) {
                    variableName = 'response_variable';
                  }
                  
                  optionTitles.push({
                  title: title.trim(),
                  configIndex: parseInt(configIndex),
                  sectionIndex: parseInt(sectionIndex),
                  optionIndex: parseInt(optionIndex),
                  variableName: variableName.trim()
                });
              }
            }
          });
          
          // Get current workflow state to check for existing branches
          const currentState = window.store?.getState ? window.store.getState() : null;
          const currentBranches = currentState?.workflow?.selectedWorkflow?.currentVersion?.branches || {};
          
          // Check if we're editing an existing action and reuse existing branches
          let branchIds = [];
          if (editAction && editAction.next && Array.isArray(editAction.next)) {
            console.log('Editing existing action, reusing existing branches:', editAction.next);
            branchIds = editAction.next.filter(id => id.startsWith('option-'));
          } else {
            // Create new branches for each option found in form values
            branchIds = optionTitles.map((option, idx) => `option-${Date.now()}-${idx}`);
          }
          
          // Ensure we have enough branches for all options
          while (branchIds.length < optionTitles.length) {
            branchIds.push(`option-${Date.now()}-${branchIds.length}`);
          }
          
          // Remove extra branches if we have more than needed
          if (branchIds.length > optionTitles.length) {
            // Get the branches that will be removed
            const branchesToRemove = branchIds.slice(optionTitles.length);
            console.log('Removing extra branches:', branchesToRemove);
            
            // Remove each branch from Redux state
            for (const branchIdToRemove of branchesToRemove) {
              console.log(`Removing option branch: ${branchIdToRemove}`);
              await dispatch(removeBranch({ branchId: branchIdToRemove }));
            }
            
            // Update branchIds array to only include the branches we're keeping
            branchIds = branchIds.slice(0, optionTitles.length);
          }
          
          for (let i = 0; i < branchIds.length; i++) {
            const branchId = branchIds[i];
            const option = optionTitles[i];
            
            // Check if branch already exists
            if (!currentBranches[branchId]) {
              await dispatch(addBranch({ branchId }));
            }
            
            // Clear existing nodes in the branch and add the trigger node
            const triggerNode = {
              id: `${branchId}-trigger`,
              type: 'trigger',
              trigger: {
                name: 'options',
                description: 'Trigger for option selection',
                triggerType: 'non-root',
                config: [{
                  fields: [{
                    label: option.variableName,
                    type: 'variable',
                    selectedValue: option.title
                  }]
                }],
                category: 'Nil'
              }
            };
            
            // Update the branch with the new trigger node
            await dispatch(updateNodeInBranch({ 
              branchId, 
              nodeId: `${branchId}-trigger`, 
              updates: triggerNode 
            }));
            
            // If the node doesn't exist, add it
            const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
            const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
            if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
              await dispatch(addNodeToBranch({
                branchId,
                node: triggerNode
              }));
            }
          }
          
          // 3. Set the next array for this node
          actionWithConfig.next = branchIds;
          
          // Only create a "No reply" branch if "Wait for reply" is checked
          if (shouldCreateWaitBranch) {
            
            // Extract time duration values from form
            let days = 0, hours = 0, minutes = 0, seconds = 0;
            
            // Look for time duration fields in the form values
            Object.keys(formValues).forEach(key => {
              if (key.includes('days') && key.includes('Wait for reply')) {
                days = parseInt(formValues[key]) || 0;
              } else if (key.includes('hours') && key.includes('Wait for reply')) {
                hours = parseInt(formValues[key]) || 0;
              } else if (key.includes('minutes') && key.includes('Wait for reply')) {
                minutes = parseInt(formValues[key]) || 0;
              } else if (key.includes('seconds') && key.includes('Wait for reply')) {
                seconds = parseInt(formValues[key]) || 0;
              }
            });
            
            // Also check in the action config for time values
            if (selectedAction && selectedAction.config) {
              selectedAction.config.forEach(config => {
                if (config.fields) {
                  config.fields.forEach(field => {
                    if (field.label === 'Wait for reply' && field.subfields) {
                      field.subfields.forEach(subfield => {
                        if (subfield.label === 'days' && subfield.selectedValue) {
                          days = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'hours' && subfield.selectedValue) {
                          hours = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'minutes' && subfield.selectedValue) {
                          minutes = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'seconds' && subfield.selectedValue) {
                          seconds = parseInt(subfield.selectedValue) || 0;
                        }
                      });
                    }
                  });
                }
              });
            }
            
            // Create trigger data for database
            const triggerData = {
              name: 'no reply',
              description: 'no reply',
              triggerType: 'non-root',
              config: [{
                fields: [
                  {
                    type: 'number',
                    label: 'days',
                    selectedValue: days
                  },
                  {
                    type: 'number',
                    label: 'hours',
                    selectedValue: hours
                  },
                  {
                    type: 'number',
                    label: 'minutes',
                    selectedValue: minutes
                  },
                  {
                    type: 'number',
                    label: 'seconds',
                    selectedValue: seconds
                  }
                ]
              }],
              category: 'Nil'
            };
            
            // Handle "No reply" branch
            let noReplyBranchId = null;
            if (editAction && editAction.next && Array.isArray(editAction.next)) {
              noReplyBranchId = editAction.next.find(id => id.startsWith('no-reply-'));
            }
            
            // Use existing no-reply branch or create new one
            if (!noReplyBranchId) {
              noReplyBranchId = `no-reply-${Date.now()}`;
            }
            
            // Check if branch already exists
            if (!currentBranches[noReplyBranchId]) {
              await dispatch(addBranch({ branchId: noReplyBranchId }));
            }
            
            // Update the no-reply branch with the new trigger node
            const noReplyTriggerNode = {
              id: `${noReplyBranchId}-trigger`,
              type: 'trigger',
              trigger: triggerData
            };
            
            await dispatch(updateNodeInBranch({ 
              branchId: noReplyBranchId, 
              nodeId: `${noReplyBranchId}-trigger`, 
              updates: noReplyTriggerNode 
            }));
            
            // If the node doesn't exist, add it
            const stateAfterNoReplyUpdate = window.store?.getState ? window.store.getState() : null;
            const noReplyBranchAfterUpdate = stateAfterNoReplyUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[noReplyBranchId];
            if (!noReplyBranchAfterUpdate || noReplyBranchAfterUpdate.length === 0) {
              await dispatch(addNodeToBranch({
                branchId: noReplyBranchId,
                node: noReplyTriggerNode
              }));
            }
            
            // Add "No reply" to the next array
            actionWithConfig.next.push(noReplyBranchId);
            console.log('Added No reply branch with time config:', noReplyBranchId);
          } else {
            console.log('Wait for reply is disabled, removing No reply branch if it exists');
            
            // Find and remove the no-reply branch if it exists
            if (editAction && editAction.next && Array.isArray(editAction.next)) {
              const noReplyBranchId = editAction.next.find(id => id.startsWith('no-reply-'));
              if (noReplyBranchId) {
                console.log(`Removing no-reply branch: ${noReplyBranchId}`);
                
                // Remove the branch from Redux state
                await dispatch(removeBranch({ branchId: noReplyBranchId }));
                
                // Remove the branch ID from the next array
                const nextIndex = actionWithConfig.next.indexOf(noReplyBranchId);
                if (nextIndex !== -1) {
                  actionWithConfig.next.splice(nextIndex, 1);
                  console.log(`Removed ${noReplyBranchId} from next array`);
                }
              }
            }
          }
        } else {
          
          // Check if we're editing an existing action and reuse existing branches
          let branchIds = [];
          if (editAction && editAction.next && Array.isArray(editAction.next)) {
            console.log('Editing existing action, reusing existing branches:', editAction.next);
            branchIds = editAction.next.filter(id => id.startsWith('option-'));
          } else {
            // Create new branches for each option found in config
            branchIds = options.map((option, idx) => `option-${Date.now()}-${idx}`);
          }
          
          // Ensure we have enough branches for all options
          while (branchIds.length < options.length) {
            branchIds.push(`option-${Date.now()}-${branchIds.length}`);
          }
          
          // Remove extra branches if we have more than needed
          if (branchIds.length > options.length) {
            // Get the branches that will be removed
            const branchesToRemove = branchIds.slice(options.length);
            console.log('Removing extra branches:', branchesToRemove);
            
            // Remove each branch from Redux state
            for (const branchIdToRemove of branchesToRemove) {
              console.log(`Removing option branch: ${branchIdToRemove}`);
              await dispatch(removeBranch({ branchId: branchIdToRemove }));
            }
            
            // Update branchIds array to only include the branches we're keeping
            branchIds = branchIds.slice(0, options.length);
          }
          
          for (let i = 0; i < branchIds.length; i++) {
            const branchId = branchIds[i];
            const option = options[i];
            
            // Extract the option title and variable name
            const titleField = (option.subfields || []).find(f => f.label === 'Title');
            const optionTitle = titleField ? titleField.selectedValue || 'Option' : 'Option';
            
            // Look for Response variable name field in the form
            const responseVariableKey = Object.keys(formValues).find(key => key.includes('Response variable name'));
            let variableName = responseVariableKey ? formValues[responseVariableKey] : null;
            
            // If not found in form values, try to get it from the action's stored config
            if (!variableName && selectedAction && selectedAction.config) {
              for (const config of selectedAction.config) {
                if (config.fields) {
                  for (const field of config.fields) {
                    if (field.label === 'Response variable name' && field.selectedValue) {
                      variableName = field.selectedValue;
                      break;
                    }
                  }
                }
              }
            }
            
            // If still not found, use a default
            if (!variableName) {
              variableName = 'response_variable';
            }
            
            // Check if branch already exists
            if (!currentBranches[branchId]) {
              await dispatch(addBranch({ branchId }));
            }
            
            // Clear existing nodes in the branch and add the trigger node
            const triggerNode = {
              id: `${branchId}-trigger`,
              type: 'trigger',
              trigger: {
                name: 'options',
                description: 'Trigger for option selection',
                triggerType: 'non-root',
                config: [{
                  fields: [{
                    label: variableName,
                    type: 'variable',
                    selectedValue: optionTitle
                  }]
                }],
                category: 'Nil'
              }
            };
            
            // Update the branch with the new trigger node
            await dispatch(updateNodeInBranch({ 
              branchId, 
              nodeId: `${branchId}-trigger`, 
              updates: triggerNode 
            }));
            
            // If the node doesn't exist, add it
            const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
            const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
            if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
              await dispatch(addNodeToBranch({
                branchId,
                node: triggerNode
              }));
            }
          }
          
          // 3. Set the next array for this node
          actionWithConfig.next = branchIds;
          
          // Only create a "No reply" branch if "Wait for reply" is checked
          if (shouldCreateWaitBranch) {
            
            // Extract time duration values from form
            let days = 0, hours = 0, minutes = 0, seconds = 0;
            
            // Look for time duration fields in the form values
            Object.keys(formValues).forEach(key => {
              if (key.includes('days') && key.includes('Wait for reply')) {
                days = parseInt(formValues[key]) || 0;
              } else if (key.includes('hours') && key.includes('Wait for reply')) {
                hours = parseInt(formValues[key]) || 0;
              } else if (key.includes('minutes') && key.includes('Wait for reply')) {
                minutes = parseInt(formValues[key]) || 0;
              } else if (key.includes('seconds') && key.includes('Wait for reply')) {
                seconds = parseInt(formValues[key]) || 0;
              }
            });
            
            // Also check in the action config for time values
            if (selectedAction && selectedAction.config) {
              selectedAction.config.forEach(config => {
                if (config.fields) {
                  config.fields.forEach(field => {
                    if (field.label === 'Wait for reply' && field.subfields) {
                      field.subfields.forEach(subfield => {
                        if (subfield.label === 'days' && subfield.selectedValue) {
                          days = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'hours' && subfield.selectedValue) {
                          hours = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'minutes' && subfield.selectedValue) {
                          minutes = parseInt(subfield.selectedValue) || 0;
                        } else if (subfield.label === 'seconds' && subfield.selectedValue) {
                          seconds = parseInt(subfield.selectedValue) || 0;
                        }
                      });
                    }
                  });
                }
              });
            }
            
            // Create trigger data for database
            const triggerData = {
              name: 'no reply',
              description: 'no reply',
              triggerType: 'non-root',
              config: [{
                fields: [
                  {
                    type: 'number',
                    label: 'days',
                    selectedValue: days
                  },
                  {
                    type: 'number',
                    label: 'hours',
                    selectedValue: hours
                  },
                  {
                    type: 'number',
                    label: 'minutes',
                    selectedValue: minutes
                  },
                  {
                    type: 'number',
                    label: 'seconds',
                    selectedValue: seconds
                  }
                ]
              }],
              category: 'Nil'
            };
            
            // Save trigger to database
            try {
              // Get token from memory (not localStorage)
              const token = await getAuthToken();
              
              if (token) {
                const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
                await axios.post(
                  `${serverDomain}/api/workflow/createtrigger`,
                  triggerData,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                      "Content-Type": "application/json",
                    },
                  }
                );
                
                // Trigger saved to database
              }
            } catch (error) {
              console.error('Error saving trigger to database:', error);
            }
            
            // Handle "No reply" branch
            let noReplyBranchId = null;
            if (editAction && editAction.next && Array.isArray(editAction.next)) {
              noReplyBranchId = editAction.next.find(id => id.startsWith('no-reply-'));
            }
            
            // Use existing no-reply branch or create new one
            if (!noReplyBranchId) {
              noReplyBranchId = `no-reply-${Date.now()}`;
            }
            
            // Check if branch already exists
            if (!currentBranches[noReplyBranchId]) {
              await dispatch(addBranch({ branchId: noReplyBranchId }));
            }
            
            // Update the no-reply branch with the new trigger node
            const noReplyTriggerNode = {
              id: `${noReplyBranchId}-trigger`,
              type: 'trigger',
              trigger: triggerData
            };
            
            await dispatch(updateNodeInBranch({ 
              branchId: noReplyBranchId, 
              nodeId: `${noReplyBranchId}-trigger`, 
              updates: noReplyTriggerNode 
            }));
            
            // If the node doesn't exist, add it
            const stateAfterNoReplyUpdate = window.store?.getState ? window.store.getState() : null;
            const noReplyBranchAfterUpdate = stateAfterNoReplyUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[noReplyBranchId];
            if (!noReplyBranchAfterUpdate || noReplyBranchAfterUpdate.length === 0) {
              await dispatch(addNodeToBranch({
                branchId: noReplyBranchId,
                node: noReplyTriggerNode
              }));
            }
            
            // Add "No reply" to the next array
            actionWithConfig.next.push(noReplyBranchId);
            console.log('Added No reply branch with time config:', noReplyBranchId);
          } else {
            console.log('Wait for reply is disabled, removing No reply branch if it exists');
            
            // Find and remove the no-reply branch if it exists
            if (editAction && editAction.next && Array.isArray(editAction.next)) {
              const noReplyBranchId = editAction.next.find(id => id.startsWith('no-reply-'));
              if (noReplyBranchId) {
                console.log(`Removing no-reply branch: ${noReplyBranchId}`);
                
                // Remove the branch from Redux state
                await dispatch(removeBranch({ branchId: noReplyBranchId }));
                
                // Remove the branch ID from the next array
                const nextIndex = actionWithConfig.next.indexOf(noReplyBranchId);
                if (nextIndex !== -1) {
                  actionWithConfig.next.splice(nextIndex, 1);
                  console.log(`Removed ${noReplyBranchId} from next array`);
                }
              }
            }
          }
        }
      } else {
        // No branching - render as normal action
        actionWithConfig.next = [];
      }
    }

    // Special logic for AI Reply actions — create one branch per enabled agent action
    if (actionWithConfig.name === 'AI Reply') {
      const AI_ACTION_DISPLAY_NAMES = {
        assignment: 'Assignment',
        catalog_send: 'Catalog Send',
        list_message: 'List Message',
        button_message: 'Button Message',
        api_integration: 'API Integration',
        close_conversation: 'Close Conversation',
      };

      // Resolve the selected agent from formValues (field key is '0-AI Agent')
      const selectedAgentId = formValues['0-AI Agent'] || '';
      const selectedAgent = aiAgents.find(a => a._id === selectedAgentId) || null;
      const agentEnabledActions = selectedAgent?.enabledActions || [];

      if (agentEnabledActions.length > 0) {
        // Reuse existing ai-action- branch IDs when editing
        let branchIds = [];
        if (editAction && editAction.next && Array.isArray(editAction.next)) {
          branchIds = editAction.next.filter(id => id.startsWith('ai-action-'));
        }

        // Grow branch list to match enabled actions count
        while (branchIds.length < agentEnabledActions.length) {
          branchIds.push(`ai-action-${Date.now()}-${branchIds.length}`);
        }

        // Remove excess branches if agent now has fewer actions
        if (branchIds.length > agentEnabledActions.length) {
          const excess = branchIds.slice(agentEnabledActions.length);
          for (const bid of excess) {
            await dispatch(removeBranch({ branchId: bid }));
          }
          branchIds = branchIds.slice(0, agentEnabledActions.length);
        }

        // Create / update a branch for each enabled action
        for (let i = 0; i < branchIds.length; i++) {
          const branchId = branchIds[i];
          const actionType = agentEnabledActions[i];
          const displayName = AI_ACTION_DISPLAY_NAMES[actionType] || actionType;

          if (!currentBranches[branchId]) {
            await dispatch(addBranch({ branchId }));
          }

          const triggerNode = {
            id: `${branchId}-trigger`,
            type: 'trigger',
            trigger: {
              name: displayName,
              description: `AI agent action: ${displayName}`,
              triggerType: 'non-root',
              config: [{ fields: [{ label: 'Action Type', type: 'text', selectedValue: actionType }] }],
              category: 'Nil',
            },
          };

          await dispatch(updateNodeInBranch({ branchId, nodeId: `${branchId}-trigger`, updates: triggerNode }));

          // If the branch was newly created (empty), add the trigger node
          const stateCheck = window.store?.getState ? window.store.getState() : null;
          const branchCheck = stateCheck?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
          if (!branchCheck || branchCheck.length === 0) {
            await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
          }
        }

        actionWithConfig.next = branchIds;
      } else {
        // Agent has no enabled actions — clear any stale ai-action- branches
        if (editAction && editAction.next && Array.isArray(editAction.next)) {
          for (const bid of editAction.next.filter(id => id.startsWith('ai-action-'))) {
            await dispatch(removeBranch({ branchId: bid }));
          }
        }
        actionWithConfig.next = [];
      }
    }

    // Special logic for "Branch: Business Hours" — always exactly two branches (yes/no),
    // each seeded with a static label trigger node (no reply to match against; the
    // executor evaluates the condition synchronously and jumps straight to one of them).
    if (actionWithConfig.name === 'Branch: Business Hours') {
      const configValue = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE;

      const resolveBranchId = (branchName) => {
        // Reuse the existing branch key when editing so downstream node edits in that
        // branch aren't orphaned; otherwise mint one (matching the option-* prefix the
        // canvas already renders generically as a compact "option node").
        return editAction?.branches?.[branchName] || `option-${branchName}-${Date.now()}`;
      };
      const yesBranchId = resolveBranchId('yes');
      const noBranchId = resolveBranchId('no');

      for (const [branchName, branchId, label] of [
        ['yes', yesBranchId, 'Business hours: Yes'],
        ['no', noBranchId, 'Business hours: No'],
      ]) {
        if (!currentBranches[branchId]) {
          await dispatch(addBranch({ branchId }));
        }
        const triggerNode = {
          id: `${branchId}-trigger`,
          type: 'trigger',
          trigger: {
            name: label,
            description: branchName === 'yes'
              ? 'Runs when the current time is inside the configured business hours.'
              : 'Runs when the current time is outside the configured business hours.',
            triggerType: 'non-root',
            config: [],
            category: 'Nil',
          },
        };
        await dispatch(updateNodeInBranch({ branchId, nodeId: `${branchId}-trigger`, updates: triggerNode }));
        const stateAfterUpdate = window.store?.getState ? window.store.getState() : null;
        const branchAfterUpdate = stateAfterUpdate?.workflow?.selectedWorkflow?.currentVersion?.branches?.[branchId];
        if (!branchAfterUpdate || branchAfterUpdate.length === 0) {
          await dispatch(addNodeToBranch({ branchId, node: triggerNode }));
        }
      }

      actionWithConfig.next = [yesBranchId, noBranchId];
      actionWithConfig.branches = { yes: yesBranchId, no: noBranchId };
      actionWithConfig.condition = {
        type: 'business_hours',
        timezone: configValue?.timezone || 'UTC',
        schedule: configValue?.schedule || DEFAULT_BUSINESS_HOURS_VALUE.schedule,
      };
    }

    // "Branch: Contact Fields" — same two label-only yes/no branches as Business Hours.
    if (actionWithConfig.name === CONTACT_FIELDS_BRANCH_ACTION_NAME) {
      const configValue = formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE;
      await applyContactFieldsBranches({ dispatch, editAction, actionWithConfig, configValue });
    }

    // Update or add the action node
    if (editActionBranch && editActionNodeId !== undefined && editActionNodeId !== null) {
      
      // Editing existing node in a branch
      await dispatch(updateNodeInBranch({ 
        branchId: editActionBranch, 
        nodeId: editActionNodeId, 
        updates: { action: actionWithConfig, next: actionWithConfig.next } 
      }));
      
      if (actionWithConfig.next) {
        await dispatch(setNodeNextBranches({ 
          branchId: editActionBranch, 
          nodeId: editActionNodeId, 
          next: actionWithConfig.next 
        }));
      }
    } else if (pendingInsert && pendingInsert.branchId) {
      
      // Inserting new node in a branch at a specific index
      const node = { 
        id: Date.now().toString(), 
        type: 'action', 
        action: actionWithConfig, 
        next: actionWithConfig.next 
      };
      
      await dispatch(addNodeToBranch({ 
        branchId: pendingInsert.branchId, 
        node, 
        nodeIndex: pendingInsert.nodeIndex 
      }));
      
      if (actionWithConfig.next) {
        // Find the nodeId of the node just added
        const state = window.store?.getState ? window.store.getState() : null;
        const branch = state?.workflow?.selectedWorkflow?.currentVersion?.branches?.[pendingInsert.branchId] || [];
        const addedNode = branch.find(n => n.action === actionWithConfig || n.next === actionWithConfig.next);
        if (addedNode) {
          await dispatch(setNodeNextBranches({ 
            branchId: pendingInsert.branchId, 
            nodeId: addedNode.id, 
            next: actionWithConfig.next 
          }));
        }
      }
    } else {
      setSaving(false);
      return;
    }

    // Get final state and save to backend
    const finalState = window.store?.getState ? window.store.getState() : null;
    const finalWorkflow = finalState?.workflow?.selectedWorkflow;
    
    
    // Check for carousel media in the final workflow before saving
    if (finalWorkflow && finalWorkflow.currentVersion && finalWorkflow.currentVersion.branches) {
      Object.keys(finalWorkflow.currentVersion.branches).forEach(branchId => {
        const branch = finalWorkflow.currentVersion.branches[branchId];
        if (Array.isArray(branch)) {
          branch.forEach(node => {
            if (node.action && node.action.config) {
              node.action.config.forEach(config => {
                if (config.fields) {
                  config.fields.forEach(field => {
                    if (field.type === 'template' && field.subfields) {
                      const carouselMediaField = field.subfields.find(subfield => subfield.label === 'Carousel Media');
                      if (carouselMediaField && carouselMediaField.selectedValue && Object.keys(carouselMediaField.selectedValue).length > 0) {
                        // Carousel media detected and will be saved
                      }
                    }
                  });
                }
              });
            }
          });
        }
      });
    }
    
    if (finalWorkflow && finalWorkflow._id) {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        console.error("No token found");
        return;
      }
      
      try {
        // Sanitize the workflow data - only send necessary fields
        const workflowPayload = {
          currentVersion: {
            name: finalWorkflow.currentVersion?.name || 'Untitled Workflow',
            description: finalWorkflow.currentVersion?.description || '',
            branches: finalWorkflow.currentVersion?.branches || { start: [] },
          },
          status: finalWorkflow.status || 'off',
        };
        
        const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
        await axios.put(
          `${serverDomain}/api/workflow/update/${finalWorkflow._id}`,
          workflowPayload,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setSaving(false);
        if (onClose) onClose();
      } catch (err) {
        console.error('Backend update failed:', err);
        
        // Show user-friendly error message
        if (err.response?.data?.message) {
          alert(`Failed to save workflow: ${err.response.data.message}`);
        } else if (err.response?.data?.errors) {
          const errorMessages = err.response.data.errors.map(e => `${e.field}: ${e.message}`).join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert(`Failed to save workflow: ${err.message || 'Unknown error'}`);
        }
        setSaving(false);
      }
    } else {
      setSaving(false);
    }
  };

  // Removed duplicate useEffect that was causing double API calls

  if (!open) return null;

  const actionDrawerSelectMenuProps = {
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    PaperProps: {
      sx: {
        mt: 0.5,
        maxHeight: 320,
        bgcolor: isDark ? "#2e3440" : "#fff",
        border: `1px solid ${isDark ? "#444" : "#e0e0e0"}`,
        "& .MuiMenuItem-root": { fontSize: 15 },
        "& .MuiMenuItem-root.Mui-selected": {
          bgcolor: isDark ? "rgba(25, 118, 210, 0.28)" : "action.selected",
        },
      },
    },
  };

  const actionDrawerSelectSx = {
    borderRadius: "6px",
    fontSize: 15,
    bgcolor: isDark ? "#23272b" : "#fff",
    color: isDark ? "#fff" : "#222",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#444" : "#ccc",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: isDark ? "#666" : "#999",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderWidth: 1,
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSelect-icon": {
      color: isDark ? "#fff" : "inherit",
    },
  };

  // Recursive field renderer for config fields and sections
  const renderFields = (fields, parentKey = "", cfgIdx = 0, path = []) => {
    // Count occurrences of each section label at this level
    const sectionCounts = {};
    fields.forEach(f => {
      if (f.type === 'section') {
        const label = f.label || f.name || '';
        sectionCounts[label] = (sectionCounts[label] || 0) + 1;
      }
    });
    // Track the current index for each label
    const sectionIndex = {};
    
    // For list message actions, calculate current counts to check limits
    let currentSectionCount = 0;
    let currentTotalOptions = 0;
    let canAddSection = true;
    let canAddOption = true;
    // For interactive reply buttons message actions, calculate current button count
    let currentButtonCount = 0;
    let canAddButton = true;
    
    if (selectedAction?.name === 'Send list message' && localConfig && localConfig[cfgIdx] && Array.isArray(localConfig[cfgIdx].fields)) {
      const counts = countSectionsAndOptions(localConfig[cfgIdx].fields);
      currentSectionCount = counts.sectionCount;
      currentTotalOptions = counts.totalOptions;
      canAddSection = currentSectionCount < 10;
      canAddOption = currentTotalOptions < 10;
    }
    if ((selectedAction?.name === 'Send interactive reply buttons message' || 
      selectedAction?.name === 'Send Interactive Reply Buttons Message') && 
     localConfig && localConfig[cfgIdx] && Array.isArray(localConfig[cfgIdx].fields)) {
   const counts = countButtons(localConfig[cfgIdx].fields);
   currentButtonCount = counts.buttonCount;
   canAddButton = currentButtonCount < WEBSITE_MAX_BUTTONS;
 }
    
    return fields.map((field, idx) => {
      const key = `${parentKey}${field.label || field.name || idx}`;
      const thisPath = [...path, idx];
      if (field.dependsOn) {
        const dependsOnKey = Object.keys(formValues).find((k) => k.includes(field.dependsOn));
        if (!formValues[dependsOnKey]) return null;
      }
      if (field.type === "section" && Array.isArray(field.subfields)) {
        // Determine if this is a top-level section (path.length === 0)
        const isTopLevel = path.length === 0;
        const label = field.label || field.name || '';
        let numberedLabel = label;
        if (sectionCounts[label] > 1) {
          sectionIndex[label] = (sectionIndex[label] || 0) + 1;
          numberedLabel = `${label} ${sectionIndex[label]}`;
        }
        // Show minus button for duplicated sections (index > 0)
        const showMinus = sectionCounts[label] > 1 && sectionIndex[label] > 1;
        
        // Determine if this is adding a Section or an Option
        const isSectionField = label.toLowerCase() === 'section';
        const isOptionField = label.toLowerCase() === 'option';
        const isButtonField = label.toLowerCase() === 'button';
        const shouldDisableAdd = (isSectionField && !canAddSection) || 
                                  (isOptionField && !canAddOption) || 
                                  (isButtonField && !canAddButton);
        
        return (
          <Box
            key={key}
            sx={{
              mb: 0.5,
              ml: isTopLevel ? 0.5 : 1.5,
              borderLeft: `1px solid ${isDark ? '#555' : '#bbb'}`,
              pl: isTopLevel ? 1.5 : 2,
              minHeight: 32,
              position: 'relative',
              background: 'transparent',
              display: 'block',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.2 }}>
              <Typography
                sx={{
                  fontWeight: isTopLevel ? 700 : 500,
                  color: isDark ? '#fff' : '#222',
                  fontSize: isTopLevel ? 16 : 15,
                  letterSpacing: 0.1,
                  whiteSpace: 'nowrap',
                  display: 'block',
                  mr: 25,
                }}
              >
                {numberedLabel}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDuplicateSection(cfgIdx, thisPath)}
                disabled={shouldDisableAdd}
                title={shouldDisableAdd ? 
                  (isSectionField ? 'Maximum 10 sections allowed' : 
                   isOptionField ? 'Maximum 10 total options allowed' : 
                   isButtonField ? `Maximum ${WEBSITE_MAX_BUTTONS} buttons allowed` :
                   `Cannot add more ${label}`) : 
                  `Add ${label}`}
                sx={{
                  p: '2px',
                  color: shouldDisableAdd ? (isDark ? '#666' : '#999') : (isDark ? '#fff' : '#222'),
                  background: 'transparent',
                  '&:hover': { background: shouldDisableAdd ? 'transparent' : (isDark ? '#333' : '#eee') },
                  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
                  ml: 0.5,
                }}>
                <span style={{ fontSize: 16, fontWeight: 700, display: 'inline-block', lineHeight: 1 }}>+</span>
              </IconButton>
              {showMinus && (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveSection(cfgIdx, thisPath)}
                  sx={{
                    p: '2px',
                    color: isDark ? '#fff' : '#222',
                    background: 'transparent',
                    '&:hover': { background: isDark ? '#333' : '#eee' },
                    ml: 0.5,
                  }}>
                  <span style={{ fontSize: 16, fontWeight: 700, display: 'inline-block', lineHeight: 1 }}>-</span>
                </IconButton>
              )}
            </Box>
            <Box sx={{ width: '100%' }}>{renderFields(field.subfields, `${key}-${idx}-`, cfgIdx, thisPath)}</Box>
          </Box>
        );
      }
      // Checkbox type: render a tick button, show subfields if checked
      if (field.type === "checkbox") {
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={!!formValues[key]}
                onChange={e => setFormValues(v => ({ ...v, [key]: e.target.checked }))}
                sx={{ p: 0.5, mr: 1 }}
              />
              <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14, cursor: 'pointer' }}>{field.label}</label>
            </Box>
            {field.subfields && Array.isArray(field.subfields) && formValues[key] ? (
              <Box sx={{ ml: 3, mt: 1 }}>{renderFields(field.subfields, `${key}-${idx}-`, cfgIdx, thisPath)}</Box>
            ) : null}
          </Box>
        );
      }
      // Custom: Actions dropdown for selecting existing workflow actions
      // This allows users to select an action from the current workflow
      // The selectedValue will store the branchId and nodeIndex of the selected action
      // Only show this field if the current node is just before an end node
      if (field.type === 'actions') {
        console.log('Is node before end node:', isNodeBeforeEndNode);
        console.log('Available workflow actions:', workflowActions.length);
        console.log('Form values for this field:', formValues[key]);
        
        // Only render the field if the node is before an end node
        if (!isNodeBeforeEndNode) {
          console.log('Actions field hidden - node is not before end node');
          
          // Get the appropriate message based on context
          let message, subMessage;
          if (getNodeContext === 'branching') {
            message = "⚠️ You can't go back from here as branching exists in the workflow.";
            subMessage = "The 'Go to action' field is only available when the workflow can continue linearly.";
          } else {
            message = "⚠️ You can't go back from here as there are subsequent actions in the workflow.";
            subMessage = "The 'Go to action' field is only available at the end of a workflow branch.";
          }
          
          return (
            <Box key={key} sx={{ mb: 2, ml: 2 }}>
              <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
              <Box sx={{ 
                mt: 1, 
                p: 2, 
                bgcolor: isDark ? '#2a2a2a' : '#f8f8f8', 
                borderRadius: 1,
                border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                borderLeft: `3px solid ${isDark ? '#666' : '#999'}`
              }}>
                <Typography variant="body2" sx={{ 
                  color: isDark ? '#bbb' : '#666',
                  fontSize: 13,
                  fontStyle: 'italic'
                }}>
                  {message}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: isDark ? '#888' : '#999',
                  fontSize: 11,
                  display: 'block',
                  mt: 0.5
                }}>
                  {subMessage}
                </Typography>
              </Box>
            </Box>
          );
        }
        
        const selectedActionId = formValues[key] || '';
        const selectedWorkflowAction = workflowActions.find(action => action.id === selectedActionId);
        
        console.log('Selected action ID:', selectedActionId);
        console.log('Selected workflow action:', selectedWorkflowAction);
        
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            
            {/* Selection Mode Indicator */}
            {actionSelectionMode && actionSelectionFieldKey === key && (
              <Box sx={{ 
                mt: 1, 
                p: 2, 
                bgcolor: isDark ? '#1a3a1a' : '#e8f5e8', 
                borderRadius: 1,
                border: `2px solid ${isDark ? '#4caf50' : '#4caf50'}`,
                textAlign: 'center'
              }}>
                <Typography variant="body2" sx={{ 
                  color: isDark ? '#4caf50' : '#2e7d32',
                  fontWeight: 600,
                  mb: 1
                }}>
                  🎯 Click on an action node in the workflow to select it
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setActionSelectionMode(false);
                    setActionSelectionFieldKey(null);
                  }}
                  sx={{ 
                    color: isDark ? '#4caf50' : '#2e7d32',
                    borderColor: isDark ? '#4caf50' : '#2e7d32',
                    '&:hover': {
                      borderColor: isDark ? '#66bb6a' : '#66bb6a',
                      bgcolor: isDark ? '#1a3a1a' : '#e8f5e8'
                    }
                  }}
                >
                  Cancel Selection
                </Button>
              </Box>
            )}
            
            {/* Action Selection Button */}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setActionSelectionMode(true);
                setActionSelectionFieldKey(key);
              }}
              disabled={actionSelectionMode && actionSelectionFieldKey !== key}
              sx={{
                mt: 1,
                mb: 1,
                borderColor: isDark ? '#444' : '#ccc',
                color: isDark ? '#fff' : '#222',
                '&:hover': {
                  borderColor: isDark ? '#666' : '#999',
                  bgcolor: isDark ? '#2a2a2a' : '#f5f5f5'
                },
                '&:disabled': {
                  color: isDark ? '#666' : '#999',
                  borderColor: isDark ? '#333' : '#e0e0e0'
                }
              }}
            >
              {selectedWorkflowAction ? `Selected: ${selectedWorkflowAction.name}` : 'Click to Select Action'}
            </Button>
            
            {/* Selected Action Display */}
            {selectedWorkflowAction && !actionSelectionMode && (
              <Box sx={{ mt: 1, p: 1, bgcolor: isDark ? '#333' : '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: isDark ? '#bbb' : '#666' }}>
                  Branch: {selectedWorkflowAction.branchId}, Index: {selectedWorkflowAction.nodeIndex}, ID: {selectedWorkflowAction.nodeId || selectedWorkflowAction.nodeIndex}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }

      // Custom: Team or TeamMember dropdown
      if (field.type === 'team') {
        // Get selected value from formValues or from field.selectedValues if editing
        let selectedValue = formValues[key] || '';
        if (!selectedValue && field.selectedValues && Array.isArray(field.selectedValues) && field.selectedValues.length > 0) {
          selectedValue = field.selectedValues[0]; // Single team selection
          // Also update formValues to reflect the existing selection
          if (!formValues[key]) {
            setFormValues(v => ({ ...v, [key]: selectedValue }));
          }
        }
        
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <select
              value={selectedValue}
              onChange={e => setFormValues(v => ({ ...v, [key]: e.target.value }))}
              style={{
                width: '90%',
                marginTop: 4,
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${isDark ? '#444' : '#ccc'}`,
                background: isDark ? '#23272b' : '#fff',
                color: isDark ? '#fff' : '#222',
                fontSize: 15,
                marginBottom: 8,
              }}
            >
              <option value='' disabled>Select a team</option>
              {teams && teams.map(team => (
                <option key={team.id || team._id || team.teamName} value={team.id || team._id || team.teamName}>{team.teamName}</option>
              ))}
            </select>
          </Box>
        );
      }
      if (field.type === 'agent') {
        const selectedValue = formValues[key] || '';
        if (!selectedValue && field.selectedValue) {
          setFormValues(v => ({ ...v, [key]: field.selectedValue }));
        }
        const selectedAgent = aiAgents.find(a => a._id === selectedValue) || null;
        return (
          <Box key={key} sx={{ mb: 2, ml: 2, mr: 1 }}>
            <Typography variant="caption" sx={{ color: isDark ? '#bbb' : '#666', fontWeight: 500, mb: 0.5, display: 'block' }}>
              {field.label}
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedValue}
              onChange={e => setFormValues(v => ({ ...v, [key]: e.target.value }))}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: isDark ? '#1e2328' : '#fafafa',
                },
              }}
              SelectProps={{
                displayEmpty: true,
                renderValue: (val) => {
                  const agent = aiAgents.find(a => a._id === val) || null;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SmartToyIcon sx={{ fontSize: 16, color: agent ? 'primary.main' : (isDark ? '#666' : '#aaa') }} />
                      <Typography variant="body2" sx={{ color: agent ? 'text.primary' : (isDark ? '#888' : '#aaa') }}>
                        {agent ? agent.name : 'Default agent'}
                      </Typography>
                      {agent?.isDefault && (
                        <Typography variant="caption" sx={{
                          ml: 0.5, px: 0.8, py: 0.1,
                          bgcolor: isDark ? '#1a3a2a' : '#e8f5e9',
                          color: isDark ? '#66bb6a' : '#2e7d32',
                          borderRadius: 1, fontSize: 10, fontWeight: 600,
                        }}>
                          default
                        </Typography>
                      )}
                    </Box>
                  );
                },
              }}
            >
              <MenuItem value="">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SmartToyIcon sx={{ fontSize: 16, color: isDark ? '#666' : '#aaa' }} />
                  <Typography variant="body2" color="text.secondary">Default agent</Typography>
                </Box>
              </MenuItem>
              {aiAgentsLoading && (
                <MenuItem disabled sx={{ opacity: 0.5, py: 0.5 }}>
                  <Typography variant="caption">Loading agents…</Typography>
                </MenuItem>
              )}
              {!aiAgentsLoading && aiAgents.length > 0 && (
                <MenuItem disabled sx={{ opacity: 0.5, py: 0.25 }}>
                  <Typography variant="caption">— select a specific agent —</Typography>
                </MenuItem>
              )}
              {aiAgents.map(agent => (
                <MenuItem key={agent._id} value={agent._id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <SmartToyIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{agent.name}</Typography>
                    {agent.isDefault && (
                      <Typography variant="caption" sx={{
                        px: 0.8, py: 0.1,
                        bgcolor: isDark ? '#1a3a2a' : '#e8f5e9',
                        color: isDark ? '#66bb6a' : '#2e7d32',
                        borderRadius: 1, fontSize: 10, fontWeight: 600,
                      }}>
                        default
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="caption" sx={{ color: isDark ? '#666' : '#999', mt: 0.5, display: 'block' }}>
              {selectedAgent
                ? `Using "${selectedAgent.name}" — overrides workspace default for this action`
                : 'Uses the workspace default agent'}
            </Typography>
          </Box>
        );
      }
      if (field.type === 'teammember') {
        // Get selected value from formValues or from field.selectedValues if editing
        let selectedValue = formValues[key] || '';
        if (!selectedValue && field.selectedValues && Array.isArray(field.selectedValues) && field.selectedValues.length > 0) {
          selectedValue = field.selectedValues[0]; // Single team member selection
          // Also update formValues to reflect the existing selection
          if (!formValues[key]) {
            setFormValues(v => ({ ...v, [key]: selectedValue }));
          }
        }
        
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <FormControl size="small" sx={{ mt: 0.5, mb: 1, width: '90%' }}>
              <Select
                value={selectedValue || ''}
                onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
                displayEmpty
                inputProps={{ 'aria-label': field.label }}
                MenuProps={actionDrawerSelectMenuProps}
                renderValue={(v) => {
                  if (!v) return 'Select a team member';
                  const member = (teamMembers || []).find(m => String(m.id || m._id || m.email) === String(v));
                  return member ? (member.name || member.email || String(v)) : String(v);
                }}
                sx={actionDrawerSelectSx}
              >
                <MenuItem value="" disabled sx={{ opacity: 0.8 }}>Select a team member</MenuItem>
                {teamMembers && teamMembers.map((member) => (
                  <MenuItem key={member.id || member._id || member.email} value={String(member.id || member._id || member.email)}>
                    {member.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );
      }
      if (field.type === 'multipleteammember') {
        // Get selected values from formValues or from field.selectedValues if editing
        let selectedIds = formValues[key];
        if (!Array.isArray(selectedIds)) {
          selectedIds = (selectedIds != null && selectedIds !== '') ? [selectedIds] : [];
        }
        if (selectedIds.length === 0 && field.selectedValues && Array.isArray(field.selectedValues)) {
          selectedIds = field.selectedValues;
          if (!formValues[key]) {
            setFormValues(v => ({ ...v, [key]: selectedIds }));
          }
        }

        // Map selectedIds to team member objects for Autocomplete value
        const selectedMembers = selectedIds
          .map(val => (teamMembers || []).find(m => m.id === val || m._id === val || String(m._id) === String(val) || m.email === val))
          .filter(Boolean);
          
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <Autocomplete
              multiple
              options={teamMembers || []}
              getOptionLabel={option => option.email || option.name || ''}
              value={selectedMembers}
              onChange={(_, newValue) => {
                setFormValues(v => ({
                  ...v,
                  [key]: newValue.map(m => m.id || m._id || m.name || m.email)
                }));
              }}
              renderInput={params => (
                <TextField {...params} variant="outlined" placeholder="Select members" />
              )}
              sx={{
                width: '90%',
                marginTop: 1,
                background: isDark ? '#23272b' : '#fff',
                borderRadius: 1,
              }}
            />
          </Box>
        );
      }
      if (field.type === 'multipleroles') {
        // Get selected values from formValues or from field.selectedValues if editing
        let selectedIds = formValues[key] || [];
        if (!selectedIds.length && field.selectedValues && Array.isArray(field.selectedValues)) {
          selectedIds = field.selectedValues;
          // Also update formValues to reflect the existing selection
          if (!formValues[key]) {
            setFormValues(v => ({ ...v, [key]: selectedIds }));
          }
        }
        
        // Map selectedIds to role objects for Autocomplete value
        const selectedRoles = selectedIds
          .map(val => rolesWithUsage.find(r => r.id === val || r._id === val || r.name === val))
          .filter(Boolean);
          
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <Autocomplete
              multiple
              options={rolesWithUsage || []}
              getOptionLabel={option => option.roleName || ''}
              value={selectedRoles}
              onChange={(_, newValue) => {
                setFormValues(v => ({
                  ...v,
                  [key]: newValue.map(r => r.id || r._id || r.name)
                }));
              }}
              renderInput={params => (
                <TextField {...params} variant="outlined" placeholder="Select roles" />
              )}
              sx={{
                width: '90%',
                marginTop: 1,
                background: isDark ? '#23272b' : '#fff',
                borderRadius: 1,
              }}
            />
          </Box>
        );
      }



      // Handle catalog field type
      if (field.type === 'catalog') {
        const currentCatalogValue = formValues[key];
        const selectedCatalogObj = typeof currentCatalogValue === 'object' ? currentCatalogValue : null;

        // Handle catalog selection with product selection
        const handleSelectCatalog = (catalog) => {
          console.log('catalog object:', catalog);
          console.log('catalog.id:', catalog?.id || catalog?._id);
          console.log('catalog.products:', catalog?.products);
          console.log('catalog.products.length:', catalog?.products?.length);
          
          setFormValues(v => {
            const updated = {
              ...v,
              [key]: catalog
            };
            console.log('Updated formValues with catalog:', updated);
            return updated;
          });
        };


        return (
          <Box key={key} sx={{ mb: 2, ml: 2, mt: 2, mr: 2 }}>
            <Typography sx={{ fontWeight: 600, mb: 1 }}>Select Catalog & Products</Typography>
            {catalogsStatus === 'loading' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Loading catalogs...
                </Typography>
              </Box>
            )}
            {catalogsStatus === 'failed' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Failed to load catalogs. Please check your WhatsApp Business account connection.
              </Alert>
            )}
            <CatalogSend 
              onCatalogSelect={handleSelectCatalog}
              selectedCatalog={selectedCatalogObj}
            />
          </Box>
        );
      }

      // Handle template field type
if (field.type === 'template') {
  const currentTemplateValue = formValues[key];
  const currentTemplateName = typeof currentTemplateValue === 'object' ? currentTemplateValue?.name : currentTemplateValue;
  const selectedTemplateObj = (templates || []).find(t => t.name === currentTemplateName) || (typeof currentTemplateValue === 'object' ? currentTemplateValue : null);

  // Helper: extract all placeholder variables from a template
  const extractTemplateVariables = (template) => {
    if (!template || !template.components) return { header: [], body: [], url: [] };
    const vars = { header: [], body: [], url: [] };
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    const header = template.components.find(c => c.type === 'HEADER');
    const body = template.components.find(c => c.type === 'BODY');
    const buttons = template.components.find(c => (c.type || '').toUpperCase() === 'BUTTONS');
    if (header?.text) {
      const seen = new Set();
      let m;
      while ((m = re.exec(header.text)) !== null) {
        const idx = m[1];
        if (!seen.has(idx)) { seen.add(idx); vars.header.push(parseInt(idx, 10)); }
      }
    }
    if (body?.text) {
      const seen = new Set();
      let m;
      while ((m = re.exec(body.text)) !== null) {
        const idx = m[1];
        if (!seen.has(idx)) { seen.add(idx); vars.body.push(parseInt(idx, 10)); }
      }
    }
    if (buttons?.buttons?.length) {
      const seen = new Set();
      for (const b of buttons.buttons) {
        if ((b.type || '').toUpperCase() === 'URL' && typeof b.url === 'string') {
          let m; re.lastIndex = 0;
          while ((m = re.exec(b.url)) !== null) {
            const idx = m[1];
            if (!seen.has(idx)) { seen.add(idx); vars.url.push(parseInt(idx, 10)); }
          }
        }
      }
    }
    vars.header.sort((a,b)=>a-b); vars.body.sort((a,b)=>a-b); vars.url.sort((a,b)=>a-b);
    return vars;
  };

  const handleSelectTemplate = (template) => {
    setFormValues(v => ({
      ...v,
      [key]: {
        name: template.name,
        language: template.language || template.languageCode || 'en',
        category: template.category,
        variables: {},
        buttonVariables: {},
        media: null,
        carouselMedia: {}
      }
    }));
    if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: undefined }));
  };



  const templateFieldError = fieldErrors[key];
  // Only show template list if no template is selected
  if (!selectedTemplateObj) {
    return (
      <Box
        key={key}
        sx={{
          mb: 2,
          ml: 2,
          mt: 2,
          mr: 2,
          p: 1.5,
          borderRadius: 1,
          border: templateFieldError ? `2px solid ${theme.palette.error.main}` : 'none',
          backgroundColor: templateFieldError ? (theme.palette.error.main + '08') : 'transparent',
        }}
      >
        {templateFieldError && (
          <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mb: 1 }}>
            {templateFieldError}
          </Typography>
        )}
        <TextField
          fullWidth
          size="small"
          placeholder="Search templates..."
          value={templateSearchTerm}
          onChange={(e) => setTemplateSearchTerm(e.target.value)}
          sx={{ mb: 2, mt: 0 }}
        />
        <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
          {templatesStatus === 'loading' ? (
            <CircularProgress size={24} />
          ) : templatesStatus === 'failed' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                px: 2,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: theme.palette.error.main,
                borderRadius: 2,
                backgroundColor: theme.palette.error.light + '10',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                ❌ Failed to Load Templates
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.error.main,
                  opacity: 0.8,
                  maxWidth: 300,
                }}
              >
                There was an error loading templates. Please try refreshing the page.
              </Typography>
            </Box>
          ) : filteredTemplates && filteredTemplates.length > 0 ? (
            filteredTemplates.map((tpl, i) => (
              <Box
                key={tpl._id || tpl.id || tpl.name || i}
                onClick={() => handleSelectTemplate(tpl)}
                sx={{
                  mb: 1.5,
                  p: 0.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: (currentTemplateName === tpl.name) ? theme.palette.primary.main : (isDark ? '#444' : '#e0e0e0'),
                  cursor: 'pointer',
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                <TemplateSendPreview singleTemplate={tpl} />
              </Box>
            ))
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4,
                px: 2,
                textAlign: 'center',
                border: '2px dashed',
                borderColor: theme.palette.error.main,
                borderRadius: 2,
                backgroundColor: theme.palette.error.light + '10',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.error.main,
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                ⚠️ No Templates Available
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.error.main,
                  opacity: 0.8,
                  maxWidth: 300,
                }}
              >
                {templateSearchTerm.trim() 
                  ? `No templates found matching "${templateSearchTerm}"`
                  : 'No templates have been created yet. Please create some templates first.'
                }
              </Typography>
              {!templateSearchTerm.trim() && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.error.main,
                    opacity: 0.6,
                    mt: 1,
                    fontStyle: 'italic',
                  }}
                >
                  Templates can be created in the Templates section
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  }

  // If a template is selected, show only its preview and config fields
  const vars = extractTemplateVariables(selectedTemplateObj);

  // "Send Template message" action, body variables only, only when this action lives inside
  // one of the "Send Template" trigger's own quick-reply branches — offers Fixed / From
  // Template (the trigger's own named template variables, e.g. invoice_no) / From Contact as
  // the variable's value source, in addition to today's plain fixed-text box. Every other
  // trigger/branch renders the exact same plain TextField as before (see the `!isSendTemplateBranch`
  // branch below) — this is purely additive UI, no new stored data shape: selecting "From
  // Template"/"From Contact" just writes a `{{tokenName}}` / `{{contact.field}}` string into the
  // same `variables['body-N']` value the backend's existing substituteWorkflowVariables()
  // (workflow.executor.js) already resolves from enrollment.workflowContext / the contact doc —
  // no backend changes needed for this.
  const rootTriggerForAction = selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger;
  const currentActionBranchIdForVars = editActionBranch || pendingInsert?.branchId;
  const isSendTemplateBranch = Boolean(
    rootTriggerForAction?.name === 'Send Template' &&
    Array.isArray(rootTriggerForAction?.next) &&
    currentActionBranchIdForVars &&
    rootTriggerForAction.next.includes(currentActionBranchIdForVars)
  );
  const triggerVariableMapping = isSendTemplateBranch && Array.isArray(rootTriggerForAction?.variableMapping)
    ? rootTriggerForAction.variableMapping
    : [];

  // Button URL variable extraction
  const buttonsComp = selectedTemplateObj?.components?.find(c => (c.type || '').toUpperCase() === 'BUTTONS');
  const buttonVars = (() => {
    if (!buttonsComp?.buttons?.length) return [];
    const results = [];
    const re = /\{\{\s*(\d+)\s*\}\}/g;
    buttonsComp.buttons.forEach((btn, bIdx) => {
      const type = (btn.type || '').toUpperCase();
      if (type === 'URL' && typeof btn.url === 'string') {
        re.lastIndex = 0; const seen = new Set(); let m;
        while ((m = re.exec(btn.url)) !== null) {
          const idx = parseInt(m[1], 10);
          if (!seen.has(idx)) { seen.add(idx); results.push({ buttonIndex: bIdx, varIndex: idx }); }
        }
      }
    });
    return results.sort((a,b)=> a.buttonIndex - b.buttonIndex || a.varIndex - b.varIndex);
  })();

  // Check if template has media header
  const headerComp = selectedTemplateObj?.components?.find(c => c.type === 'HEADER');
  const headerFormat = headerComp?.format?.toUpperCase();
  const isMediaHeader = headerFormat && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat);
  const hasUploadedMedia = templateMediaUploads[key];

  const setVariableValue = (scope, index, value) => {
    setFormValues(v => {
      const prev = (typeof v[key] === 'object' && v[key]) || { name: selectedTemplateObj?.name, language: selectedTemplateObj?.language, category: selectedTemplateObj?.category, variables: {} };
      const variables = { ...(prev.variables || {}) };
      variables[`${scope}-${index}`] = value;
      return { ...v, [key]: { ...prev, variables } };
    });
  };

  const setButtonVariableValue = (buttonIndex, varIndex, value) => {
    setFormValues(v => {
      const prev = (typeof v[key] === 'object' && v[key]) || { name: selectedTemplateObj?.name, language: selectedTemplateObj?.language, category: selectedTemplateObj?.category, variables: {}, buttonVariables: {} };
      const buttonVariables = { ...(prev.buttonVariables || {}) };
      buttonVariables[`button-${buttonIndex}-url-${varIndex}`] = value;
      return { ...v, [key]: { ...prev, buttonVariables } };
    });
  };

  return (
    <Box key={key} sx={{ mb: 2, ml: 2, mt: 2, mr: 2 }}>
       <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton
            size="small"
            onClick={() => setFormValues(v => ({ ...v, [key]: '' }))}
            sx={{ mr: 1 }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 600 }}>Back to select different Template</Typography>
        </Box>
      <TemplateSendPreview singleTemplate={selectedTemplateObj} />
      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Template configuration</Typography>
        {isMediaHeader && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: isDark ? '#444' : '#e0e0e0', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Header Media ({headerFormat})
            </Typography>
            {hasUploadedMedia ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="body2" color="success.main">
                  Media uploaded successfully
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setTemplateMediaUploads(prev => {
                      const newState = { ...prev };
                      delete newState[key];
                      return newState;
                    });
                    if (templateFileInputRefs.current[key]) {
                      templateFileInputRefs.current[key].value = '';
                    }
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => templateFileInputRefs.current[key]?.click()}
                  disabled={currentUploadingTemplate === key}
                  startIcon={<CloudUploadIcon />}
                >
                  {currentUploadingTemplate === key ? 'Uploading...' : 'Upload Media'}
                </Button>
                <Typography variant="caption" color="textSecondary">
                  {headerFormat === 'IMAGE' ? 'PNG, JPG' : headerFormat === 'VIDEO' ? 'MP4' : 'PDF'}
                </Typography>
              </Box>
            )}
                {typeof formValues[key] === 'object' && formValues[key]?.media && (
      <Box sx={{ mt: 2, background: 'rgba(86, 86, 86, 0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Saved Media:</Typography>
        {/* If you have a URL, show image/video preview */}
        {formValues[key].media.url && formValues[key].media.fileType?.startsWith('image') && (
          <img
            src={formValues[key].media.url}
            alt="template media"
            style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
          />
        )}
        {formValues[key].media.url && formValues[key].media.fileType?.startsWith('video') && (
          <video
            src={formValues[key].media.url}
            controls
            style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8 }}
          />
        )}
        {/* If only mediaId, show it as text */}
        {!formValues[key].media.url && (
          <Typography variant="caption" color="textSecondary">
            Media ID: {formValues[key].media.mediaId}
          </Typography>
        )}
        {formValues[key].media === null && (
      <Typography variant="caption" color="textSecondary">
        No media uploaded.
      </Typography>
    )}
      </Box>
    )}
          <input
              ref={el => templateFileInputRefs.current[key] = el}
              type="file"
              hidden
              accept={
                headerFormat === 'IMAGE' ? 'image/png,image/jpeg,image/jpg' :
                headerFormat === 'VIDEO' ? 'video/mp4' :
                headerFormat === 'DOCUMENT' ? '.pdf' : ''
              }
              onChange={(e) => handleTemplateFileSelect(key, selectedTemplateObj, e)}
            />
          </Box>
        )}
        {(vars.header.length + vars.body.length + vars.url.length === 0) && !isMediaHeader && (
          <Typography variant="body2" color={isDark ? '#bbb' : '#555'}>No variables required for this template.</Typography>
        )}
        {vars.header.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Header variables</Typography>
            {vars.header.map(idx => (
              <TextField
                key={`header-${idx}`}
                size="small"
                fullWidth
                placeholder={`Header {{${idx}}}`}
                value={(typeof formValues[key] === 'object' && formValues[key]?.variables?.[`header-${idx}`]) || ''}
                onChange={(e) => setVariableValue('header', idx, e.target.value)}
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        )}
        {vars.body.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Body variables</Typography>
            {vars.body.map(idx => {
              const bodyValue = (typeof formValues[key] === 'object' && formValues[key]?.variables?.[`body-${idx}`]) || '';
              if (!isSendTemplateBranch) {
                return (
                  <TextField
                    key={`body-${idx}`}
                    size="small"
                    fullWidth
                    placeholder={`Body {{${idx}}}`}
                    value={bodyValue}
                    onChange={(e) => setVariableValue('body', idx, e.target.value)}
                    sx={{ mb: 1 }}
                  />
                );
              }
              const parsedSource = parseTemplateVariableSource(bodyValue, triggerVariableMapping);
              return (
                <Box key={`body-${idx}`} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <Select
                      value={parsedSource.source}
                      onChange={(e) => {
                        const nextSource = e.target.value;
                        if (nextSource === 'fixed') setVariableValue('body', idx, '');
                        else if (nextSource === 'template') setVariableValue('body', idx, triggerVariableMapping[0] ? `{{${triggerVariableMapping[0].name}}}` : '');
                        else if (nextSource === 'contact') setVariableValue('body', idx, `{{contact.${TEMPLATE_VAR_CONTACT_FIELDS[0].value}}}`);
                      }}
                    >
                      <MenuItem value="fixed">Fixed</MenuItem>
                      <MenuItem value="template">From Template</MenuItem>
                      <MenuItem value="contact">From Contact</MenuItem>
                    </Select>
                  </FormControl>
                  {parsedSource.source === 'fixed' && (
                    <TextField
                      size="small"
                      fullWidth
                      placeholder={`Body {{${idx}}}`}
                      value={bodyValue}
                      onChange={(e) => setVariableValue('body', idx, e.target.value)}
                    />
                  )}
                  {parsedSource.source === 'template' && (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={parsedSource.key}
                        onChange={(e) => setVariableValue('body', idx, `{{${e.target.value}}}`)}
                        displayEmpty
                      >
                        <MenuItem value="">Select template variable…</MenuItem>
                        {triggerVariableMapping.map((v) => (
                          <MenuItem key={v.name} value={v.name}>{v.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {parsedSource.source === 'contact' && (
                    <FormControl size="small" fullWidth>
                      <Select
                        value={parsedSource.key}
                        onChange={(e) => setVariableValue('body', idx, `{{contact.${e.target.value}}}`)}
                        displayEmpty
                      >
                        <MenuItem value="">Select contact field…</MenuItem>
                        {TEMPLATE_VAR_CONTACT_FIELDS.map((f) => (
                          <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
        {vars.url.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>URL variables</Typography>
            {vars.url.map(idx => (
              <TextField
                key={`url-${idx}`}
                size="small"
                fullWidth
                placeholder={`URL {{${idx}}}`}
                value={(typeof formValues[key] === 'object' && formValues[key]?.variables?.[`url-${idx}`]) || ''}
                onChange={(e) => setVariableValue('url', idx, e.target.value)}
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        )}
        {buttonVars && buttonVars.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Button URL variables</Typography>
            {buttonVars.map(({ buttonIndex, varIndex }) => (
              <TextField
                key={`button-${buttonIndex}-url-${varIndex}`}
                size="small"
                fullWidth
                placeholder={`Button ${buttonIndex + 1} URL {{${varIndex}}}`}
                value={(typeof formValues[key] === 'object' && formValues[key]?.buttonVariables?.[`button-${buttonIndex}-url-${varIndex}`]) || ''}
                onChange={(e) => setButtonVariableValue(buttonIndex, varIndex, e.target.value)}
                sx={{ mb: 1 }}
              />
            ))}
          </Box>
        )}
        {/* Carousel Card Media Upload Section */}
        {selectedTemplateObj?.components?.find(c => c.type?.toLowerCase() === 'carousel') && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: isDark ? '#444' : '#e0e0e0', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Carousel Card Media Upload
            </Typography>
            {selectedTemplateObj.components.find(c => c.type?.toLowerCase() === 'carousel')?.cards?.map((card, cardIndex) => {
              const cardHeader = card.components?.find(c => c.type === 'header' || c.type === 'HEADER');
              if (!cardHeader) return null;
              const format = (cardHeader.format || 'image').toUpperCase();
              const hasUpload = carouselMediaUploads[`${key}-${cardIndex}`];
              const isUploading = currentUploadingCarouselCard === `${key}-${cardIndex}`;
              return (
                <Box key={`carousel-card-${cardIndex}`} sx={{ mb: 2, p: 1, border: '1px solid', borderColor: isDark ? '#555' : '#ddd', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Card {cardIndex + 1} ({format})
                  </Typography>
                  {hasUpload ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" color="success.main">
                        Media uploaded successfully
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setCarouselMediaUploads(prev => {
                            const newState = { ...prev };
                            delete newState[`${key}-${cardIndex}`];
                            return newState;
                          });
                          if (carouselFileInputRefs.current[`${key}-${cardIndex}`]) {
                            carouselFileInputRefs.current[`${key}-${cardIndex}`].value = '';
                          }
                          setFormValues(v => {
                            const currentValue = v[key];
                            const updatedValue = typeof currentValue === 'object' ? { ...currentValue } : {};
                            if (updatedValue.carouselMedia) {
                              delete updatedValue.carouselMedia[cardIndex];
                            }
                            return { ...v, [key]: updatedValue };
                          });
                        }}
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => carouselFileInputRefs.current[`${key}-${cardIndex}`]?.click()}
                        disabled={isUploading}
                        startIcon={<CloudUploadIcon />}
                      >
                        {isUploading ? 'Uploading...' : `Upload Media for Card ${cardIndex + 1}`}
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        {format === 'IMAGE' ? 'PNG, JPG' : format === 'VIDEO' ? 'MP4' : format === 'DOCUMENT' ? 'PDF' : 'Media'}
                      </Typography>
                    </Box>
                  )}
                  <input
                    ref={el => carouselFileInputRefs.current[`${key}-${cardIndex}`] = el}
                    type="file"
                    hidden
                    accept={
                      format === 'IMAGE' ? 'image/png,image/jpeg,image/jpg' :
                      format === 'VIDEO' ? 'video/mp4' :
                      format === 'DOCUMENT' ? '.pdf' : ''
                    }
                    onChange={(e) => handleCarouselCardFileSelect(key, cardIndex, card, e)}
                  />
                  
                </Box>
                
              );
            })}

            {formValues[key]?.carouselMedia && (
      <Box sx={{ mt: 2, background: 'rgba(86, 86, 86, 0.07)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ mb: 1 }}>Saved Carousel Media:</Typography>
        {(Array.isArray(formValues[key].carouselMedia)
          ? formValues[key].carouselMedia
          : Object.values(formValues[key].carouselMedia)
        ).map((mediaObj, idx) => (
      <Box key={idx} sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ mb: 0.5 }}>Card {idx + 1}:</Typography>
        {mediaObj.url && mediaObj.fileType?.startsWith('image') && (
          <img
            src={mediaObj.url}
            alt={`carousel-media-${idx}`}
            style={{ maxWidth: 80, maxHeight: 80, borderRadius: 6 }}
          />
        )}
        {mediaObj.url && mediaObj.fileType?.startsWith('video') && (
          <video
            src={mediaObj.url}
            controls
            style={{ maxWidth: 80, maxHeight: 80, borderRadius: 6 }}
          />
        )}
        {!mediaObj.url && mediaObj.mediaId && (
          <Typography variant="caption" color="textSecondary">
            Media ID: {mediaObj.mediaId}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
)}
      
          </Box>
        )}
      </Box>
    </Box>
  );
}

      // Custom: Select dropdown with subfields
      if (field.type === 'select' && Array.isArray(field.options)) {
        const initialSelectValue = formValues[key] ?? field.selectedValue ?? '';
        if (initialSelectValue !== '' && formValues[key] === undefined && field.selectedValue !== undefined) {
          setFormValues((v) => ({ ...v, [key]: initialSelectValue }));
        }
        // Find the selected option object (by label or value)
        // Handle both string options and object options
        const selectedOption = field.options.find(opt => {
          if (typeof opt === 'string') {
            return opt === initialSelectValue;
          }
          return (opt.label || opt.value) === initialSelectValue;
        });
        const selectValue = initialSelectValue != null && initialSelectValue !== '' ? initialSelectValue : '';
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <FormControl size="small" sx={{ mt: 0.5, mb: 1, width: '90%' }}>
              <Select
                value={selectValue}
                onChange={(e) => setFormValues((v) => ({ ...v, [key]: e.target.value }))}
                displayEmpty
                inputProps={{ 'aria-label': field.label }}
                MenuProps={actionDrawerSelectMenuProps}
                renderValue={(v) => (v ? String(v) : 'Select an option')}
                sx={actionDrawerSelectSx}
              >
                <MenuItem value="" disabled sx={{ opacity: 0.8 }}>Select an option</MenuItem>
                {field.options.map((opt, i) => {
                  const optionValue = typeof opt === 'string' ? opt : (opt.label || opt.value);
                  const optionKey = typeof opt === 'string' ? opt : (opt.value || opt.label || i);
                  return (
                    <MenuItem key={optionKey} value={optionValue}>
                      {optionValue}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {/* Render subfields for the selected option, if any - use ${key}- to match validation/save keys */}
            {selectedOption && Array.isArray(selectedOption.subfields) && selectedOption.subfields.length > 0 && (
              <Box sx={{ ml: 3, mt: 1 }}>
                {renderFields(selectedOption.subfields, `${key}-`, cfgIdx, thisPath)}
              </Box>
            )}
          </Box>
        );
      }
      
      // Handle file type for workflow actions
      if (field.type === "file") {
        // Multi-image upload mode for Image fields
        if (field.label === "Image") {
          const images = Array.isArray(formValues[key]) ? formValues[key] : [];
          const uploadImage = () => {
            const currentArr = Array.isArray(formValues[key]) ? formValues[key] : [];
            const insertIndex = currentArr.length;
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = field.accept || 'image/*';
            input.onchange = async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              try {
                setFormValues(v => {
                  const arr = Array.isArray(v[key]) ? [...v[key]] : [];
                  return { ...v, [key]: [...arr, { status: 'uploading' }] };
                });
                const token = await getAuthToken();
                if (!token) { alert('No authentication token found'); return; }
                const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
                const formData = new FormData();
                formData.append('file', file);
                const response = await fetch(`${serverDomain}/api/templates/sendupload`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                  body: formData
                });
                if (!response.ok) {
                  const errData = await response.json().catch(() => ({}));
                  throw new Error(errData.error || errData.message || 'Upload failed');
                }
                const result = await response.json();
                const fileUrl = result.data?.url || result.data;
                if (!fileUrl) throw new Error('No URL returned from upload');
                setFormValues(v => {
                  const arr = Array.isArray(v[key]) ? [...v[key]] : [];
                  const newArr = [...arr];
                  newArr[insertIndex] = { status: 'uploaded', url: fileUrl, originalName: file.name, size: file.size, type: file.type };
                  return { ...v, [key]: newArr };
                });
              } catch (error) {
                console.error('Error uploading image:', error);
                alert('Failed to upload image. Please try again.');
                setFormValues(v => {
                  const arr = Array.isArray(v[key]) ? [...v[key]] : [];
                  return { ...v, [key]: arr.filter((_, i) => i !== insertIndex) };
                });
              }
            };
            input.click();
          };
          return (
            <Box key={key} sx={{ mb: 2, ml: 2 }}>
              <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
              {images.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1, mb: 1 }}>
                  {images.map((img, imgIdx) => (
                    <Box
                      key={imgIdx}
                      sx={{
                        position: 'relative',
                        width: 80,
                        height: 80,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: isDark ? '#23272b' : '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {img.status === 'uploading' ? (
                        <CircularProgress size={24} />
                      ) : img.url ? (
                        <>
                          <img
                            src={img.url}
                            alt={img.originalName || `Image ${imgIdx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                            onClick={() => setPreviewImageUrl(img.url)}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: 'absolute', top: 0, right: 0,
                              bgcolor: 'rgba(0,0,0,0.55)', color: '#fff', p: 0.25,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setFormValues(v => {
                                const arr = Array.isArray(v[key]) ? [...v[key]] : [];
                                arr.splice(imgIdx, 1);
                                return { ...v, [key]: arr };
                              });
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button variant="outlined" size="small" onClick={uploadImage} startIcon={<CloudUploadIcon />}>
                  {images.length === 0 ? 'Upload Image' : 'Add Another Image'}
                </Button>
                <Typography variant="caption" color="textSecondary">PNG, JPG (Max 5MB)</Typography>
              </Box>
              {images.filter(i => i.status === 'uploaded').length > 0 && (
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                  {images.filter(i => i.status === 'uploaded').length} image{images.filter(i => i.status === 'uploaded').length > 1 ? 's' : ''} added
                </Typography>
              )}
            </Box>
          );
        }

        // Single file upload for Video, Audio, Document, Header image, etc.
        const isImageField = field.accept === "image/*" || field.label === "Header image";
        const currentImageUrl = isImageField
          ? (typeof formValues[key] === 'string' ? formValues[key] : formValues[key]?.url)
          : null;
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = field.accept || 'image/*';
                  input.onchange = async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const token = await getAuthToken();
                        if (!token) { alert('No authentication token found'); return; }
                        setFormValues(v => ({ ...v, [key]: { status: 'uploading', file: file } }));
                        const serverDomain = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';
                        const response = await fetch(`${serverDomain}/api/templates/sendupload`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData
                        });
                        if (!response.ok) {
                          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                          throw new Error(errorData.error || errorData.message || 'Upload failed');
                        }
                        const result = await response.json();
                        const fileUrl = result.data?.url || result.data;
                        if (!fileUrl) throw new Error('No URL returned from upload');
                        setFormValues(v => ({ ...v, [key]: { status: 'uploaded', url: fileUrl, originalName: file.name, size: file.size, type: file.type } }));
                      } catch (error) {
                        console.error('Error uploading file:', error);
                        alert('Failed to upload file. Please try again.');
                        setFormValues(v => ({ ...v, [key]: '' }));
                      }
                    }
                  };
                  input.click();
                }}
                startIcon={<CloudUploadIcon />}
              >
                {field.label === "Video" ? "Upload Video" :
                 field.label === "Document" ? "Upload Document" :
                 field.label === "Audio" ? "Upload Audio" :
                 isImageField ? "Upload Image" :
                 "Upload File"}
              </Button>
              <Typography variant="caption" color="textSecondary">
                {field.accept === "video/*" ? "MP4, 3GPP (Max 16MB)" :
                 field.accept === "audio/*" ? "AAC, MP4, AMR, MPEG, OGG (Max 16MB)" :
                 field.accept && field.accept.includes(".pdf") ? "PDF, DOC, DOCX, PPT, PPTX, XLS, XLSX, TXT (Max 100MB)" :
                 "PNG, JPG (Max 5MB)"}
              </Typography>
            </Box>
            {formValues[key] && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: isDark ? '#444' : '#e0e0e0', borderRadius: 1, background: isDark ? '#23272b' : '#f5f5f5' }}>
                {formValues[key].status === 'uploading' ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="info.main">Uploading...</Typography>
                  </Box>
                ) : isImageField && currentImageUrl ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        border: '1px solid',
                        borderColor: isDark ? '#444' : '#e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                        bgcolor: isDark ? '#1a1d20' : '#f5f5f5',
                        flexShrink: 0,
                      }}
                    >
                      <img
                        src={currentImageUrl}
                        alt="Header"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                        onClick={() => setPreviewImageUrl(currentImageUrl)}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" color="success.main">Image uploaded</Typography>
                      <IconButton size="small" onClick={() => setFormValues(v => ({ ...v, [key]: '' }))}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ) : formValues[key].status === 'uploaded' ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                      <Typography variant="body2" color="success.main">
                        {field.label === "Video" ? "Video uploaded" : field.label === "Document" ? "Document uploaded" : field.label === "Audio" ? "Audio uploaded" : "File uploaded"}
                      </Typography>
                      <IconButton size="small" onClick={() => setFormValues(v => ({ ...v, [key]: '' }))}>
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="textSecondary">File: {formValues[key].originalName}</Typography>
                    <Typography variant="caption" color="textSecondary" display="block">Size: {(formValues[key].size / 1024 / 1024).toFixed(2)} MB</Typography>
                  </>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    <Typography variant="body2" color="success.main">
                      {field.label === "Video" ? "Video selected" : field.label === "Document" ? "Document selected" : field.label === "Audio" ? "Audio selected" : "File selected"}
                    </Typography>
                    <IconButton size="small" onClick={() => setFormValues(v => ({ ...v, [key]: '' }))}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      }
      
      // Render input based on type
      let inputType = "text";
      if (field.type === "number") inputType = "number";
      if (field.type === "text large") {
        const lowerLabel = (field.label || field.name || '').toLowerCase();
        let maxLength = undefined;
        if (selectedAction?.name === 'Send list message') {
          if (lowerLabel === 'message body') maxLength = 4096;
          if (lowerLabel === 'description' && key.toLowerCase().includes('option')) maxLength = 72;
        }
        const hasError = !!fieldErrors[key];
        return (
          <Box key={key} sx={{ mb: 2, ml: 2 }}>
            <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
            <textarea
              style={{
                width: '90%',
                marginTop: 4,
                padding: '8px 10px',
                borderRadius: 6,
                border: `1px solid ${hasError ? (theme.palette.error.main) : (isDark ? '#444' : '#ccc')}`,
                background: isDark ? '#23272b' : '#fff',
                color: isDark ? '#fff' : '#222',
                fontSize: 15,
                marginBottom: 8,
                minHeight: 150,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
              maxLength={maxLength}
              placeholder={maxLength ? `Max ${maxLength} characters` : undefined}
              value={formValues[key] || ''}
              onChange={e => {
                setFormValues(v => ({ ...v, [key]: e.target.value }));
                if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: undefined }));
              }}
            />
            {hasError && (
              <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                {fieldErrors[key]}
              </Typography>
            )}
          </Box>
        );
      }
      const hasError = !!fieldErrors[key];
      return (
        <Box key={key} sx={{ mb: 2, ml: 2 }}>
          <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14 }}>{field.label}</label>
          {(() => {
            const inputMaxLength = (() => {
              const lbl = (field.label || field.name || '').toLowerCase();
              if (lbl === 'button description') return 72;
              if (selectedAction?.name !== 'Send list message') return undefined;
              const keyLower = key.toLowerCase();
              if (lbl === 'button title') return 20;
              if (lbl === 'message header') return 60;
              if (lbl === 'section title') return 24;
              if (lbl === 'title' && keyLower.includes('option')) return 24;
              if (lbl === 'description' && keyLower.includes('option')) return 72;
              return undefined;
            })();
            return (
              <>
                <input
                  type={inputType}
                  style={{
                    width: '90%',
                    marginTop: 4,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${hasError ? (theme.palette.error.main) : (isDark ? '#444' : '#ccc')}`,
                    background: isDark ? '#23272b' : '#fff',
                    color: isDark ? '#fff' : '#222',
                    fontSize: 15,
                    marginBottom: 8,
                  }}
                  maxLength={inputMaxLength}
                  placeholder={inputMaxLength ? `Max ${inputMaxLength} characters` : undefined}
                  value={formValues[key] || ''}
                  onChange={e => {
                    setFormValues(v => ({ ...v, [key]: e.target.value }));
                    if (fieldErrors[key]) setFieldErrors(prev => ({ ...prev, [key]: undefined }));
                  }}
                />
                {hasError && (
                  <Typography variant="caption" sx={{ display: 'block', color: 'error.main', mt: 0.5 }}>
                    {fieldErrors[key]}
                  </Typography>
                )}
              </>
            );
          })()}
        </Box>
      );
    });
  };

  const isApiIntegrationDrawer = selectedAction?.name === 'API Integration';

  return (
    <>
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        height: '100%',
        boxSizing: 'border-box',
        width: isApiIntegrationDrawer
          ? { xs: '100%', sm: '100%', md: '100%', lg: 940 }
          : 360,
        maxWidth: isApiIntegrationDrawer
          ? { xs: '100%', sm: '100%', md: '100%', lg: 940 }
          : 360,
        bgcolor: isDark ? '#23272b' : '#fff',
        boxShadow: 6,
        zIndex: 30,
        borderLeft: `1.5px solid ${isDark ? '#444' : '#e0e0e0'}`,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        color: isDark ? theme.palette.text.primary : 'inherit',
        ml: { xs: 0, sm: 0, md: 0, lg: 2 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {selectedAction && (
            <IconButton onClick={() => setSelectedAction(null)} sx={{ mr: 1, color: isDark ? '#fff' : '#222' }}>
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
          )}
          <BuildCircleIcon sx={{ color: 'primary.main', fontSize: 28, mr: 1 }} />
          <Typography variant="h6" fontWeight={700}>
            {selectedAction ? selectedAction.name : 'Choose an action'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: isDark ? theme.palette.text.primary : undefined }}><CloseIcon /></IconButton>
      </Box>
      <Box
        ref={drawerBodyScrollRef}
        sx={{
          mt: 1,
          color: isDark ? theme.palette.text.secondary : 'text.secondary',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          pb: selectedAction ? 8 : 0,
          scrollbarWidth: { xs: 'none', sm: 'none', md: 'none', lg: 'auto' },
          msOverflowStyle: { xs: 'none', sm: 'none', md: 'none', lg: 'auto' },
          '&::-webkit-scrollbar': {
            width: { xs: 0, sm: 0, md: 0, lg: 8 },
            height: { xs: 0, sm: 0, md: 0, lg: 8 },
          },
        }}
      >
        {selectedAction ? (
          <Box sx={{ position: 'relative', width: '100%', flexShrink: 0, pb: 4 }}>
            {selectedAction.name === 'API Integration' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Name"
                  placeholder="e.g. Create Lead"
                  value={(formValues['0-Configuration'] ?? {}).name || ''}
                  onChange={(e) => setFormValues(prev => ({
                    ...prev,
                    '0-Configuration': { ...(prev['0-Configuration'] ?? DEFAULT_API_INTEGRATION_CONFIG), name: e.target.value }
                  }))}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Description"
                  placeholder="e.g. Sends contact data to CRM"
                  value={(formValues['0-Configuration'] ?? {}).description || ''}
                  onChange={(e) => setFormValues(prev => ({
                    ...prev,
                    '0-Configuration': { ...(prev['0-Configuration'] ?? DEFAULT_API_INTEGRATION_CONFIG), description: e.target.value }
                  }))}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
                <ApiIntegrationForm
                  value={formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_API_INTEGRATION_CONFIG}
                  onChange={(v) => setFormValues(prev => ({
                    ...prev,
                    '0-Configuration': {
                      ...v,
                      name: (prev['0-Configuration'] ?? {}).name || '',
                      description: (prev['0-Configuration'] ?? {}).description || '',
                    }
                  }))}
                  showOrderFields={selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger?.name === 'Contact places an order'}
                  templateVariableFields={
                    selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger?.name === 'Send Template'
                      ? (selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger?.variableMapping || [])
                      : null
                  }
                />
              </Box>
            ) : selectedAction.name === 'Branch: Business Hours' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Splits the workflow: the YES path runs when the current time is inside the schedule below, the NO path runs otherwise.
                </Typography>
                <BusinessHoursEditor
                  value={formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_BUSINESS_HOURS_VALUE}
                  onChange={(v) => {
                    setFormValues(prev => ({ ...prev, '0-Configuration': v }));
                    if (fieldErrors.__businessHours) setFieldErrors(prev => ({ ...prev, __businessHours: undefined }));
                  }}
                  expandedDay={businessHoursExpandedDay}
                  onExpandedDayChange={setBusinessHoursExpandedDay}
                />
                {fieldErrors.__businessHours && (
                  <Alert severity="error" sx={{ mt: 1 }}>{fieldErrors.__businessHours}</Alert>
                )}
              </Box>
            ) : selectedAction.name === SET_CONTACT_FIELDS_ACTION_NAME ? (
              <SetContactFieldsEditor
                value={formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_SET_CONTACT_FIELDS_VALUE}
                onChange={(v) => {
                  setFormValues(prev => ({ ...prev, '0-Configuration': v }));
                  if (fieldErrors.__setContactFields) setFieldErrors(prev => ({ ...prev, __setContactFields: undefined }));
                }}
                workspaceId={workspaceId}
                error={fieldErrors.__setContactFields}
                selectMenuProps={actionDrawerSelectMenuProps}
                selectSx={actionDrawerSelectSx}
              />
            ) : selectedAction.name === CONTACT_FIELDS_BRANCH_ACTION_NAME ? (
              <ContactFieldsEditor
                value={formValues['0-Configuration'] ?? localConfig?.[0]?.fields?.[0]?.selectedValue ?? DEFAULT_CONTACT_FIELDS_VALUE}
                onChange={(v) => {
                  setFormValues(prev => ({ ...prev, '0-Configuration': v }));
                  if (fieldErrors.__contactFields) setFieldErrors(prev => ({ ...prev, __contactFields: undefined }));
                }}
                workspaceId={workspaceId}
                error={fieldErrors.__contactFields}
                selectMenuProps={actionDrawerSelectMenuProps}
                selectSx={actionDrawerSelectSx}
              />
            ) : localConfig && Array.isArray(localConfig) && localConfig.length > 0 ? (
              localConfig.map((cfg, idx) => {
                // Calculate counts for list message validation
                let sectionCount = 0;
                let totalOptions = 0;
                let showSectionError = false;
                let showOptionError = false;

                // Calculate counts for interactive reply buttons message validation
                let buttonCount = 0;
                let showButtonError = false;
                
                if (selectedAction?.name === 'Send list message' && Array.isArray(cfg.fields)) {
                  const counts = countSectionsAndOptions(cfg.fields);
                  sectionCount = counts.sectionCount;
                  totalOptions = counts.totalOptions;
                  showSectionError = sectionCount >= 10;
                  showOptionError = totalOptions >= 10;
                }

                if ((selectedAction?.name === 'Send interactive reply buttons message' || 
                  selectedAction?.name === 'Send Interactive Reply Buttons Message') && 
                 Array.isArray(cfg.fields)) {
               const counts = countButtons(cfg.fields);
               buttonCount = counts.buttonCount;
               showButtonError = buttonCount > WEBSITE_MAX_BUTTONS;
             }
                
                return (
                  <Box key={idx} sx={{ mb: 3 }}>
                    <Typography sx={{ fontWeight: 600, m: 2, mb: 4, color: isDark ? '#fff' : '#222', fontSize: 17 }}>{cfg.name}</Typography>

                    {/* Show error message once at the top for interactive reply buttons message actions */}
                    {(selectedAction?.name === 'Send interactive reply buttons message' || 
                      selectedAction?.name === 'Send Interactive Reply Buttons Message') && showButtonError && (
                      <Alert 
                        severity="error" 
                        sx={{ 
                          m: 2,
                          mb: 2,
                          fontSize: '12px',
                          py: 1,
                          '& .MuiAlert-message': { fontSize: '12px' }
                        }}
                      >
                        <Typography variant="body2">
                          Maximum {WEBSITE_MAX_BUTTONS} buttons allowed. You have {buttonCount} buttons. Please remove {buttonCount - WEBSITE_MAX_BUTTONS} button(s) before saving.
                        </Typography>
                      </Alert>
                    )}
                    {/* Show error message once at the top for list message actions */}
                    {selectedAction?.name === 'Send list message' && (showSectionError || showOptionError) && (
                      <Alert 
                        severity="error" 
                        sx={{ 
                          m: 2,
                          mb: 2,
                          fontSize: '12px',
                          py: 1,
                          '& .MuiAlert-message': { fontSize: '12px' }
                        }}
                      >
                        {showSectionError && (
                          <Typography variant="body2" sx={{ mb: showOptionError ? 0.5 : 0 }}>
                            Maximum 10 sections allowed. You have {sectionCount} sections.
                          </Typography>
                        )}
                        {showOptionError && (
                          <Typography variant="body2">
                            Maximum 10 total options allowed across all sections. You have {totalOptions} options.
                          </Typography>
                        )}
                      </Alert>
                    )}
                    {selectedAction?.name === 'Set Variable' ? (
                      /* Custom multi-variable entry UI */
                      <Box sx={{ px: 2 }}>
                        {variableEntries.map((entry, eIdx) => (
                          <Box
                            key={eIdx}
                            sx={{
                              mb: 2,
                              p: 2,
                              border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                              borderRadius: 1,
                              background: isDark ? '#23272b' : '#fff',
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                              <Typography fontWeight={600} fontSize={13} sx={{ color: isDark ? '#ccc' : '#333' }}>
                                Variable {eIdx + 1}
                              </Typography>
                              {variableEntries.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => setVariableEntries(v => v.filter((_, i) => i !== eIdx))}
                                  sx={{ color: 'error.main' }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>
                            {/* Variable name */}
                            <TextField
                              fullWidth
                              size="small"
                              label="Variable name"
                              placeholder="e.g. customerName (use {{customerName}} in messages)"
                              value={entry.name}
                              onChange={e => setVariableEntries(v => v.map((en, i) => i === eIdx ? { ...en, name: e.target.value } : en))}
                              sx={{
                                mb: 1.5,
                                '& .MuiOutlinedInput-root': {
                                  color: isDark ? '#fff' : '#222',
                                  '& fieldset': { borderColor: isDark ? '#555' : '#ccc' },
                                },
                                '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' },
                              }}
                            />
                            {/* Variable type */}
                            <Box sx={{ mb: 1.5 }}>
                              <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 13 }}>Variable type</label>
                              <FormControl size="small" sx={{ mt: 0.5, width: '100%' }}>
                                <Select
                                  value={entry.type}
                                  onChange={e => setVariableEntries(v => v.map((en, i) => i === eIdx ? { ...en, type: e.target.value } : en))}
                                  MenuProps={actionDrawerSelectMenuProps}
                                  sx={actionDrawerSelectSx}
                                >
                                  <MenuItem value="Text">Text</MenuItem>
                                  <MenuItem value="Number">Number</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            {/* Value source */}
                            <Box sx={{ mb: 1.5 }}>
                              <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 13 }}>Value source</label>
                              <FormControl size="small" sx={{ mt: 0.5, width: '100%' }}>
                                <Select
                                  value={entry.valueSource}
                                  onChange={e => setVariableEntries(v => v.map((en, i) => i === eIdx ? { ...en, valueSource: e.target.value, contactField: '', fixedValue: '' } : en))}
                                  MenuProps={actionDrawerSelectMenuProps}
                                  sx={actionDrawerSelectSx}
                                >
                                  <MenuItem value="Fixed value">Fixed value</MenuItem>
                                  <MenuItem value="From contact">From contact</MenuItem>
                                </Select>
                              </FormControl>
                            </Box>
                            {/* Fixed value input */}
                            {entry.valueSource === 'Fixed value' && (
                              <TextField
                                fullWidth
                                size="small"
                                label="Variable value"
                                multiline
                                rows={2}
                                placeholder="Enter the value"
                                value={entry.fixedValue}
                                onChange={e => setVariableEntries(v => v.map((en, i) => i === eIdx ? { ...en, fixedValue: e.target.value } : en))}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    color: isDark ? '#fff' : '#222',
                                    '& fieldset': { borderColor: isDark ? '#555' : '#ccc' },
                                  },
                                  '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' },
                                }}
                              />
                            )}
                            {/* Contact field selector */}
                            {entry.valueSource === 'From contact' && (
                              <Box>
                                <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 13 }}>Contact field</label>
                                <FormControl size="small" sx={{ mt: 0.5, width: '100%' }}>
                                  <Select
                                    value={normalizeContactFieldValue(entry.contactField)}
                                    onChange={e => setVariableEntries(v => v.map((en, i) => i === eIdx ? { ...en, contactField: e.target.value } : en))}
                                    displayEmpty
                                    MenuProps={actionDrawerSelectMenuProps}
                                    sx={actionDrawerSelectSx}
                                    renderValue={v => contactFieldOptions.find(o => o.value === v)?.label || <span style={{ color: isDark ? '#888' : '#aaa' }}>Select contact field</span>}
                                  >
                                    {contactFieldOptions.map(o => (
                                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Box>
                            )}
                          </Box>
                        ))}
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => setVariableEntries(v => [
                            ...v,
                            { type: 'Text', name: '', valueSource: 'Fixed value', fixedValue: '', contactField: '' }
                          ])}
                          sx={{ mt: 1, textTransform: 'none' }}
                        >
                          Add Variable
                        </Button>
                      </Box>
                    ) : (
                      Array.isArray(cfg.fields) && cfg.fields.length > 0 ? renderFields(cfg.fields, `${idx}-`, idx, []) : null
                    )}
                  </Box>
                );
              })
            ) : null}
            
            {/* Keyword Configuration Section - Only show for non-Contact category actions and not Quick ACTION */}
            {selectedAction?.category !== 'Contact' &&
             !selectedAction?.name?.toLowerCase().includes('quick action') &&
             !editAction?.category?.includes('Contact') &&
             !(editAction?.name?.toLowerCase().includes('quick action')) &&
             selectedAction?.name !== 'Branch: Business Hours' &&
             editAction?.name !== 'Branch: Business Hours' &&
             selectedAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             editAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             !isGoToActionName(selectedAction?.name) &&
             !isGoToActionName(editAction?.name) && (
            <Box sx={{ mb: 3, mt: 3, p: 2, border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`, borderRadius: 2, background: isDark ? '#2e3440' : '#f9f9f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#222', fontSize: 16 }}>
                  Keywords
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setKeywords([...keywords, { keyword: '', targetId: '' }])}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  + Add Keyword
                </Button>
              </Box>
              <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', mb: 2, display: 'block' }}>
                {useKeywordBranches
                  ? 'Add keywords (text or integers like 1, 2, 123). Each keyword creates its own branch, like a list message option, where you add the steps to run when the user types it'
                  : 'Add keywords (text or integers like 1, 2, 123) that users can type to jump to specific messages in this workflow'}
              </Typography>
              
              {keywords.length === 0 ? (
                <Typography variant="body2" sx={{ color: isDark ? '#888' : '#999', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  No keywords configured. Click "Add Keyword" to add one.
                </Typography>
              ) : (
                keywords.map((kw, idx) => (
                  <Box key={idx} sx={{ mb: 2, p: 2, border: `1px solid ${isDark ? '#555' : '#ddd'}`, borderRadius: 1, background: isDark ? '#23272b' : '#fff' }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Keyword"
                        placeholder="e.g., price, support, 1, 2, 123"
                        value={kw.keyword || ''}
                        onChange={(e) => {
                          const newKeywords = [...keywords];
                          newKeywords[idx].keyword = e.target.value;
                          setKeywords(newKeywords);
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            color: isDark ? '#fff' : '#222',
                            '& fieldset': { borderColor: isDark ? '#555' : '#ccc' }
                          },
                          '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' }
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => {
                          const newKeywords = keywords.filter((_, i) => i !== idx);
                          setKeywords(newKeywords);
                        }}
                        sx={{ color: 'error.main' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    {/* Target Message Selection - Similar to Go to Action (WhatsApp Web workflows use a branch per keyword instead) */}
                    {!useKeywordBranches && <Box sx={{ mb: 1 }}>
                      <label style={{ color: isDark ? '#bbb' : '#555', fontSize: 14, display: 'block', mb: 1 }}>Target Message</label>
                      
                      {/* Selection Mode Indicator */}
                      {keywordTargetSelectionMode && keywordTargetSelectionIndex === idx && (
                        <Box sx={{ 
                          mt: 1, 
                          mb: 1,
                          p: 2, 
                          bgcolor: isDark ? '#1a3a1a' : '#e8f5e8', 
                          borderRadius: 1,
                          border: `2px solid ${isDark ? '#4caf50' : '#4caf50'}`,
                          textAlign: 'center'
                        }}>
                          <Typography variant="body2" sx={{ 
                            color: isDark ? '#4caf50' : '#2e7d32',
                            fontWeight: 600,
                            mb: 1
                          }}>
                            🎯 Click on a message node in the workflow to select it as the target
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setKeywordTargetSelectionMode(false);
                              setKeywordTargetSelectionIndex(null);
                            }}
                            sx={{ 
                              color: isDark ? '#4caf50' : '#2e7d32',
                              borderColor: isDark ? '#4caf50' : '#2e7d32',
                              '&:hover': {
                                borderColor: isDark ? '#66bb6a' : '#66bb6a',
                                bgcolor: isDark ? '#1a3a1a' : '#e8f5e8'
                              }
                            }}
                          >
                            Cancel Selection
                          </Button>
                        </Box>
                      )}
                      
                      {/* Target Message Selection Button */}
                      <Button
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          setKeywordTargetSelectionMode(true);
                          setKeywordTargetSelectionIndex(idx);
                        }}
                        disabled={keywordTargetSelectionMode && keywordTargetSelectionIndex !== idx}
                        sx={{
                          mt: 1,
                          mb: 1,
                          borderColor: isDark ? '#444' : '#ccc',
                          color: isDark ? '#fff' : '#222',
                          '&:hover': {
                            borderColor: isDark ? '#666' : '#999',
                            bgcolor: isDark ? '#2a2a2a' : '#f5f5f5'
                          },
                          '&:disabled': {
                            color: isDark ? '#666' : '#999',
                            borderColor: isDark ? '#333' : '#e0e0e0'
                          }
                        }}
                      >
                        {(() => {
                          if (!kw.targetId) return 'Click to Select Target Message';
                          // Find the selected message to display its name
                          const selectedMessage = extractWorkflowMessages.find(msg => msg.id === kw.targetId);
                          if (selectedMessage) {
                            if (selectedMessage.messageText) {
                              const displayText = selectedMessage.messageText.length > 40 
                                ? selectedMessage.messageText.substring(0, 37) + '...' 
                                : selectedMessage.messageText;
                              return `Selected: ${displayText}`;
                            }
                            return `Selected: ${selectedMessage.actionName}`;
                          }
                          return 'Click to Select Target Message';
                        })()}
                      </Button>
                      
                      {/* Selected Target Message Display */}
                      {kw.targetId && !keywordTargetSelectionMode && (() => {
                        const selectedMessage = extractWorkflowMessages.find(msg => msg.id === kw.targetId);
                        if (selectedMessage) {
                          return (
                            <Box sx={{ mt: 1, p: 1, bgcolor: isDark ? '#333' : '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="caption" sx={{ color: isDark ? '#bbb' : '#666', fontSize: 11 }}>
                                {selectedMessage.messageText 
                                  ? (selectedMessage.messageText.length > 50 ? selectedMessage.messageText.substring(0, 47) + '...' : selectedMessage.messageText)
                                  : selectedMessage.actionName}
                              </Typography>
                            </Box>
                          );
                        }
                        return null;
                      })()}
                    </Box>}
                  </Box>
                ))
              )}

              {useKeywordBranches && keywords.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      size="small"
                      checked={keywordNoReply.enabled}
                      onChange={(e) => setKeywordNoReply({ ...keywordNoReply, enabled: e.target.checked })}
                    />
                    <Typography sx={{ color: isDark ? '#fff' : '#222', fontSize: 14 }}>
                      Wait for reply (adds a "No reply" branch)
                    </Typography>
                  </Box>
                  {keywordNoReply.enabled && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {NO_REPLY_UNITS.map(unit => (
                        <TextField
                          key={unit.key}
                          size="small"
                          type="number"
                          label={unit.key}
                          value={keywordNoReply[unit.key]}
                          inputProps={{ min: 0 }}
                          onChange={(e) => setKeywordNoReply({ ...keywordNoReply, [unit.key]: Math.max(0, parseInt(e.target.value, 10) || 0) })}
                          sx={{
                            '& .MuiOutlinedInput-root': { color: isDark ? '#fff' : '#222', '& fieldset': { borderColor: isDark ? '#555' : '#ccc' } },
                            '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            )}

            {/* Keyword Wait Mode Configuration - Show when keywords exist (even if not fully configured) and action is not Contact category or Quick ACTION */}
            {keywords.length > 0 &&
             !useKeywordBranches &&
             selectedAction?.category !== 'Contact' &&
             !selectedAction?.name?.toLowerCase().includes('quick action') &&
             !editAction?.category?.includes('Contact') &&
             !(editAction?.name?.toLowerCase().includes('quick action')) &&
             selectedAction?.name !== 'Branch: Business Hours' &&
             editAction?.name !== 'Branch: Business Hours' &&
             selectedAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             editAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             !isGoToActionName(selectedAction?.name) &&
             !isGoToActionName(editAction?.name) && (
              <Box sx={{ mb: 3, mt: 3, p: 2, border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`, borderRadius: 2, background: isDark ? '#2e3440' : '#f9f9f9' }}>
                <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#222', fontSize: 16, mb: 1 }}>
                  After sending this message
                </Typography>
                <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', mb: 2, display: 'block' }}>
                  Choose whether to send the next message immediately or wait for a keyword reply from the user
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: `2px solid ${!waitForKeyword ? (isDark ? '#4caf50' : '#4caf50') : (isDark ? '#555' : '#ddd')}`, 
                      borderRadius: 1, 
                      background: !waitForKeyword ? (isDark ? '#1a3a1a' : '#e8f5e8') : (isDark ? '#23272b' : '#fff'),
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: !waitForKeyword ? (isDark ? '#66bb6a' : '#66bb6a') : (isDark ? '#666' : '#999'),
                      }
                    }}
                    onClick={() => setWaitForKeyword(false)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="radio"
                        checked={!waitForKeyword}
                        onChange={() => setWaitForKeyword(false)}
                        style={{ cursor: 'pointer' }}
                      />
                      <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#222', fontSize: 14 }}>
                        Send next message immediately
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', ml: 3, display: 'block', mt: 0.5 }}>
                      The workflow will continue to the next step right after sending this message
                    </Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      p: 2, 
                      border: `2px solid ${waitForKeyword ? (isDark ? '#4caf50' : '#4caf50') : (isDark ? '#555' : '#ddd')}`, 
                      borderRadius: 1, 
                      background: waitForKeyword ? (isDark ? '#1a3a1a' : '#e8f5e8') : (isDark ? '#23272b' : '#fff'),
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: waitForKeyword ? (isDark ? '#66bb6a' : '#66bb6a') : (isDark ? '#666' : '#999'),
                      }
                    }}
                    onClick={() => setWaitForKeyword(true)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <input
                        type="radio"
                        checked={waitForKeyword}
                        onChange={() => setWaitForKeyword(true)}
                        style={{ cursor: 'pointer' }}
                      />
                      <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#222', fontSize: 14 }}>
                        Wait for keyword reply
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', ml: 3, display: 'block', mt: 0.5 }}>
                      The workflow will pause and wait for the user to type a keyword. Based on the keyword, it will jump to the corresponding target message
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}
            
            {/* Incorrect Keyword Message Configuration Section - Only show for non-Contact category actions and not Quick ACTION */}
            {selectedAction?.category !== 'Contact' &&
             !selectedAction?.name?.toLowerCase().includes('quick action') &&
             !editAction?.category?.includes('Contact') &&
             !(editAction?.name?.toLowerCase().includes('quick action')) &&
             selectedAction?.name !== 'Branch: Business Hours' &&
             editAction?.name !== 'Branch: Business Hours' &&
             selectedAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             editAction?.name !== CONTACT_FIELDS_BRANCH_ACTION_NAME &&
             !isGoToActionName(selectedAction?.name) &&
             !isGoToActionName(editAction?.name) && (
            <Box sx={{ mb: 3, mt: 3, p: 2, border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`, borderRadius: 2, background: isDark ? '#2e3440' : '#f9f9f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontWeight: 600, color: isDark ? '#fff' : '#222', fontSize: 16 }}>
                  Incorrect Keyword Message
                </Typography>
                <Checkbox
                  checked={incorrectKeywordMessage.enabled}
                  onChange={(e) => setIncorrectKeywordMessage({ ...incorrectKeywordMessage, enabled: e.target.checked })}
                  sx={{ color: isDark ? '#fff' : '#222' }}
                />
              </Box>
              <Typography variant="caption" sx={{ color: isDark ? '#aaa' : '#666', mb: 2, display: 'block' }}>
                Message to send when user types something that doesn't match any keyword or valid option
              </Typography>
              
              {incorrectKeywordMessage.enabled ? (
                <>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                    label="Message Text"
                    placeholder="I didn't understand that. Please try again."
                    value={incorrectKeywordMessage.text || ''}
                    error={!!fieldErrors['__incorrectKeywordMessage']}
                    helperText={fieldErrors['__incorrectKeywordMessage']}
                    onChange={(e) => {
                      setIncorrectKeywordMessage({ ...incorrectKeywordMessage, text: e.target.value });
                      if (fieldErrors['__incorrectKeywordMessage']) setFieldErrors(prev => ({ ...prev, __incorrectKeywordMessage: undefined }));
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        color: isDark ? '#fff' : '#222',
                        '& fieldset': { borderColor: isDark ? '#555' : '#ccc' }
                      },
                      '& .MuiInputLabel-root': { color: isDark ? '#aaa' : '#666' }
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Checkbox
                      checked={incorrectKeywordMessage.showOptions || false}
                      onChange={(e) => setIncorrectKeywordMessage({ ...incorrectKeywordMessage, showOptions: e.target.checked })}
                      sx={{ color: isDark ? '#fff' : '#222' }}
                    />
                    <Typography variant="body2" sx={{ color: isDark ? '#bbb' : '#555', fontSize: 13 }}>
                      Show available keyword options in the message
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: isDark ? '#888' : '#999', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  Incorrect keyword message is disabled. Users won't receive a message for invalid inputs.
                </Typography>
              )}
            </Box>
            )}
            
            {/* Sticky Save button at the bottom */}
            <Box sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              zIndex: 40,
              px: 2,
              background: isDark ? '#23272b' : '#fff',
              borderTop: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
              boxShadow: 0,
              display: 'flex',
              justifyContent: 'flex-start',
              py: 2,
              mt: 3,
            }}>
              <Button
                variant="contained"
                color="primary"
                sx={{
                  borderRadius: '20px',
                  backgroundColor: (theme) => theme.palette.primary.main,
                  color: 'white',
                  textTransform: 'capitalize',
                  padding: '8px 16px',
                  fontWeight: 600,
                  fontSize: 16,
                  boxShadow: 2,
                  '&:hover': {
                    backgroundColor: (theme) => theme.palette.primary.dark,
                  },
                }}
                onClick={handleSaveAction}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : actions.length === 0 ? (
          <Typography>No actions found.</Typography>
        ) : (
          (() => {
            const triggerName = selectedWorkflow?.currentVersion?.branches?.start?.[0]?.trigger?.name;
            // Extract API Integration, Set Variable and AI Reply for dedicated sections
            let apiIntegrationAction = null;
            let setVariableAction = null;
            for (const categoryObj of actions) {
              if (!apiIntegrationAction) {
                const found = (categoryObj.actions || []).find(a => a.name === 'API Integration');
                if (found) apiIntegrationAction = found;
              }
              if (!setVariableAction) {
                const found = (categoryObj.actions || []).find(a => a.name === 'Set Variable');
                if (found) setVariableAction = found;
              }
    
              if (apiIntegrationAction && setVariableAction ) break;
            }
  
          
            const isApiIntegrationVisible = !!apiIntegrationAction;
            const isSetVariableVisible = setVariableAction && (
              !setVariableAction.onlyForTriggers?.length ||
              (triggerName && setVariableAction.onlyForTriggers.includes(triggerName))
            );

            return (
              <>
                {/* API Integration accordion (same style as other categories) */}
                {isApiIntegrationVisible && (
                  <Accordion
                    expanded={expanded === 'Integrations'}
                    onChange={handleAccordionChange('Integrations')}
                    sx={{
                      bgcolor: isDark ? '#23272b' : '#f5f5f5',
                      color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
                      mb: 1,
                      boxShadow: 0,
                      border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {CATEGORY_ICONS['Integrations']}
                        <Typography fontWeight={600}>Integrations</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Paper
                        elevation={isDark ? 2 : 1}
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: isDark ? '#23272b' : '#fff',
                          border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                          transition: 'box-shadow 0.2s, border-color 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 4,
                            borderColor: theme.palette.primary.main,
                            background: isDark ? '#2e3440' : '#f0f7ff',
                          },
                        }}
                        onClick={() => setSelectedAction(apiIntegrationAction)}
                      >
                        <Typography fontWeight={700} fontSize={16} gutterBottom>
                          {apiIntegrationAction.name}
                        </Typography>
                        <Typography fontSize={14} color={isDark ? theme.palette.text.secondary : theme.palette.text.secondary}>
                          {apiIntegrationAction.description || 'Call an external API from the workflow'}
                        </Typography>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Set Variable accordion (separate from Contact) */}
                {isSetVariableVisible && (
                  <Accordion
                    expanded={expanded === 'Variables'}
                    onChange={handleAccordionChange('Variables')}
                    sx={{
                      bgcolor: isDark ? '#23272b' : '#f5f5f5',
                      color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
                      mb: 1,
                      boxShadow: 0,
                      border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {CATEGORY_ICONS['Variables']}
                        <Typography fontWeight={600}>Set Variable</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      <Paper
                        elevation={isDark ? 2 : 1}
                        sx={{
                          mb: 2,
                          p: 2,
                          bgcolor: isDark ? '#23272b' : '#fff',
                          border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                          transition: 'box-shadow 0.2s, border-color 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 4,
                            borderColor: theme.palette.primary.main,
                            background: isDark ? '#2e3440' : '#f0f7ff',
                          },
                        }}
                        onClick={() => setSelectedAction(setVariableAction)}
                      >
                        <Typography fontWeight={700} fontSize={16} gutterBottom>
                          {setVariableAction.name}
                        </Typography>
                        <Typography fontSize={14} color={isDark ? theme.palette.text.secondary : theme.palette.text.secondary}>
                          {setVariableAction.description || 'Store a value in a variable for use later in the workflow'}
                        </Typography>
                      </Paper>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Category accordions (exclude API Integration, Set Variable) */}
                {actions.map(categoryObj => {
                  const visibleActions = (categoryObj.actions || []).filter(
                    action =>
                      action.name !== 'API Integration' &&
                      action.name !== 'Set Variable' &&
                      (!action.onlyForTriggers?.length || (triggerName && action.onlyForTriggers.includes(triggerName)))
                  );
                  if (visibleActions.length === 0) return null;
                  return (
                    <Accordion
                      key={categoryObj.category}
                      expanded={expanded === categoryObj.category}
                      onChange={handleAccordionChange(categoryObj.category)}
                      sx={{
                        bgcolor: isDark ? '#23272b' : '#f5f5f5',
                        color: isDark ? theme.palette.text.primary : theme.palette.text.primary,
                        mb: 1,
                        boxShadow: 0,
                        border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {CATEGORY_ICONS[categoryObj.category] || <CategoryIcon sx={{ mr: 1, color: 'primary.main' }} />}
                          <Typography fontWeight={600}>{categoryObj.category}</Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0 }}>
                        {visibleActions.map(action => (
                          <Paper
                            key={action._id || action.id || action.name}
                            elevation={isDark ? 2 : 1}
                            sx={{
                              mb: 2,
                              p: 2,
                              bgcolor: isDark ? '#23272b' : '#fff',
                              border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
                              transition: 'box-shadow 0.2s, border-color 0.2s',
                              cursor: 'pointer',
                              '&:hover': {
                                boxShadow: 4,
                                borderColor: theme.palette.primary.main,
                                background: isDark ? '#2e3440' : '#f0f7ff',
                              },
                            }}
                            onClick={() => setSelectedAction(action)}
                          >
                            <Typography fontWeight={700} fontSize={16} gutterBottom>
                              {action.name}
                            </Typography>
                            <Typography fontSize={14} color={isDark ? theme.palette.text.secondary : theme.palette.text.secondary}>
                              {action.description || 'No description'}
                            </Typography>
                          </Paper>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </>
            );
          })()
        )}
      </Box>
    </Box>

    {/* Full-size image preview overlay */}
    {previewImageUrl && (
      <Box
        sx={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          bgcolor: 'rgba(0,0,0,0.88)',
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onClick={() => setPreviewImageUrl(null)}
      >
        <IconButton
          size="small"
          sx={{ position: 'fixed', top: 16, right: 16, bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', zIndex: 100000, '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } }}
          onClick={() => setPreviewImageUrl(null)}
        >
          <CloseIcon />
        </IconButton>
        <img
          src={previewImageUrl}
          alt="Preview"
          style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, display: 'block', boxShadow: '0 8px 32px rgba(0,0,0,0.7)' }}
          onClick={(e) => e.stopPropagation()}
        />
      </Box>
    )}
    </>
  );
};

export default ChooseWebsiteActionDrawer;